
import createFetchClient from "openapi-fetch";
import { z } from "zod";
import type { paths } from "./generated/schema.js";
import type { FilterableFields } from "./generated/filterable.generated.js";
import {
    zAbortResult,
    zBatchDeleteResult,
    zDirectoryRealmPreflight,
    zDirectoryRealmRefoundResult,
    zFsAcl,
    zFsEditorSession,
    zFsListing,
    zIdentifyResult,
    zNetworkNodeBindingList,
    zNodeDiskChunksList,
    zPermissionList,
    zRevokeUserSessionsResponse,
    zSession,
    zSessionCreated,
    zSudoGrant,
    zTotpEnrollment,
} from "./generated/zod/zod.gen.js";
// FACADE-TIPATA (#183, 2026-08-21): the rendered-resource schemas come from
// resources.ts — the generated contract schemas strengthened with the required
// `id` the wire always carries (reperto declared there), plus the inferred
// response types the facade signatures below now declare instead of
// `Record<string, unknown>`.
import {
    zAlert,
    zAlertRule,
    zAlertSink,
    zAuditChainVerification,
    zAuditEvent,
    zBackup,
    zBackupTarget,
    zBucket,
    zCephCluster, zMultiscsiCluster,
    zCephHealthReport, zCephPoolExplorerEntry, zCephPoolExplorerList, zCephPoolObjectsPage, zCephCrushMap, zCephOsdExplorerList,
    zCluster,
    zDhcpService,
    zDirectoryRealm,
    zFileShare,
    zFileShareLockdownResult,
    zBackupCopy,
    zBackupCopyList,
    zClusterDiskList,
    zClusterNode,
    zClusterNodeRegistration,
    zClusterNodeReinstatement,
    zClusterNodeRevocation,
    zRollingUpdateState,
    zCredential,
    zGetSystemHealthResponse,
    zGrant,
    zHost,
    zIoServerPair,
    zIoServerPairCredential,
    zMetricsToken,
    zMonitoringOverview,
    zMonitoringTrendSeries,
    zNetwork,
    zNetworkNodeBinding,
    zNodeRaid,
    zNodeVdisk,
    zObjectStore,
    zRecoveryKeysExport,
    zReplicationLink,
    zReplicationLinkDemoteResult,
    zRole,
    zSyncLink,
    zSyncLinkDemoteResult,
    zS3Account,
    zS3AccountWithSecret,
    zScope,
    zScopeReport,
    zTarget,
    zTask,
    zUser,
    zVolume,
    zVolumeSnapshot,
    zFileShareSnapshot,
    zCatalogCredentials,
    zCatalogEntry,
    zCatalogStatus,
    zCatalogSummary,
    zClientAuditTail,
    zCatalogSource,
    zNodeMountCensus,
    type CatalogCredentials,
    type CatalogEntry,
    type CatalogStatus,
    type CatalogSummary,
    type ClientAuditTail,
    type CatalogSource,
    type NodeMountCensus,
    zEnclosure,
    type Enclosure,
} from "./resources.js";
import type {
    AbortResult,
    Alert,
    AlertRule,
    AlertSink,
    AuditChainVerification,
    AuditEvent,
    Backup,
    BackupTarget,
    Bucket,
    Cluster,
    BackupCopy,
    MetricsToken,
    ClusterDisk,
    ClusterNode,
    RollingUpdateState,
    ClusterNodeRegistration,
    ClusterNodeReinstatement,
    ClusterNodeRevocation,
    CephCluster,
    Credential,
    DhcpService,
    DirectoryRealm,
    FileShare,
    FileShareLockdownResult,
    Grant,
    Host,
    IoServerPair,
    IoServerPairCredential,
    MonitoringOverview,
    MonitoringTrendSeries,
    MultiscsiCluster,
    Network,
    NetworkNodeBinding,
    NodeDiskChunks,
    NodeRaid,
    NodeVdisk,
    ObjectStore,
    PermissionInfo,
    RecoveryKeysExport,
    ReplicationLink,
    ReplicationLinkDemoteResult,
    Role,
    S3Account,
    Scope,
    ScopeReport,
    SyncLink,
    SyncLinkDemoteResult,
    SystemHealth,
    Target,
    User,
    Volume,
    VolumeSnapshot,
    FileShareSnapshot,
    SnapshotRetention,
} from "./resources.js";
import {
    diffExtentsOfLog,
    parseVolumeDiffResult,
    type DiffExtent,
    type VolumeDiffInput,
    type VolumeDiffResult,
} from "./diff.js";
import { ndjsonLines } from "./ndjson.js";
import { genPageIterator, type PageIterator } from "./page-iterator.js";
import { TVStorApiError, TVStorTaskError, TVStorTaskTimeoutError, leaseHolderOf, type LeaseHolder } from "./problem.js";



// The hand-written facade over the typed transport (design cloned from tvirt-v6,
// ratified there 2026-07-10): structured filters serialized to the ?filter= grammar,
// list = one explicit page, listAll = per-item PageIterator, writes return
// { resource, task }, problem+json surfaces as TVStorApiError, zod validates the
// boundary (validateResponses).

export type FilterOp =
    | "eq" | "!eq" | "in" | "!in"
    | "contains" | "!contains" | "startsWith" | "!startsWith" | "endsWith" | "!endsWith"
    | "gt" | "gte" | "lt" | "lte" | "exists";

export interface FilterTerm<F extends string = string> {
    field: F;
    op: FilterOp;
    value: unknown;
}



export interface ListParams<F extends string = string> {
    filter?: FilterTerm<F>[];
    sort?: { field: F; direction?: "asc" | "desc" };
    scope?: string;
    limit?: number;
    cursor?: string | null;
}



// CRUD-3 (#207, 2026-08-23): le due maniglie trasversali del contract
// (common.yaml `parameters/DryRun` e `parameters/IdempotencyKey`), esposte
// TIPATE sui verbi che le dichiarano — mai su quelli che non le hanno.
export interface DryRunOptions {
    // `?dryRun=true`: ammissione COMPLETA (schema, invarianti, referenze,
    // RBAC, unicita') senza persistere ne' schedulare nulla — il server
    // risponde il would-be result. Omesso o false: il parametro NON viaggia
    // (il default del contract e' false; mandarlo esplicito sarebbe rumore).
    dryRun?: boolean;
}

export interface IdempotencyOptions {
    // `Idempotency-Key`: UUID coniato dal CLIENT per le action — il server
    // conserva l'esito per (credenziale, chiave) 24 h e un retry con la
    // stessa chiave RIGIOCA la risposta invece di rieseguire. La chiave la
    // conia il gesto (un dialog aperto = una chiave), mai questa facade: una
    // chiave nuova a ogni retry non proteggerebbe niente.
    idempotencyKey?: string;
}



export interface Page<T> {
    items: T[];
    nextCursor: string | null;
    // Present (true) when the page was served from the stale replica fallback
    // (cluster-first §6) — absent on fresh store reads and on task surfaces.
    stale?: boolean;
}



export interface WriteResult<T> {
    resource: T;
    // The X-TVStor-Task reference — feed it to tasks.wait/follow (a parent task
    // when the operation runs as an ordered sequence of children).
    task: string | null;
}



// E2E-2c rilievo 86 (2026-11-01): what the client does with a 409
// `metadata_lease_held` — the per-datapool metadata lease is held by an active
// task (backend tasks/catalog.ts), and two tenants provisioning together met
// it for tens of seconds while the CLI and the webui just spelled it. Opt-in:
// `wait()` is asked the moment the 409 arrives (the CLI answers with its
// --wait flag); when it says yes the transport waits for the holder — the
// family when there is one, the task otherwise — to leave queued|running (an
// OBSERVATION of its phase, never a timer: the 409's Retry-After is the poll
// hint for clients that cannot follow tasks) and re-sends the SAME request.
// Bounded: `maxAttempts` 409s (default 20, the bench's number), each holder
// wait bounded by `holderTimeoutMsOf` (default 600 s). A holder wait that
// expires raises TVStorTaskTimeoutError on the holder (the wait gave up;
// nothing of the caller's was started); past the last attempt the 409
// surfaces as it came (TVStorApiError, `leaseHolder` set).
export interface MetadataLeasePolicy {
    wait: () => boolean;
    onWait?: (holder: LeaseHolder, attempt: number) => void;
    onReleased?: (holder: LeaseHolder, attempt: number, waitedMs: number) => void;
    maxAttempts?: number;
    holderTimeoutMsOf?: (holder: LeaseHolder) => number;
}



export interface LeaseReleaseWaitOptions {
    pollMs?: number;
    pollMaxMs?: number;
    timeoutMs?: number;
    onPoll?: (task: Task) => void;
}



export interface ClientOptionsV1 {
    baseUrl: string;
    token?: string;
    validateResponses?: boolean;
    // Injectable fetch — custom CA / dispatcher on Node, defaults to the global fetch.
    fetch?: typeof fetch;
    metadataLease?: MetadataLeasePolicy;
}



// FACADE-TIPATA (#183, 2026-08-21): the task render as the contract declares
// it (tasks.yaml via zTask) — no longer a Record. `result` stays a readonly
// record by contract (per-type payloads are the consumers' business, e.g.
// parseVolumeDiffResult), `error` is the RFC 9457 problem document.
export type Task = z.infer<typeof zTask>;
export type BatchDeleteResult = { deleted: string[]; refused: { id: string; code: string; detail: string }[] };



// What every waited call takes (#102 CLI-WAIT, ratifica Luca 2026-08-12 §8).
// `pollMs` is the FIRST interval, no longer the only one: the poll backs off
// from it up to `pollMaxMs`. `timeoutMs` is the wait's own budget — when it
// expires the call throws TVStorTaskTimeoutError, which is a different fact
// from the task having failed and is meant to be handled differently.
export interface TaskWaitOptions {
    pollMs?: number;
    pollMaxMs?: number;
    timeoutMs?: number;
    onProgress?: (task: Task) => void;
}



// THE POLL BACKS OFF. A flat 250 ms is 4 GET al secondo for as long as the task
// runs — on a `backup.ship`, whose engine budget is 24 h, that is ~345 000
// requests spent learning a percentage, and the wait is the CHEAPEST part of
// what the cluster is doing at that moment. Starting at 250 ms keeps a
// two-second task as prompt as it was; growing by half each round reaches the
// 5 s ceiling after ~9 polls (~17 s), which turns the same 24 h ship into
// ~17 000 requests. The ceiling matters more than the curve: it is what bounds
// the cost of the long waits, which are the only ones that were ever expensive.
const WAIT_POLL_FIRST_MS = 250;
const WAIT_POLL_MAX_MS = 5_000;
const WAIT_POLL_GROWTH = 1.5;

// E2E-2c rilievo 86: the metadata-lease retry's bounds (MetadataLeasePolicy) —
// the bench's own numbers (e2e/lib.sh: 20 attempts, 600 s per family wait).
const LEASE_RETRY_ATTEMPTS = 20;
const LEASE_HOLDER_WAIT_MS = 600_000;



// What `volumes.diffAndWait` hands back (M4a, 2026-08-04): the two channels of
// a CHANGED BLOCK TRACKING answer in one object. `summary` is the normalized
// `Task.result` — granularity, counts, and the inline head of the extent list;
// `extents()` opens the task LOG and streams the FULL, authoritative list,
// however long it is. The summary is enough whenever `summary.truncated` is
// false; the iterator is the only correct source when it is true.
export interface VolumeDiffAnswer {
    task: string;
    summary: VolumeDiffResult;
    extents: () => AsyncGenerator<DiffExtent>;
}



// The keystore inputs (M2g, 2026-08-03) are typed BY HAND because the secret
// fields are WRITE-ONLY: the generated response schema (zCredential) strips
// them — they exist only here, on the way IN (create and rotate), and are
// never rendered back, never logged, never on argv (credentials.yaml header).
// Exactly the block matching `kind` must be present (422 validation otherwise).
// GUI-PARITA' ondata 1a (#181, 2026-08-19): the union realigned to the FULL
// contract enum (credentials.yaml — 13 kinds). It had silently stopped at the
// ENC-1 state (11) while NAS-2 added `machineAccount` (#110) and
// `directoryAccount` (Fase 1a) — the same hand-written-twin drift as
// MetricName in ondata 0. NOTE the create/rotate inputs below deliberately
// carry NO `machineAccount` block: that credential is PLATFORM-BORN (the
// join writes it, the SINGLE elected rotator re-seals it — credentials.yaml
// "NOT an operator gesture"); the operator verb is
// `actions/rotateMachineAccount` (#162), not yet on this facade (dated seam).
export type CredentialKind = "iscsiChap" | "bmcAccount" | "cephx" | "luksKey" | "s3Account" | "replicationToken" | "fileUser" | "smtpAccount" | "webhookSecret" | "tlsCertificate" | "wireguardKey" | "machineAccount" | "directoryAccount";

export interface CredentialCreateInput {
    name: string;
    kind: CredentialKind;
    scope?: string;
    notes?: string;
    // The one-way pair authenticates the initiator; the OPTIONAL mutual pair
    // (travels together — 422 on half a pair) authenticates the target back.
    iscsiChap?: { username: string; password: string; mutualUsername?: string; mutualPassword?: string };
    // #89 TLS-PIN (2026-08-09, ratifica §9.2 — rettificata §9.5 il
    // 2026-08-10): `certificateFingerprint` — the sha-256 of the DER
    // certificate the device serves — is OPTIONAL on the credential and
    // MANDATORY IN THE CONSUMER. It is PUBLIC material (it renders back, and
    // pinning or re-pinning is an ordinary PATCH). Without it the fencing
    // shim refuses to open the Redfish connection at all: that call carries
    // `password` in an Authorization: Basic header toward the device that
    // powers nodes off. Omit it only where no TLS is spoken — the cascade's
    // switch and PDU accounts, an IPMI-only BMC.
    bmcAccount?: { username: string; certificateFingerprint?: string; password: string };
    // Consumed since M3 (2026-08-03): the platform mints one per host of a
    // `protocol: rbd` target; operator-created entries stay legal
    // (external-cluster clients, roadmap).
    cephx?: { entity: string; key: string };
    // Declared-unused until M4 (architecture §7): `passphrase` omitted, the
    // platform MINTS one (the rendered `minted` flag flips true).
    luksKey?: { passphrase?: string };
    // M4b (2026-08-04): the backup targets' destination identity — the
    // accessKeyId is PUBLIC, the secret is write-only like every other secret
    // half; endpoint/bucket/region live on the consumer (BackupTarget.s3).
    s3Account?: { accessKeyId: string; secretAccessKey: string };
    // M4b (2026-08-04): the DR link's `tvsl_` bearer (replication.yaml trust
    // boundary) — on the source it is what the link presents, on the
    // destination the verifier of presented bearers. clusterName is PUBLIC:
    // the PEER cluster this token speaks for/with.
    replicationToken?: { clusterName: string; token: string };
    // NAS-1 (2026-08-04): a LOCAL file-service user — the identity-fence
    // answer for AD-less sites (credentials.yaml): SMB/SFTP shares reference
    // it by id (`FileShare.smb.users[]` / `sftp.users[]`). `username` and
    // `sshPublicKeys` are PUBLIC; the password is write-only like every
    // secret half. At least one of password / sshPublicKeys (422 — a user
    // nobody can authenticate as is refused); an SMB reference requires the
    // password half.
    fileUser?: { username: string; password?: string; sshPublicKeys?: string[] };
    // M5b (2026-08-04): the alert sinks' SMTP submission login — `username`
    // is PUBLIC, the password write-only; host/port/tls/from/to live on the
    // consumer (`AlertSink.email`, the s3Account split).
    smtpAccount?: { username: string; password: string };
    // M5b (2026-08-04): the alert sinks' webhook HMAC key — no public half
    // exists, the block IS its secret (X-TVStor-Signature: sha256=<hex>).
    webhookSecret?: { secret: string };
    // OBJ-1 (2026-08-04): a DATA-PLANE TLS server pair, consumed by
    // `ObjectStore.tlsCertificate` (fallback: the cluster certificate). The
    // PEM chain (leaf first) is PUBLIC and renders back; the private key is
    // write-only like every secret half — admission verifies the two match.
    tlsCertificate?: { certificatePem: string; privateKeyPem: string };
    // ENC-1 (2026-08-05): a node's replica-tunnel X25519 pair — normally
    // PLATFORM-MINTED at replica-encryption activation (`wg-node-<8hex>`);
    // creating one by hand is the bring-your-own-key path. privateKey
    // optional (omitted = platform mint, `minted` flips true); the rendered
    // publicKey is DERIVED server-side, never accepted as input.
    wireguardKey?: { privateKey?: string };
    // NAS-2 Fase 1a (2026-08-15, #181 ondata 1a): the account the appliance
    // authenticates AS against a directory — the JOIN account on an `ad`
    // realm, the service BIND account on an `ldap` one. `username` is PUBLIC
    // (whichever spelling the directory wants: `administrator@TVSTOR.TEST`,
    // `TVSTOR\administrator`, or a full bind DN); the password is write-only
    // and REQUIRED at creation — the secret belongs to the customer's
    // directory, there is no platform-minted branch. No minimum length beyond
    // one character (the directory's policy governs, credentials.yaml).
    // ENEA-A-R6 (2026-10-23): the MIT KEYTAB form of the machine identity —
    // `realm` and `accountName` public, `keytab` = base64 of the file kadmin's
    // `ktadd -k` wrote (write-only). The Samba form stays platform-born.
    machineAccount?: { realm: string; accountName: string; keytab: string };
    directoryAccount?: { username: string; password: string };
}

// Body of actions/rotate — SECRET halves only (public-field changes are
// ordinary PATCH); the block must match the credential's kind.
export interface CredentialRotateInput {
    iscsiChap?: { password: string; mutualPassword?: string };
    bmcAccount?: { password: string };
    cephx?: { key: string };
    luksKey?: { passphrase?: string };
    s3Account?: { secretAccessKey: string };
    // Rotate on BOTH clusters, destination first — the verifier must know the
    // new secret before the source presents it (credentials.yaml, 2026-08-04).
    replicationToken?: { token: string };
    // NAS-1 (2026-08-04): the new password — consumers converge via the
    // reconcile pass (Samba user database, SFTP auth re-fed on the serving
    // nodes); sshPublicKeys changes are ordinary PATCH (public field).
    fileUser?: { password: string };
    // M5b (2026-08-04): the new SMTP AUTH password (a username change is
    // ordinary PATCH); delivery tasks resolve at use time — no re-apply task.
    smtpAccount?: { password: string };
    // M5b (2026-08-04): the new HMAC key — update the RECEIVING system first.
    webhookSecret?: { secret: string };
    // OBJ-1 (2026-08-04) — DECLARED DEVIATION from the secret-halves-only
    // rule: this rotate carries the PUBLIC certificate TOO, because the pair
    // is ONE cryptographic object (the certificate binds the key's public
    // half). Key-preserving renewal (new certificate, same key) stays
    // ordinary PATCH on `certificatePem`.
    tlsCertificate?: { certificatePem: string; privateKeyPem: string };
    // ENC-1 (2026-08-05): the new tunnel private key — omitted, the platform
    // mints a fresh pair; the rendered publicKey is re-derived either way.
    wireguardKey?: { privateKey?: string };
    // NAS-2 Fase 1a (2026-08-15): the join/bind account's new password — the
    // ORDINARY operator gesture here (the directory's administrator changed
    // it there, this writes it here). Contrast `machineAccount`, which has NO
    // block in this input on purpose (see CredentialKind above).
    directoryAccount?: { password: string };
    // ENEA-A-R6: a new keytab (keytab form) — the Samba form rotates through rotateMachineAccount.
    machineAccount?: { keytab?: string; secretsTdb?: string };
}



// The one-time render of an S3 account's minted key pair (OBJ-1, 2026-08-04):
// answer of `objectStores.s3Accounts.create` and `.rotate` — the
// `secret.secretAccessKey` exists ONLY in that response body (the M5a
// join-token write-once discipline); no read path ever re-renders it, and
// losing it means rotate. FACADE-TIPATA (#183): inferred from the recomposed
// resources.ts schema — `account` is the rendered S3Account, no longer Record.
export type S3AccountWithSecret = z.infer<typeof zS3AccountWithSecret>;



// The web explorer's fs surface (NAS-WebExplorer #30, 2026-08-05): one entry
// and one page of a NON-RECURSIVE folder listing — regular files and folders
// only (symlinks and special files are omitted server-side), folders first
// then bytewise name order, `nextCursor` normalized to null at exhaustion
// (the facade's Page convention). `sizeBytes`/`modifiedAt` may be null when
// the serving node could not stat the entry — degraded, never hidden.
export interface FsEntry {
    name: string;
    kind: "file" | "folder";
    sizeBytes?: number | null | undefined;
    modifiedAt?: string | null | undefined;
}

export interface FsListing {
    path: string;
    entries: FsEntry[];
    nextCursor: string | null;
}

// What `fileShares.fsDownload` resolves with: the body stream plus the
// headers a consumer saves or renders by — never buffered here. ENEA-A-R10:
// `fileShares.catalog.export` resolves with the same shape (CSV/JSONL stream).
// ENEA-A-R10: the catalog query as the facade takes it (Dates or ISO strings
// for the mtime bounds) — catalogQueryOf renders the wire's query string.
export interface CatalogListQuery {
    pathPrefix?: string;
    extension?: string;
    type?: "file" | "dir" | "symlink" | "other";
    sizeMin?: number;
    sizeMax?: number;
    mtimeFrom?: Date | string;
    mtimeTo?: Date | string;
    uid?: number;
    gid?: number;
    nameContains?: string;
    sort?: "path" | "-path" | "name" | "-name" | "size" | "-size" | "mtime" | "-mtime" | "ctime" | "-ctime";
    limit?: number;
    cursor?: string;
}

export function catalogQueryOf(q: CatalogListQuery): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(q)) {
        if (value === undefined || value === null || value === "") {
            continue;
        }
        out[key] = value instanceof Date ? value.toISOString() : (value as string | number);
    }
    return out;
}

export interface FsDownload {
    stream: ReadableStream<Uint8Array>;
    contentType: string | null;
    contentLength: number | null;
    contentDisposition: string | null;
}

// What `fileShares.fsEditorSession` resolves with (WEBEXP-EDIT #63,
// 2026-08-05): the Collabora editor frame URL plus the DEDICATED `tvse_`
// editor token — rendered exactly once, NEVER the caller's session token
// (the OnlyOffice spike's binding finding 1). `accessTokenTtl` is the WOPI
// convention verbatim: expiry as milliseconds since the epoch. `editorUrl`
// is a PATH RELATIVE TO THE API ORIGIN since WEBEXP-PROXY (2026-08-06): the
// frame loads through the backend's `/api/v1/collabora/…` reverse proxy —
// prefix the origin the API is reached on.
export interface FsEditorSession {
    editorUrl: string;
    wopiSrc: string;
    accessToken: string;
    accessTokenTtl: number;
    expiresAt: string;
}



// The rich-ACL surface (NAS-2 Fase 2, #169 2026-08-17, consumed by the webui
// since #181 ondata 1a): the ACL of a path inside a share, in the share's
// NATIVE representation and never an invented translation. `daclPresent`
// tells an ABSENT acl from an EMPTY one (on `nt` the two are opposites);
// `lossyFor` names the protocols that see something else, `losses` says
// what exactly — data, not a footnote. `unnamedMask`/`unnamedFlags` are
// int64 on the wire and BIGINT after zod's coercion (validateResponses).
export type FsAclRight =
    | "readData" | "writeData" | "appendData" | "readEa" | "writeEa" | "execute"
    | "deleteChild" | "readAttributes" | "writeAttributes" | "delete" | "readControl"
    | "writeDac" | "writeOwner" | "synchronize" | "accessSystemSecurity"
    | "maximumAllowed" | "genericAll" | "genericExecute" | "genericWrite" | "genericRead";

export type FsAclInheritFlag =
    | "objectInherit" | "containerInherit" | "noPropagateInherit" | "inheritOnly"
    | "inherited" | "auditSuccess" | "auditFailure";

export type FsAclLossCode =
    | "denyAce" | "aceOrder" | "granularRights" | "appendWithoutWrite"
    | "richInheritance" | "inheritOnly" | "noPropagateInherit" | "posixMask"
    | "creatorTrustee" | "auditing" | "unmappableTrustee" | "posixMaskWidening"
    | "defaultAclWidening";

export interface FsAclEntry {
    kind: "allow" | "deny" | "audit";
    trustee: string;
    rights: FsAclRight[];
    inherit?: FsAclInheritFlag[] | undefined;
    unnamedMask: bigint;
    unnamedFlags: bigint;
}

// The write-side twin: the mask halves are OPTIONAL on the way in (the
// server defaults them to 0) and accepted as number OR bigint — the facade
// serializes them to JSON numbers on the wire (JSON has no bigint).
export interface FsAclEntryInput {
    kind: "allow" | "deny" | "audit";
    trustee: string;
    rights: FsAclRight[];
    inherit?: FsAclInheritFlag[];
    unnamedMask?: number | bigint;
    unnamedFlags?: number | bigint;
}

export interface FsAclLoss {
    code: FsAclLossCode;
    severity: "lossy" | "notRepresentable";
    entries: number[];
    detail: string;
    remedy: string;
}

export interface FsAcl {
    path: string;
    model: "posix" | "nt";
    owner?: string | null | undefined;
    group?: string | null | undefined;
    entries: FsAclEntry[];
    daclPresent: boolean;
    lossyFor: ("nfs" | "smb" | "sftp" | "ftp")[];
    losses: FsAclLoss[];
}



// Directory realms (NAS-2 Fase 1a, 2026-08-15 — directoryrealms.yaml; facade
// branch added by #181 ondata 1a). The create input is typed BY HAND like the
// other creates: exactly the block matching `type` must be present (422),
// `uidRange` omitted = the automatic grid slot, supplied = the declared road
// (both engraved at creation, immutable after); `domainSid` is the EARLY road
// of D4 (`ad` only, create-only — rebinding later is `actions/refound`).
export interface DirectoryRealmCreateInput {
    name: string;
    type: "ad" | "ldap";
    credential: string;
    servers?: string[];
    ad?: { kerberosRealm: string; netbiosDomain: string; machineAccountOu?: string | null };
    // ENEA-A-R6 (2026-10-23): `kerberos` is the MIT half (null = none).
    ldap?: {
        baseDn: string;
        tls: "ldaps" | "startTls";
        userFilter?: string | null;
        groupFilter?: string | null;
        kerberos?: { realm: string; kdcs: string[]; adminServer?: string | null; nfsHostname: string; keytabCredential?: string | null } | null;
    };
    // ENEA-A-R6: the group→role map of the directory web login (both realm types).
    adminRoles?: { group: string; role: string; scope?: string | null }[] | null;
    uidRange?: { base: number; span: number };
    domainSid?: string;
    identityKeyAttribute?: string;
    offlineWindowSeconds?: number;
    scope?: string;
    notes?: string;
}

// The preflight report — THREE-valued on purpose (`ready|blocked|inconclusive`,
// never a boolean: "I could not look" is not "it is fine"); every `fail` and
// every `unknown` verdict carries `remedy` as a FIELD.
export type DirectoryRealmPreflightCheckId =
    | "declaration" | "credential" | "idRange" | "idRangeDisjoint" | "domainIdentity"
    | "identityKey" | "identityKeyInDirectory" | "dnsSrv" | "serverDns" | "reverseDns"
    | "clockSkew" | "machineAccountRights" | "directoryBindRights" | "sharePrincipal";

export interface DirectoryRealmPreflightCheck {
    id: DirectoryRealmPreflightCheckId;
    title: string;
    verdict: { kind: "pass" | "fail" | "unknown"; detail: string; remedy?: string | undefined };
}

export interface DirectoryRealmPreflight {
    outcome: "ready" | "blocked" | "inconclusive";
    observedFrom: string;
    checks: DirectoryRealmPreflightCheck[];
}

export interface DirectoryRealmRefoundResult {
    realm: string;
    previousDomainSid: string;
    domainSid: string;
    retainedAllocations: number;
    consequence: string;
}

// Answer of `actions/revokeSessions` (R9 source 3, #178) — the counters are
// rendered, never discarded (the T8 lesson); null when the server omits one.
export interface RealmSessionsRevocation {
    revokedSessions: number | null;
    revokedEditorTokens: number | null;
}

// The credential twin (SEC-REVOKE #79, wired in console with #181 ondata 1c):
// same shape by contract (credentials.yaml actionRevokeSessions is the shape
// the realm and lockdown answers reuse), same discipline — counters rendered,
// never discarded.
export interface CredentialSessionsRevocation {
    revokedSessions: number | null;
    revokedEditorTokens: number | null;
}

// The user twin (users.yaml actionRevokeSessions — the TVirt shape): same
// counters, same normalization. FACADE-TIPATA (#183): the verb's answer used
// to be an unvalidated `Record<string, unknown>`.
export interface UserSessionsRevocation {
    revokedSessions: number | null;
    revokedEditorTokens: number | null;
}



// The monitoring vocabularies (M5b, 2026-08-04, monitoring.yaml — PROPOSAL:
// names and set pending ratification): the FIXED metric catalog the platform
// samples every 5 minutes, and the kinds of target a sample belongs to. A
// closed union on purpose — the problem-code lesson applied to metrics.
export type MetricTargetKind = "node" | "cephcluster" | "multiscsicluster" | "volume" | "share" | "store" | "objectstore" | "backuptarget";
// GUI-PARITA' ondata 0 (#181, 2026-08-19): the union realigned to the FULL
// contract catalog (monitoring.yaml — 39 names, 7 target kinds). It had
// silently stopped at the M5b/PKI-2 state (22 names, 5 kinds) while the
// contract grew through SCRUB-MS, #73.1, #166, #156, NAS-2, OBJ-1 and
// SEC-TSA.1/.2 — a hand-written twin of a closed enum drifts unless every
// contract addition lands here in the same change.
// ENEA-A-R13 «erasure coding» (2026-10-24, volumes.yaml redundancy): the
// redundancy policy as a WRITE input — exactly ONE form per volume. The
// replicated pair {copies, minInSync} (REMODEL R2, the CLI's X/Y) or the
// erasure-coded {erasure: {k, m}} (the CLI's k+m; ceph only, k + m within the
// cluster's OSD hosts, anti-overcommit weight (k+m)/k); `deviceClass`
// composes with both (DISK-2 #50). `volumes.create` takes a plain record
// (the body is the contract's), `backups.restore` spells it.
// ENEA-B-R14 «QoS» (2026-10-24): the write-side QoS leaves — a share (and a
// ceph cluster's gatewayQos) takes THROUGHPUT ceilings only, a store or a
// bucket takes requests AND throughput (RGW ratelimit). Null removes (merge-patch).
export interface ShareQosInput {
    readBytesPerSec?: number | null;
    writeBytesPerSec?: number | null;
}

export interface ObjectStoreQosInput extends ShareQosInput {
    readOps?: number | null;
    writeOps?: number | null;
}

export type VolumeRedundancyInput =
    | { copies: number; minInSync: number; deviceClass?: "hdd" | "ssd" | "nvme" }
    | { erasure: { k: number; m: number }; deviceClass?: "hdd" | "ssd" | "nvme" };

export type MetricName =
    | "node.cpuLoad1" | "node.memUsedPercent" | "node.fsUsedPercent"
    | "node.netRxBytesPerSec" | "node.netTxBytesPerSec" | "node.netErrors" | "node.netDrops"
    | "node.replicaTunnelPeersDown"
    // #123 CONFIG-REPLICA: l'eta' della copia di configurazione del nodo.
    | "node.configReplicaAgeSeconds"
    // PKI-2 (2026-08-10): l'eta' della lista di revoca che il nodo applica.
    | "node.crlAgeSeconds"
    // NAS-2: la deriva del machine account; #166: il freno delle resurrezioni;
    // #73.1: le righe di audit che il backend NON e' riuscito a scrivere.
    | "node.machineAccountDivergedSeconds" | "node.daemonRestarts" | "node.auditWriteFailures"
    // METRICS-3 (#45): lo spool della receive DR contro il tetto #99.
    | "node.replicationSpoolPercent"
    // #33 EXT-2 round 3 (2026-08-22): path FAILED verso LUN di SAN terze
    // (censimento multipathd sul nodo di dovere).
    | "node.sanPathsDown"
    | "node.diskWearMaxPercent"
    | "node.diskMediaErrors"
    // ENEA-A-R16 «drain» (2026-10-24): l'unico allarme di un nodo in manutenzione.
    | "node.maintenanceHours"
    // SONDE-RESIDUE (2026-08-27): demoni ceph MUTI sul nodo — vivi di
    // processo ma che non rispondono alla sonda parlante (#127/#146).
    | "node.cephDaemonsMute"
    // REMODEL R1 (#214, 2026-08-26): la famiglia datapool.* sdoppiata per
    // specie — ogni serie vive sul kind di cluster che la produce.
    | "cephcluster.capacityGiB" | "cephcluster.usedGiB" | "cephcluster.usedPercent" | "cephcluster.daysToFull"
    // METRICS-3 (#45): OSD giu' dal censimento ceph gia' scritto.
    | "cephcluster.osdsDown"
    // #204 CEPH-STRETCH Fase B (2026-08-25): PG non attivi = il pool blocca.
    | "cephcluster.pgsInactive"
    | "multiscsicluster.capacityGiB" | "multiscsicluster.usedGiB" | "multiscsicluster.usedPercent" | "multiscsicluster.daysToFull"
    // SCRUB-MS (#94): mismatch dell'ultima passata e giorni dall'ultima completa.
    | "multiscsicluster.scrubMismatches" | "multiscsicluster.scrubAgeDays"
    // METRICS-3 (#45): PV spariti sotto un pool che serve.
    | "multiscsicluster.missingPvs"
    | "volume.health" | "volume.backupAgeHours" | "volume.replicaLagSeconds" | "volume.resyncPercent"
    // #39 REP-SYNC-MS (2026-08-22): la promessa RPO=0 rotta ad alta voce.
    | "volume.syncDegraded"
    // METRICS-3 (#45): i rate e l'await del device md del volume (multiscsi).
    | "volume.readBytesPerSec" | "volume.writeBytesPerSec" | "volume.readIops" | "volume.writeIops" | "volume.ioAwaitMs"
    | "share.health" | "share.clients" | "share.fsUsedPercent" | "share.daysToFull"
    // #136/#137: vivo-ma-non-serve e streak di morti; #156: lease SMB non rinnovato.
    | "share.daemonsNotServing" | "share.daemonRestarts" | "share.smbLeaseUnrenewedSeconds"
    // ENEA-A-R12 «gestione 2» (2026-10-23): le metriche dall'audit client
    // (presenti solo con l'audit della share acceso).
    | "share.opsPerSec" | "share.bytesPerSec" | "share.deleteOps" | "share.authFailures" | "share.accessDenied"
    | "store.health" | "store.lagSeconds"
    // OBJ-1: la salute 0/1 dell'object store.
    | "objectstore.health"
    // ENEA-A-R12: le metriche dall'audit client S3 (solo con l'audit dello store acceso).
    | "objectstore.requestsPerSec" | "objectstore.bytesPerSec" | "objectstore.deleteOps" | "objectstore.authFailures" | "objectstore.accessDenied"
    // SEC-TSA.1 (#179) e SEC-TSA.2 (#180): la catena RFC 3161 sui backup.
    | "backuptarget.timestampUnreachable" | "backuptarget.timestampRefused" | "backuptarget.timestampUnverifiable"
    | "backuptarget.timestampInvalid" | "backuptarget.timestampTampered" | "backuptarget.timestampAgeDays";
export type TrendRange = "1h" | "6h" | "24h" | "7d" | "30d" | "90d";



// The identifier-first auth surfaces (TVirt round #102 pattern, adopted for TVStor
// 2026-08-02). The types mirror what the backend ALWAYS renders, so clients never
// read the guaranteed fields as possibly-undefined.
export type IdentifyResult = { realm: string; method: "password"; realmKind?: "local" | "cluster" | "files" | "directory" | undefined };
// WEBEXP-KIOSK (ratifica Luca 2026-08-05): `files`-realm logins answer a KIOSK
// session — `mode` says which chrome the client mounts, `kioskShares` is the
// browse perimeter {id, name, writable} the picker needs (the share-list routes
// are RBAC-fenced away from kiosk principals). Both ride login AND the
// introspection of one's own session (`sessions.current`), recomputed live.
export type KioskShare = { id: string; name: string; writable: boolean };
export type Session = {
    id: string;
    user: { username: string; realm: string; displayName: string };
    createdAt: string;
    expiresAt: string;
    active: boolean;
    mustChangePassword?: boolean | undefined;
    // MFA.2 (2026-08-18): the enrolment warning — present together, on the
    // login response only, while the credential is marked mfaRequired with no
    // second factor and the grace window is open (contract auth.yaml).
    mfaEnrollmentDeadline?: string | undefined;
    mfaEnrollmentDaysLeft?: number | undefined;
    mode?: "console" | "kiosk" | undefined;
    kioskShares?: KioskShare[] | undefined;
    // ENEA-A-R6 (2026-10-23): the roles the realm's adminRoles map gave a directory session (console); absent on platform principals.
    roles?: readonly string[] | undefined;
};
export type SessionCreated = Session & {
    token: string;
};
// #74 MFA-1 (contract 2026-08-18; facade #181 ondata 1b): the enrolment
// material `actions/enrollTotp` answers EXACTLY ONCE — the server keeps the
// secret sealed and the recovery codes hashed, nothing here can be fetched
// again. `otpauthUri` is DATA for the client to render as a QR code.
export type TotpEnrollment = {
    secret: string;
    otpauthUri: string;
    recoveryCodes: string[];
};



interface TransportResult {
    data?: unknown;
    error?: unknown;
    response: Response;
}



function filterString(term: FilterTerm): string {
    return `${term.field}:${term.op}:${JSON.stringify(term.value)}`;
}



// CRUD-3 (#207): la forma sul filo delle due maniglie. `dryRun` viaggia SOLO
// quando e' true (il default del contract e' false — l'omissione e' la forma
// onesta); la chiave viaggia SOLO quando il chiamante l'ha coniata.
function dryRunQuery(opts: DryRunOptions): { dryRun?: boolean } {
    return opts.dryRun === true ? { dryRun: true } : {};
}

function idempotencyHeaders(opts: IdempotencyOptions): Record<string, string> {
    return opts.idempotencyKey !== undefined ? { "idempotency-key": opts.idempotencyKey } : {};
}



// Accepts both the bare `t-…` id and the X-TVStor-Task URL form (takes the last
// path segment).
function parseTaskRef(ref: string): { id: string } {
    return { id: ref.split("/").pop() ?? ref };
}



// NAS-MDS-1 via B: the 202 body of `volumes.migrateToSharedFilesystem`
// (contract volumes.yaml actionMigrateToSharedFilesystem).
const zMigrateToSharedFilesystemAccepted = z.object({
    task: z.string(),
    shares: z.array(z.string()),
    estimatedBytes: z.number(),
});



// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- the facade's shape IS its inferred type
export function createClientV1(options: ClientOptionsV1) {
    // M1 (2026-08-02): mutable closure vars again, as the M0 note promised —
    // auth.login stores the session token, auth.sudo stores the X-TVStor-Sudo grant.
    let token: string | null = options.token ?? null;
    let sudoToken: string | null = null;
    const validate = options.validateResponses ?? true;
    const fetchImpl = options.fetch ?? fetch;
    const transport = createFetchClient<paths>({ baseUrl: options.baseUrl, fetch: fetchImpl });

    transport.use({
        onRequest({ request }) {
            // ENEA-A-R11: an authorization header a verb set EXPLICITLY (the
            // metrics scrape with the tvsm_ token) wins over the session.
            if (token !== null && !request.headers.has("authorization")) {
                request.headers.set("authorization", `Bearer ${token}`);
            }
            if (sudoToken !== null) {
                request.headers.set("x-tvstor-sudo", sudoToken);
            }
            return request;
        },
    });



    // E2E-2c rilievo 86 (2026-11-01): the metadata-lease retry — see
    // MetadataLeasePolicy. A spare clone of every JSON (or body-less) mutation
    // is kept by request id until its response is in; a 409 `metadata_lease_held`
    // with the policy saying "wait" is answered by waiting for the holder and
    // re-sending the clone through the same fetch (the auth header already
    // rides it); the retried response takes the first one's place, so the
    // facade's unwrap sees the final answer. Streaming bodies (uploads) are
    // never cloned — they could not be re-sent.
    const leasePolicy = options.metadataLease;
    if (leasePolicy !== undefined) {
        const spare = new Map<string, Request>();
        const retriable = (request: Request): boolean => {
            if (request.method === "GET" || request.method === "HEAD") {
                return false;
            }
            const contentType = request.headers.get("content-type") ?? "";
            return request.body === null || contentType.startsWith("application/json") || contentType.startsWith("application/merge-patch+json");
        };
        transport.use({
            onRequest({ request, id }) {
                if (retriable(request)) {
                    spare.set(id, request.clone());
                }
                return undefined;
            },
            async onResponse({ response, id }) {
                const copy = spare.get(id);
                spare.delete(id);
                if (copy === undefined || response.status !== 409) {
                    return undefined;
                }
                let answer = response;
                const limit = leasePolicy.maxAttempts ?? LEASE_RETRY_ATTEMPTS;
                for (let attempt = 1; attempt <= limit; attempt += 1) {
                    const holder = leaseHolderOf(await answer.clone().json().catch((): null => null));
                    if (holder === null || !leasePolicy.wait()) {
                        return answer;
                    }
                    leasePolicy.onWait?.(holder, attempt);
                    const started = Date.now();
                    await tasks.waitForLeaseRelease(holder, { timeoutMs: leasePolicy.holderTimeoutMsOf?.(holder) ?? LEASE_HOLDER_WAIT_MS });
                    leasePolicy.onReleased?.(holder, attempt, Date.now() - started);
                    answer = await fetchImpl(copy.clone());
                    if (answer.status !== 409) {
                        return answer;
                    }
                }
                return answer;
            },
            onError({ id }) {
                spare.delete(id);
                return undefined;
            },
        });
    }



    function unwrap<T>(result: TransportResult, schema?: z.ZodType<T>): T {
        if (result.error !== undefined) {
            throw new TVStorApiError(result.response.status, result.error as Record<string, unknown>, result.response.headers);
        }
        if (validate && schema !== undefined) {
            return schema.parse(result.data);
        }
        return result.data as T;
    }



    function listQuery(p: ListParams): Record<string, unknown> {
        const query: Record<string, unknown> = {};
        if (p.filter !== undefined && p.filter.length > 0) {
            query["filter"] = p.filter.map(filterString);
        }
        if (p.sort !== undefined) {
            query["sort"] = `${p.sort.direction === "desc" ? "-" : ""}${p.sort.field}`;
        }
        if (p.scope !== undefined) {
            query["scope"] = p.scope;
        }
        if (p.limit !== undefined) {
            query["limit"] = p.limit;
        }
        if (p.cursor !== undefined && p.cursor !== null) {
            query["cursor"] = p.cursor;
        }
        return query;
    }



    function page<T>(result: TransportResult, schema: z.ZodType<T>): Page<T> {
        const body = unwrap<{ items: unknown[]; nextCursor?: string; stale?: boolean }>(result);
        const items = validate ? body.items.map((item) => schema.parse(item)) : (body.items as T[]);
        return { items, nextCursor: body.nextCursor ?? null, ...(body.stale === true ? { stale: true } : {}) };
    }



    // Restored with M1 as the M0 note promised (unwrap + task ref from the
    // X-TVStor-Task response header, tvirt's create/patch idiom); consumers since
    // M2 — volumes, vdisks and targets create/delete (2026-08-02, reshaped M2d).
    function writeResult<T>(result: TransportResult, schema?: z.ZodType<T>): WriteResult<T> {
        const resource = unwrap<T>(result, schema);
        return { resource, task: result.response.headers.get("x-tvstor-task") };
    }



    function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
        const headers: Record<string, string> = { ...extra };
        if (token !== null) {
            headers["authorization"] = `Bearer ${token}`;
        }
        if (sudoToken !== null) {
            headers["x-tvstor-sudo"] = sudoToken;
        }
        return headers;
    }



    const system = {
        // The M0 liveness probe: { status, node, version, time } — no auth required.
        health: async (): Promise<SystemHealth> =>
            unwrap(await transport.GET("/api/v1/system/health"), zGetSystemHealthResponse),

        // The served contract itself (GET /api/v1/openapi.json) — a raw fetch: the
        // document is not a modelled resource, and no auth is required to read it.
        openapi: async (): Promise<Record<string, unknown>> => {
            const response = await fetchImpl(`${options.baseUrl}/api/v1/openapi.json`, { headers: authHeaders() });
            if (!response.ok) {
                throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
            }
            return (await response.json()) as Record<string, unknown>;
        },
    };



    const auth = {
        // Step 1 of the identifier-first login: the realm is READ OUT OF the
        // identifier the user typed and the client is told HOW to authenticate —
        // never WHETHER the account exists. Since #118 NAS-QUALNAMES (ratifica
        // Luca 2026-08-14 §2) the identifier MUST be qualified — `REALM\user` or
        // `user@REALM` — and a bare name answers 422 naming both forms; there is
        // no realm defaulting left to guess with. Needs no authentication at all.
        identify: async (identifier: string): Promise<IdentifyResult> =>
            unwrap(await transport.POST("/api/v1/auth/identify", { body: { identifier } }), zIdentifyResult),

        // Step 2: the username carries its realm (or `realm` states it in its own
        // field — the same qualification, and it wins); the returned tvss_ token
        // (shown exactly once) is stored on the client and rides every following
        // request.
        // `otp` (MFA.2, 2026-08-18): the second factor — a 6-digit TOTP code
        // or a single-use recovery code, told apart server-side by shape.
        // Omitted on a credential that has one, the server answers
        // `401 mfa_required` and the caller retries with the code.
        login: async (username: string, realm: string | undefined, password: string, otp?: string): Promise<SessionCreated> => {
            const session = unwrap<SessionCreated>(
                await transport.POST("/api/v1/auth/sessions", {
                    body: {
                        username,
                        password,
                        ...(realm !== undefined ? { realm } : {}),
                        ...(otp !== undefined ? { otp } : {}),
                    },
                }),
                zSessionCreated,
            );
            token = session.token;
            return session;
        },

        logout: async (): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/auth/sessions/current"));
            token = null;
            sudoToken = null;
        },

        // Sudo elevation: the tvsu_ grant rides every following request as
        // X-TVStor-Sudo until it expires server-side.
        sudo: async (password: string): Promise<void> => {
            const grant = unwrap(await transport.POST("/api/v1/auth/sudo", { body: { password } }), zSudoGrant);
            sudoToken = grant.sudoToken;
        },

        setToken: (value: string | null): void => {
            token = value;
        },

        clearToken: (): void => {
            token = null;
            sudoToken = null;
        },

        // Permission introspection: the catalog subset the calling credential
        // effectively holds on ONE scope (path string or Scope id, no `/*`), as
        // plain names. Unknown or out-of-reach scopes answer [], indistinguishable
        // from holding nothing — clients disable actions up front instead of
        // harvesting 403s.
        mePermissions: async (scope: string): Promise<string[]> => {
            const body = unwrap<{ items: { name: string }[] }>(
                await transport.GET("/api/v1/auth/me/permissions", { params: { query: { scope } } }),
                zPermissionList,
            );
            return body.items.map((entry) => entry.name);
        },

        // Scoped sessions (TVirt #24/#25 semantics): one's OWN sessions answer on
        // authentication alone; another principal's only while the caller holds
        // user.sessions.revoke on the OWNER's scope — outside that reach an id
        // reads as 404. `current` is the calling token's own session; `revoke` is
        // the scoped delete (on an own session, a logout by id). The list carries
        // only live sessions of visible principals.
        // `realm` (SEC-REVOKE #79 D2, wired here with #181 ondata 1c): keep only
        // one realm's sessions — `files` is the incident filter ("show me every
        // file-services session"). Filtering narrows what the caller may already
        // see, it never widens it; an unknown realm answers an empty page.
        sessions: {
            list: async (p: Pick<ListParams, "limit" | "cursor"> & { realm?: string | undefined } = {}): Promise<Page<Session>> =>
                page(await transport.GET("/api/v1/auth/sessions", {
                    params: {
                        query: {
                            ...listQuery(p),
                            ...(p.realm !== undefined ? { realm: p.realm } : {}),
                        } as never,
                    },
                }), zSession),

            get: async (id: string): Promise<Session> =>
                unwrap(await transport.GET("/api/v1/auth/sessions/{session}", { params: { path: { session: id } } }), zSession),

            current: async (): Promise<Session> =>
                unwrap<Session>(await transport.GET("/api/v1/auth/sessions/current"), zSession),

            revoke: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v1/auth/sessions/{session}", { params: { path: { session: id } } }));
            },
        },
    };



    const users = {
        list: async (p: ListParams<FilterableFields["listUsers"]> = {}): Promise<Page<User>> =>
            page(await transport.GET("/api/v1/users", { params: { query: listQuery(p) as never } }), zUser),

        listAll: (p: Omit<ListParams<FilterableFields["listUsers"]>, "limit" | "cursor"> = {}): PageIterator<User> =>
            genPageIterator(async ({ cursor }) => users.list({ ...p, cursor })),

        get: async (id: string): Promise<User> =>
            unwrap(await transport.GET("/api/v1/users/{user}", { params: { path: { user: id } } }), zUser),

        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<User> =>
            unwrap(await transport.POST("/api/v1/users", { params: { query: dryRunQuery(opts) }, body: body as never }), zUser),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<User> =>
            unwrap(await transport.PATCH("/api/v1/users/{user}", {
                params: { path: { user: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zUser),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/users/{user}", { params: { path: { user: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v1/users/batch", { body: { ids } as never }), zBatchDeleteResult),

        // The calling account, whoever it is — the profile surface behind the
        // avatar menu.
        myself: async (): Promise<User> =>
            unwrap(await transport.GET("/api/v1/users/myself"), zUser),

        patchMyself: async (mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<User> =>
            unwrap(await transport.PATCH("/api/v1/users/myself", {
                params: { query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zUser),

        // Administrative reset (sudo-protected): mustChangePassword defaults to
        // true server-side — the user proves the handover at next login.
        setPassword: async (id: string, newPassword: string, opts: { mustChangePassword?: boolean } = {}): Promise<void> => {
            unwrap(await transport.PUT("/api/v1/users/{user}/password", {
                params: { path: { user: id } },
                body: { newPassword, ...(opts.mustChangePassword !== undefined ? { mustChangePassword: opts.mustChangePassword } : {}) } as never,
            }));
        },

        // Self-service change (clears mustChangePassword): verifies currentPassword,
        // 403 on mismatch, 422 against the password policy. Existing sessions survive.
        setMyPassword: async (currentPassword: string, newPassword: string, opts: IdempotencyOptions = {}): Promise<void> => {
            unwrap(await transport.PUT("/api/v1/users/myself/password", {
                body: { currentPassword, newPassword } as never,
                headers: idempotencyHeaders(opts),
            }));
        },

        // Clears the lockout thresholds (Annex B heritage).
        unlock: async (id: string, opts: IdempotencyOptions = {}): Promise<User> =>
            unwrap(await transport.POST("/api/v1/users/{user}/actions/unlock", {
                params: { path: { user: id } },
                headers: idempotencyHeaders(opts),
            }), zUser),

        // Kills the user's sessions; answers the revocation counts — rendered,
        // never discarded (the T8 lesson), normalized like the credential and
        // realm twins. FACADE-TIPATA (#183): the response now parses through
        // the generated zRevokeUserSessionsResponse (it used to ride unvalidated).
        revokeSessions: async (id: string, opts: IdempotencyOptions = {}): Promise<UserSessionsRevocation> => {
            const body = unwrap(await transport.POST("/api/v1/users/{user}/actions/revokeSessions", {
                params: { path: { user: id } },
                headers: idempotencyHeaders(opts),
            }), zRevokeUserSessionsResponse);
            return {
                revokedSessions: body.revokedSessions ?? null,
                revokedEditorTokens: body.revokedEditorTokens ?? null,
            };
        },

        // #74 MFA-1 (#181 ondata 1b): TOTP enrolment on the CALLING account —
        // self-service only by contract (the secret must reach the human), and
        // SUDO-PROTECTED: call auth.sudo first, the grant rides automatically.
        // The material is answered EXACTLY ONCE; the enrolment stays PENDING
        // (login untouched) until confirmTotp proves possession.
        enrollTotp: async (): Promise<TotpEnrollment> =>
            unwrap<TotpEnrollment>(await transport.POST("/api/v1/users/myself/actions/enrollTotp"), zTotpEnrollment),

        // Activates the pending enrolment with a current code from the
        // authenticator (422 names the field when wrong; 429 spends the same
        // credential throttle as the login). Answers the account render with
        // mfaEnrolled: true.
        confirmTotp: async (otp: string): Promise<User> =>
            unwrap(await transport.POST("/api/v1/users/myself/actions/confirmTotp", { body: { otp } }), zUser),

        // Removes the CALLING account's TOTP enrolment (confirmed or pending)
        // and destroys the sealed secret + remaining recovery codes. Sudo-
        // protected by contract: weakening the login gate is the first gesture
        // a stolen bearer would try.
        disenrollMyTotp: async (): Promise<User> =>
            unwrap(await transport.POST("/api/v1/users/myself/actions/disenrollTotp"), zUser),

        // The administrative reset (user.mfa.reset + sudo): the recovery lever
        // for the user whose authenticator AND recovery codes are gone.
        disenrollTotp: async (id: string): Promise<User> =>
            unwrap(await transport.POST("/api/v1/users/{user}/actions/disenrollTotp", {
                params: { path: { user: id } },
            }), zUser),
    };



    const scopes = {
        list: async (p: ListParams<FilterableFields["listScopes"]> = {}): Promise<Page<Scope>> =>
            page(await transport.GET("/api/v1/scopes", { params: { query: listQuery(p) as never } }), zScope),

        listAll: (p: Omit<ListParams<FilterableFields["listScopes"]>, "limit" | "cursor"> = {}): PageIterator<Scope> =>
            genPageIterator(async ({ cursor }) => scopes.list({ ...p, cursor })),

        get: async (id: string): Promise<Scope> =>
            unwrap(await transport.GET("/api/v1/scopes/{scope}", { params: { path: { scope: id } } }), zScope),

        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Scope> =>
            unwrap(await transport.POST("/api/v1/scopes", { params: { query: dryRunQuery(opts) }, body: body as never }), zScope),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Scope> =>
            unwrap(await transport.PATCH("/api/v1/scopes/{scope}", {
                params: { path: { scope: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zScope),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/scopes/{scope}", { params: { path: { scope: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v1/scopes/batch", { body: { ids } as never }), zBatchDeleteResult),

        // ENEA-B-R15 «tenant»: the report of the scope AND ITS DESCENDANTS as
        // ONE JSON document (quota, net/raw promises, used, counts, addresses,
        // qos/audit summaries, rows) — scope.report.
        report: async (id: string): Promise<ScopeReport> =>
            unwrap(await transport.GET("/api/v1/scopes/{scope}/report", {
                params: { path: { scope: id }, query: { format: "json" } as never },
            }), zScopeReport),

        // The same report's ROWS as CSV/JSONL, STREAMED (the catalog export's
        // seam): the body stream and the headers to save by — never buffered.
        reportExport: async (id: string, format: "csv" | "jsonl"): Promise<FsDownload> => {
            const query = new URLSearchParams({ format });
            const response = await fetchImpl(`${options.baseUrl}/api/v1/scopes/${encodeURIComponent(id)}/report?${query.toString()}`, { headers: authHeaders() });
            if (!response.ok || response.body === null) {
                throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
            }
            const length = response.headers.get("content-length");
            return {
                stream: response.body,
                contentType: response.headers.get("content-type"),
                contentLength: length !== null ? Number(length) : null,
                contentDisposition: response.headers.get("content-disposition"),
            };
        },
    };



    const rbac = {
        permissions: async (): Promise<PermissionInfo[]> => {
            const body = unwrap(
                await transport.GET("/api/v1/rbac/permissions"),
                zPermissionList,
            );
            return body.items;
        },

        roles: {
            list: async (p: ListParams<FilterableFields["listRbacRoles"]> = {}): Promise<Page<Role>> =>
                page(await transport.GET("/api/v1/rbac/roles", { params: { query: listQuery(p) as never } }), zRole),

            listAll: (p: Omit<ListParams<FilterableFields["listRbacRoles"]>, "limit" | "cursor"> = {}): PageIterator<Role> =>
                genPageIterator(async ({ cursor }) => rbac.roles.list({ ...p, cursor })),

            get: async (id: string): Promise<Role> =>
                unwrap(await transport.GET("/api/v1/rbac/roles/{role}", { params: { path: { role: id } } }), zRole),

            create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Role> =>
                unwrap(await transport.POST("/api/v1/rbac/roles", { params: { query: dryRunQuery(opts) }, body: body as never }), zRole),

            patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Role> =>
                unwrap(await transport.PATCH("/api/v1/rbac/roles/{role}", {
                    params: { path: { role: id }, query: dryRunQuery(opts) },
                    body: mergePatch as never,
                    headers: { "content-type": "application/merge-patch+json" },
                }), zRole),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v1/rbac/roles/{role}", { params: { path: { role: id } } }));
            },

            batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
                unwrap(await transport.DELETE("/api/v1/rbac/roles/batch", { body: { ids } as never }), zBatchDeleteResult),
        },

        grants: {
            list: async (p: ListParams<FilterableFields["listRbacGrants"]> = {}): Promise<Page<Grant>> =>
                page(await transport.GET("/api/v1/rbac/grants", { params: { query: listQuery(p) as never } }), zGrant),

            listAll: (p: Omit<ListParams<FilterableFields["listRbacGrants"]>, "limit" | "cursor"> = {}): PageIterator<Grant> =>
                genPageIterator(async ({ cursor }) => rbac.grants.list({ ...p, cursor })),

            get: async (id: string): Promise<Grant> =>
                unwrap(await transport.GET("/api/v1/rbac/grants/{grant}", { params: { path: { grant: id } } }), zGrant),

            create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Grant> =>
                unwrap(await transport.POST("/api/v1/rbac/grants", { params: { query: dryRunQuery(opts) }, body: body as never }), zGrant),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v1/rbac/grants/{grant}", { params: { path: { grant: id } } }));
            },

            batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
                unwrap(await transport.DELETE("/api/v1/rbac/grants/batch", { body: { ids } as never }), zBatchDeleteResult),
        },
    };



    // Mostly read-only (the joiner lifecycle and the store-member actions join with
    // their milestones); since M2a (2026-08-02) the node item accepts PATCH for the
    // per-node declarations — `dataDisks` (explicit disk eligibility, N.15
    // precedent; the CLI verb is `config-node <node> mark-disk` since M2d,
    // dettatura 2026-08-03) and `notes`. M2d adds the vdisks subresource. M2e
    // (ratifiche Luca 2026-08-03) adds `fencingDevices` to the same node PATCH —
    // under the contract's FIELD-LEVEL GUARD: touching it needs
    // `cluster.node.fencing` AND an active sudo grant (auth.sudo first), the rest
    // of the patch stays plain `cluster.node.update` — and the singleton gains its
    // FIRST write: `patch` below, for the witness configuration (sudo-protected).
    // M2g (2026-08-03): each `fencingDevices[]` entry may declare `credential`,
    // a keystore `bmcAccount` reference BY ID (real BMC/switch/PDU accounts —
    // the `tvstor-fence` placeholder retired); it rides the same guarded node
    // PATCH untyped — only the ID ever travels, never secret material.
    // M2f (dettatura Luca 2026-08-03, 8° giro — YugabyteDB DROPPED; ack
    // policy RATIFIED 9° giro, same day): the singleton's readOnly render
    // grows the `store` block (primary, members[] with roles/slots/lag/
    // syncState plus the ack-set marks inAckSet/fenced, degraded +
    // degradedReason) as a pure PASSTHROUGH riding zCluster untouched, and
    // the `store` namespace below carries the three task-born actions —
    // `promote` (sudo AND confirm-typed), `addMember` / `removeMember`
    // (sudo). The M2f `setSyncPolicy` write is GONE with the `ANY k`
    // PROPOSAL it configured: the ack policy is fixed strict-all (every
    // live, non-excluded secondary), so the block is observation end to end.
    const cluster = {
        get: async (): Promise<Cluster> =>
            unwrap(await transport.GET("/api/v1/cluster"), zCluster),

        // JBOD-DC-1 (2026-09-06): the fleet-wide disk census — one entry per
        // WWID with the nodes that see it (`shared: true` when more than one).
        // Unpaginated by contract (bounded by the fleet's disks): a plain array.
        disks: {
            list: async (): Promise<ClusterDisk[]> =>
                unwrap(await transport.GET("/api/v1/cluster/disks"), zClusterDiskList).items,
        },

        // The singleton PATCH (M2e, 2026-08-03): writable `witness` (the witnessd
        // endpoints/order/lease timings — null removes the witness), `monitoring`
        // (M5b-1, 2026-08-04: {retentionDays} — null returns to the defaults),
        // `replicaEncryption` (ENC-1, 2026-08-05: {enabled, listenPort, mtu,
        // persistentKeepaliveSec} — enabling mints the per-node wireguardKey
        // credentials and overlay indices; null returns to the disabled
        // defaults, keys kept; the `nodes` census is readOnly)
        // `nfsEngine` (ENEA-A-R2, 2026-10-23: {ceph: ganesha|knfsd}, null = back to ganesha),
        // `gatewayFailover` (ENEA-A-R4, 2026-10-23: {rebalance: automatic|manual, rebalanceAfterMs}, null = defaults),
        // `transportSecurity` (ENEA-A-R1 N6, 2026-10-23: {plainHttp: allowed|refused}
        // — null returns to `allowed`) and `notes`.
        // The M2f `store.syncPolicy` write is retired (9° giro, 2026-08-03).
        // Sudo-protected: call auth.sudo first, the grant rides automatically.
        patch: async (mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Cluster> =>
            unwrap(await transport.PATCH("/api/v1/cluster", {
                params: { query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zCluster),

        // The AUDIT REGISTRY (#73 AUDIT-1, cluster.yaml `audit`): every API
        // mutation the cluster answered — POST/PUT/PATCH/DELETE, actions
        // included, FAILED attempts included (a 401 login and a 403 refusal
        // are outcomes, not omissions). Read-only by construction: this GET
        // is the registry's only route; rows age out through
        // `Cluster.audit.retentionDays` alone. Store-resident on purpose —
        // with the store down the list answers `503 store_unavailable`
        // (surfaced as TVStorApiError), never a clean-looking empty page
        // from the replica. Newest first by default; time filters ride
        // `occurredAt` (gte/lt), the credential axis rides
        // `username`/`userId`/`sessionId`/`realm`, the resource axis rides
        // `route` (the matched template) and `path` (the instance).
        audit: {
            list: async (p: Omit<ListParams<FilterableFields["listAuditEvents"]>, "scope"> = {}): Promise<Page<AuditEvent>> =>
                page(await transport.GET("/api/v1/cluster/audit", { params: { query: listQuery(p) as never } }), zAuditEvent),

            listAll: (p: Omit<ListParams<FilterableFields["listAuditEvents"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<AuditEvent> =>
                genPageIterator(async ({ cursor }) => cluster.audit.list({ ...p, cursor })),

            // ENEA-A-R11 (2026-10-23, N4): re-walk the per-node HASH CHAIN on
            // the store — every row's `hash` recomputed from the canonical
            // form v1 of its fields plus the previous row's hash; the first
            // break is NAMED (id + reason), rows written before the chain
            // (no hash) count as `unchained`, a retention prune leaves a
            // `prune` row carrying the last removed hash so the chain «starts
            // here» instead of screaming. `node` narrows to one node's
            // chain; omitted = every node. Tamper EVIDENCE, not a signature
            // (the manual says so). `cluster.audit.read`.
            verify: async (body: { node?: string } = {}): Promise<AuditChainVerification> =>
                unwrap(await transport.POST("/api/v1/cluster/audit/actions/verify", { body: body as never }), zAuditChainVerification),
        },

        // The cluster STORE (M2f, 2026-08-03): PostgreSQL managed by the platform
        // at EVERY scale — one primary + N streaming secondaries, promotion driven
        // by the ratified witness grant / fencing cascade (ONE arbiter for the md
        // authority AND the store), epoch re-minted on the new primary.
        store: {
            // Convenience read: the `store` block of the singleton — PASSTHROUGH,
            // the rendered object as validated by zCluster, never re-shaped here.
            // Null on a render that carries no store block (a store read that
            // could not observe it, or a caller without `cluster.store.read`).
            get: async (): Promise<NonNullable<Cluster["store"]> | null> => {
                const rendered = unwrap(await transport.GET("/api/v1/cluster"), zCluster);
                return rendered.store ?? null;
            },

            // MANUAL promotion (M2f, 2026-08-03) — the operator's entry into the
            // SAME machinery the automatic failover engine drives when it holds a
            // witness grant or a VERIFIED fence. Sudo-protected AND confirm-typed:
            // `confirm` must equal the target node's name — the caller types it,
            // the facade never fills it in. Resolves with the store.promote task
            // ref (202): re-verify the proof, pg_ctl promote, RE-MINT the epoch
            // (config_generation + cluster in ONE transaction), survivors converge
            // down, the old primary re-attaches as a SECONDARY (S6/D6). No valid
            // proof that the current primary is stopped is 412 precondition_failed
            // (D1: a timeout alone is never proof) — surfaced as TVStorApiError.
            // A FENCE-EXCLUDED survivor (members[].fenced — 9° giro) is refused
            // by the automatic path everywhere: THIS confirm-typed call is the
            // only way to promote it, and the typed confirm is the operator's
            // declared acceptance of the acked-write loss (twin of forcePromote).
            promote: async (body: { node: string; confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/actions/promoteStore", { body: body as never, headers: idempotencyHeaders(opts) });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // PLANNED switchover (STORE-HA-3 F, ratifica 2026-11-03 §35.3): the
            // operator moves the primary role off a LIVE primary — nothing is lost,
            // nobody is declared dead — so the call is sudo-protected but NOT
            // confirm-typed. `node` is optional: absent, the most aligned ack-set
            // secondary is chosen. Resolves with the store.switchover task ref (202),
            // affine to the current primary: cession election at the witness, local
            // floor, fast stop; the candidate promotes through the ordinary nomination
            // (one epoch, replayed) and the old primary re-attaches as a secondary.
            // No live primary / no witness in quorum mode / candidate outside the
            // ack set is 412; a non-secondary `node` is 422; a store task in flight
            // is 409 — surfaced as TVStorApiError.
            switchover: async (body: { node?: string } = {}, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/actions/switchoverStore", { body: body as never, headers: idempotencyHeaders(opts) });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // Grow the store by one SECONDARY (M2f, 2026-08-03) — with YugabyteDB
            // dropped this IS the 2→3 growth path: slot + pg_basebackup + a
            // detached worker-supervised instance, no engine migration left.
            // Sudo-protected. SCOPE FENCE: no join flow yet — the node row must
            // already exist (404 otherwise). Resolves with the store.member.add
            // task ref (202).
            addMember: async (body: { node: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/actions/addStoreMember", { body: body as never, headers: idempotencyHeaders(opts) });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // Shrink the store by one secondary (M2f, 2026-08-03): clear the
            // membership first (while the member still acks — strict-all, 9°
            // giro), then stop the instance and DROP its replication slot on
            // the primary — a slot left behind pins WAL forever.
            // Sudo-protected. Removing the PRIMARY is 409 conflict (promote
            // another member first); under strict-all a removal shrinks the
            // durability set BY DECLARATION — the M2f below-k refusal is
            // retired with the k. Resolves with the store.member.remove task
            // ref (202).
            removeMember: async (body: { node: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/actions/removeStoreMember", { body: body as never, headers: idempotencyHeaders(opts) });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },
        },

        // ENEA-A-R16: the orchestrated rolling update — ONE parent task
        // `cluster.rollingUpdate` advanced by the backend sweep (gates →
        // drain → await the node's return → undrain → gates → next node),
        // persisted in the task itself and resumed after a backend restart.
        // It stops at the first red gate and never proceeds by itself.
        rollingUpdate: {
            // Start (confirm-typed with the cluster's name, sudo): resolves
            // with the parent task ref (202).
            start: async (body: { confirm: string; nodes?: string[]; expectedVersion?: string; awaitReturnTimeoutMinutes?: number; drainTimeoutMinutes?: number }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/actions/rollingUpdate", { body: body as never, headers: idempotencyHeaders(opts) });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // Pause: the step in flight finishes, no new one opens.
            pause: async (): Promise<RollingUpdateState> =>
                unwrap(await transport.POST("/api/v1/cluster/actions/rollingUpdatePause"), zRollingUpdateState),

            // Resume: back to the gates at the cursor (also after a failed gate).
            resume: async (): Promise<RollingUpdateState> =>
                unwrap(await transport.POST("/api/v1/cluster/actions/rollingUpdateResume"), zRollingUpdateState),

            // Abort (confirm-typed): the parent is cancelled, the current node
            // keeps the state it is in (undrain it by hand if drained).
            abort: async (body: { confirm: string }): Promise<RollingUpdateState> =>
                unwrap(await transport.POST("/api/v1/cluster/actions/rollingUpdateAbort", { body: body as never }), zRollingUpdateState),
        },

        // ENEA HP-SAN-3 (R21, 2026-10-25, cluster.yaml nodeMounts): the MOUNT
        // CENSUS of a node — what its worker last published into
        // system_status.mounts from `findmnt -J` (pseudo-filesystems filtered,
        // sorted by target, 512 cap). `node` is the node's ID (the route's path
        // parameter), not its name. The picker of a CatalogSource chooses
        // `mountPath` from `mounts[].target` — never a free field (admission
        // requires equality with a census target). `observedAt: null` + an
        // empty list = the node never published a census.
        nodeMounts: async (node: string): Promise<NodeMountCensus> =>
            unwrap(await transport.GET("/api/v1/cluster/nodes/{node}/mounts", { params: { path: { node } } }), zNodeMountCensus),

        nodes: {
            list: async (p: Omit<ListParams<FilterableFields["listClusterNodes"]>, "scope"> = {}): Promise<Page<ClusterNode>> =>
                page(await transport.GET("/api/v1/cluster/nodes", { params: { query: listQuery(p) as never } }), zClusterNode),

            listAll: (p: Omit<ListParams<FilterableFields["listClusterNodes"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<ClusterNode> =>
                genPageIterator(async ({ cursor }) => cluster.nodes.list({ ...p, cursor })),

            get: async (id: string): Promise<ClusterNode> =>
                unwrap(await transport.GET("/api/v1/cluster/nodes/{node}", { params: { path: { node: id } } }), zClusterNode),

            patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<ClusterNode> =>
                unwrap(await transport.PATCH("/api/v1/cluster/nodes/{node}", {
                    params: { path: { node: id }, query: dryRunQuery(opts) },
                    body: mergePatch as never,
                    headers: { "content-type": "application/merge-patch+json" },
                }), zClusterNode),

            // Node pre-registration (#181 ondata 5 — the facade round the CLI
            // SEAM of 2026-08-04 announced): mints the write-once JoinToken.
            // The `joinToken` half exists ONLY in this 201 body (single use,
            // TTL 30 minutes, stored hashed server-side): no read path
            // re-renders it — losing it means revoke + re-register. The name
            // MUST equal the name the new node's installation will choose;
            // a duplicate (a live `joining` registration included) answers
            // 409 conflict — tokens are never re-issued for a live
            // registration. Sudo-protected: call auth.sudo first, the grant
            // rides automatically. `site` (#204 CEPH-STRETCH Fase A,
            // 2026-08-24): the optional multi-site topology label, declared
            // at pre-registration — node-name grammar, PATCHable later.
            create: async (body: { name: string; address: string; site?: string }, opts: DryRunOptions = {}): Promise<ClusterNodeRegistration> =>
                unwrap(await transport.POST("/api/v1/cluster/nodes", { params: { query: dryRunQuery(opts) }, body: body as never }), zClusterNodeRegistration),

            // Revoke a JOINING node's registration (#181 ondata 5): kills the
            // row AND its outstanding join token in the same config write — a
            // revoked token fails the handshake even inside its TTL. SCOPE
            // FENCE (2026-08-04): this is NOT node removal — any phase other
            // than `joining` answers 409 conflict (draining a real member is
            // a later round with its own task chain). Sudo-protected.
            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v1/cluster/nodes/{node}", { params: { path: { node: id } } }));
            },

            // ENEA-A-R16 «drain»: the maintenance drain of a MEMBER node —
            // confirm-typed (the node's name), sudo-protected. Non-destructive
            // by decree: it cedes floating addresses (RADOS grace first),
            // multiscsi authority and control roles, sets `noout`, and stops
            // the node's consolidated gateways once the addresses are gone;
            // no OSD removed, no data moved. Resolves with the node-affine
            // `node.drain` task ref (202); the node's `maintenance` leaf
            // tells the state and `maintenancePending` what is still held.
            drain: async (id: string, body: { confirm: string; reason?: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/nodes/{node}/actions/drain", {
                    params: { path: { node: id } },
                    body: body as never,
                    headers: idempotencyHeaders(opts),
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // ENEA-A-R16: the way back — `noout` unset, the beats claim again
            // (addresses come home per cluster.gatewayFailover.rebalance;
            // multiscsi volumes STAY on the peer: no home of their own).
            // Resolves with the `node.undrain` task ref (202).
            undrain: async (id: string, body: { confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/cluster/nodes/{node}/actions/undrain", {
                    params: { path: { node: id } },
                    body: body as never,
                    headers: idempotencyHeaders(opts),
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // PKI-2 (2026-08-10): puts every outstanding leaf certificate
            // issued to this node on the CA-signed revocation list (verified
            // OFFLINE by every peer during the TLS handshake — the only form
            // that works when the store is exactly what cannot be reached)
            // AND marks the node so the signing route issues it nothing
            // further. The declared cost: each peer applies the list IT HAS —
            // a revocation lands within one CRL refresh period per peer
            // (watch `node.crlAgeSeconds`). Idempotent: an already-revoked
            // node answers `revoked: 0` and the list is not re-minted.
            // Sudo-protected: call auth.sudo first, the grant rides
            // automatically. Answers {node, revoked, crlNumber} — crlNumber
            // is the GENERATION now published, the number each node reports
            // as it picks the list up.
            revokeCertificates: async (id: string): Promise<ClusterNodeRevocation> =>
                unwrap(await transport.POST("/api/v1/cluster/nodes/{node}/revoke-certificates", {
                    params: { path: { node: id } },
                }), zClusterNodeRevocation),

            // PKI-2 (2026-08-10): clears the revocation mark, so the node
            // obtains a FRESH leaf on its next PKI pass. It takes NOTHING off
            // the revocation list, deliberately: revoked serials stay revoked
            // until natural expiry — readmitting the NODE does not make a
            // stolen CERTIFICATE trustworthy again. Sudo-protected.
            reinstateCertificates: async (id: string): Promise<ClusterNodeReinstatement> =>
                unwrap(await transport.POST("/api/v1/cluster/nodes/{node}/reinstate-certificates", {
                    params: { path: { node: id } },
                }), zClusterNodeReinstatement),

            // Chunks (DISK-1 L2, 2026-08-05 — dettature/ratifiche Luca
            // 2026-08-04, docs/vdisk-partial-disk-2026-08-04.md): each marked
            // disk becomes a PV in ITS OWN VG (`tvstor-<serial>`) carved into
            // ANONYMOUS, FUNGIBLE chunks of ONE per-disk size. No chunk is
            // individually addressable — the census is a per-disk COUNTER
            // (total/allocated/free), which is why `list` answers the whole
            // (unpaginated) census as a plain array and no per-chunk surface
            // exists anywhere in this facade, by design.
            chunks: {
                // The whole chunk census of the node — one entry per CHUNKED
                // disk (chunk size, counters, phase, observed VG state).
                // Unpaginated by contract (bounded by the node's marked
                // disks): a plain array, not a Page.
                list: async (nodeId: string): Promise<NodeDiskChunks[]> => {
                    const body = unwrap(
                        await transport.GET("/api/v1/cluster/nodes/{node}/chunks", {
                            params: { path: { node: nodeId } },
                        }),
                        zNodeDiskChunksList,
                    );
                    return body.items;
                },

                // The create-chunks verb (CLI: `create-chunks --disks
                // /dev/sda,/dev/sdb --size 1T` — PLURAL: several disks in one
                // call, each carved at THIS size; per-disk regimes wanting
                // different sizes issue separate calls). Resolves with the ONE
                // node-affine node.chunks.create task ref covering the whole
                // call (202). An already-chunked disk is 409 conflict; a size
                // no disk can fit is 422 speaking GiB — TVStorApiError, as ever.
                create: async (nodeId: string, body: { disks: string[]; sizeGib: number }, opts: DryRunOptions = {}): Promise<string | null> => {
                    const result = await transport.POST("/api/v1/cluster/nodes/{node}/chunks", {
                        params: { path: { node: nodeId }, query: dryRunQuery(opts) },
                        body: body as never,
                    });
                    unwrap(result);
                    return result.response.headers.get("x-tvstor-task");
                },

                // The de-chunk (dismantle a disk's chunk regime): addressed by
                // the disk SERIAL (the census key — dev paths drift across
                // boots, serials do not) and CONFIRM-TYPED with that same
                // serial (the caller types it, the facade never fills it in).
                // Only an EMPTIED regime dismantles — 409 in_use while any
                // chunk is allocated. Resolves with the node.chunks.destroy
                // task ref (202).
                delete: async (nodeId: string, serial: string, confirm: string): Promise<string | null> => {
                    const result = await transport.DELETE("/api/v1/cluster/nodes/{node}/chunks/{disk}", {
                        params: { path: { node: nodeId, disk: serial }, query: { confirm } },
                    });
                    unwrap(result);
                    return result.response.headers.get("x-tvstor-task");
                },
            },

            // Raids (DISK-1 L3, 2026-08-05): node-local md arrays over
            // PER-DISK CHUNK AGGREGATES — for each member the allocator takes
            // `count` free chunks of that disk and concatenates them into ONE
            // LV; md sees one device per disk by construction. Levels
            // raid0|raid1|raid5|raid6|raid10 ONLY (linear is ratified OUT).
            // Byte-wise admission for the redundant levels: aggregates must
            // match in BYTES counting each disk's own chunk size — the 422
            // speaks GiB per aggregate. Subresources of the node registry
            // like the vdisks — no `?scope=`.
            raids: {
                list: async (nodeId: string, p: Omit<ListParams<FilterableFields["listNodeRaids"]>, "scope"> = {}): Promise<Page<NodeRaid>> =>
                    page(await transport.GET("/api/v1/cluster/nodes/{node}/raids", {
                        params: { path: { node: nodeId }, query: listQuery(p) as never },
                    }), zNodeRaid),

                listAll: (nodeId: string, p: Omit<ListParams<FilterableFields["listNodeRaids"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<NodeRaid> =>
                    genPageIterator(async ({ cursor }) => cluster.nodes.raids.list(nodeId, { ...p, cursor })),

                get: async (nodeId: string, id: string): Promise<NodeRaid> =>
                    unwrap(await transport.GET("/api/v1/cluster/nodes/{node}/raids/{raid}", {
                        params: { path: { node: nodeId, raid: id } },
                    }), zNodeRaid),

                // The create-raid verb (CLI: `--level 0 --members
                // /dev/sda:1x,/dev/sdb:2x` — quantity only, chunks are
                // anonymous): members are `{disk: devPath, count}` — the API
                // shape of `/dev/sdX:Kx`; admission resolves each dev path
                // against the node's dataDisks and pins the serial. 201 + the
                // node-affine node.raid.make task in `.task`.
                create: async (nodeId: string, body: { name: string; level: "raid0" | "raid1" | "raid5" | "raid6" | "raid10"; members: { disk: string; count: number }[] }, opts: DryRunOptions = {}): Promise<WriteResult<NodeRaid>> =>
                    writeResult(await transport.POST("/api/v1/cluster/nodes/{node}/raids", {
                        params: { path: { node: nodeId }, query: dryRunQuery(opts) },
                        body: body as never,
                    }), zNodeRaid),

                // CONFIRM-TYPED destructive delete (`confirm` must equal the
                // raid's current name — the caller types it, the facade never
                // fills it in): stop the md, wipe the superblocks, lvremove
                // the aggregates — the chunks return FREE to their disks. A
                // raid claimed by a vdisk is 409 in_use naming it (destroy
                // the vdisk first — which only RELEASES the raid; this DELETE
                // is what dismantles it). Resolves with the node.raid.destroy
                // task ref (202).
                delete: async (nodeId: string, id: string, confirm: string): Promise<string | null> => {
                    const result = await transport.DELETE("/api/v1/cluster/nodes/{node}/raids/{raid}", {
                        params: { path: { node: nodeId, raid: id }, query: { confirm } },
                    });
                    unwrap(result);
                    return result.response.headers.get("x-tvstor-task");
                },
            },

            // Vdisks (M2d, dettatura Luca 2026-08-03; REDESIGNED by DISK-1,
            // 2026-08-05 — the whole-disk md model is retired, pre-GA break
            // ratified 2026-08-04): the node-local block device an engine
            // consumes, LEVEL FOUR of the chunk stack. Two forms, exactly one
            // per vdisk — RAID (wrap one of the node's raids, single-owner
            // claim; its md device IS the vdisk's device) or JBOD (ONE LV
            // concatenating K chunks of ONE disk — never crosses disks).
            // Task-born (`node.vdisk.make` / `node.vdisk.destroy`,
            // node-affine). Downstream nothing changes: the vdisk remains the
            // internal-transport export unit (ratifica D-C) and the
            // contribution unit of datapools. No `?scope=` — vdisks are not
            // scoped objects.
            vdisks: {
                list: async (nodeId: string, p: Omit<ListParams<FilterableFields["listNodeVdisks"]>, "scope"> = {}): Promise<Page<NodeVdisk>> =>
                    page(await transport.GET("/api/v1/cluster/nodes/{node}/vdisks", {
                        params: { path: { node: nodeId }, query: listQuery(p) as never },
                    }), zNodeVdisk),

                listAll: (nodeId: string, p: Omit<ListParams<FilterableFields["listNodeVdisks"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<NodeVdisk> =>
                    genPageIterator(async ({ cursor }) => cluster.nodes.vdisks.list(nodeId, { ...p, cursor })),

                get: async (nodeId: string, id: string): Promise<NodeVdisk> =>
                    unwrap(await transport.GET("/api/v1/cluster/nodes/{node}/vdisks/{vdisk}", {
                        params: { path: { node: nodeId, vdisk: id } },
                    }), zNodeVdisk),

                // The DISK-1 create-vdisk verb (2026-08-05): 201 + the
                // node-affine node.vdisk.make task in `.task`. EXACTLY ONE of
                // `raid` (a raid ID — the CLI resolves the node-local name) /
                // the `disk`+`chunks` pair (jbod: dev path of a marked,
                // chunked disk + how many free chunks the LV concatenates) /
                // `external` (#201 EXT-1, 2026-08-21: the foreign-SAN LUN —
                // transport iscsi|nvmeof, portals, iqn+lun | nqn+nsid?,
                // optional iscsiChap credential and declared wwid) — both,
                // neither, or half a pair is 422. The M2d
                // `type`/`members` fields are GONE (pre-GA break, ratified
                // 2026-08-04 — recreation, no compat shim). `encryption`
                // (M4c, 2026-08-04, unchanged): chosen NOW and IMMUTABLE —
                // `luks2` puts the BACKING DEVICE (the wrapped raid's md, or
                // the jbod LV) under dm-crypt with a platform-minted keystore
                // key (the operator never sees a passphrase; the recovery
                // export is the cassaforte path); changing your mind later is
                // delete + re-create. The external form refuses `luks2` this
                // round (dated seam — EXT follow-up).
                create: async (nodeId: string, body: {
                    name: string;
                    raid?: string;
                    disk?: string;
                    chunks?: number;
                    external?: {
                        transport: "iscsi" | "nvmeof";
                        portals: { host: string; port: number }[];
                        iqn?: string;
                        lun?: number;
                        nqn?: string;
                        nsid?: number;
                        credential?: string;
                        wwid?: string;
                    };
                    encryption?: { mode: "none" | "luks2" };
                }, opts: DryRunOptions = {}): Promise<WriteResult<NodeVdisk>> =>
                    writeResult(await transport.POST("/api/v1/cluster/nodes/{node}/vdisks", {
                        params: { path: { node: nodeId }, query: dryRunQuery(opts) },
                        body: body as never,
                    }), zNodeVdisk),

                // CONFIRM-TYPED destructive delete (DISK-1 semantics,
                // 2026-08-05): `confirm` must equal the vdisk's current name
                // exactly — the caller types it, the facade never fills it in.
                // Closes the LUKS mapping when encrypted (the key credential
                // is reaped), then: raid form — RELEASES the raid (the raid
                // and its md SURVIVE, re-claimable; dismantling the array is
                // the raid's own delete); jbod form — lvremoves the concat
                // LV, the chunks return to the disk's FREE counter. Resolves
                // with the node.vdisk.destroy task ref (202).
                delete: async (nodeId: string, id: string, confirm: string): Promise<string | null> => {
                    const result = await transport.DELETE("/api/v1/cluster/nodes/{node}/vdisks/{vdisk}", {
                        params: { path: { node: nodeId, vdisk: id }, query: { confirm } },
                    });
                    unwrap(result);
                    return result.response.headers.get("x-tvstor-task");
                },

                // VDISK EXPANSION (DISK-1.1, 2026-08-05 — the ratified
                // first-class resize lands; the volumes expand doctrine at
                // L4): grow-only — the new size must be strictly greater
                // than the current one AND a whole multiple of the disk's
                // chunk granule (422 otherwise, speaking GiB). JBOD form
                // only this round (a raid-form vdisk answers 422 "raid
                // expand arrives with DISK-1.2"). Resolves with the
                // node-affine node.vdisk.expand task ref (202); on an
                // engine-bound datapool the task follows the capacity up
                // the stack, so the datapool's observed capacity grows too.
                expand: async (nodeId: string, id: string, body: { sizeGib: number }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                    const result = await transport.POST("/api/v1/cluster/nodes/{node}/vdisks/{vdisk}/actions/expand", {
                        params: { path: { node: nodeId, vdisk: id } },
                        body: body as never,
                        headers: idempotencyHeaders(opts),
                    });
                    unwrap(result);
                    return result.response.headers.get("x-tvstor-task");
                },

                // VDISK MIGRATION (DISK-2 #50, 2026-08-28 — contract
                // nodeVdiskActionMigrate): vacate ONE member disk of a
                // RAID-FORM vdisk onto another marked disk of the same
                // node, live (md --replace: the kernel copies from the
                // LIVE members — no degraded window, measured; a jbod
                // answers 422 «migrate with the cross-node mirror»). Both
                // disks named by SERIAL. Resolves with the node-affine
                // node.vdisk.migrate task ref (202) — progress is observed
                // on the vdisk's deviceState census (rebuildPercent).
                migrate: async (nodeId: string, id: string, body: { fromDisk: string; toDisk: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
                    const result = await transport.POST("/api/v1/cluster/nodes/{node}/vdisks/{vdisk}/actions/migrate", {
                        params: { path: { node: nodeId, vdisk: id } },
                        body: body as never,
                        headers: idempotencyHeaders(opts),
                    });
                    unwrap(result);
                    return result.response.headers.get("x-tvstor-task");
                },
            },
        },
    };



    // Storage clusters (REMODEL R1 #214, 2026-08-26 — the datapool surface is
    // DEAD, rename secco: the two honest resources replace it). The engine
    // field and the `use` verb died with it: the CREATE births the fabric
    // (Annex N birth doctrine restored) and answers 201 with the resource in
    // phase `initializing` PLUS the birth parent task ref — the facade
    // returns both, the volumes.create precedent. The readOnly `ceph`
    // observed block rides get/list through zCephCluster untouched: pure
    // render PASSTHROUGH, never re-shaped here.
    const cephClusters = {
        list: async (p: ListParams<FilterableFields["listCephClusters"]> = {}): Promise<Page<CephCluster>> =>
            page(await transport.GET("/api/v1/cephClusters", { params: { query: listQuery(p) as never } }), zCephCluster),

        listAll: (p: Omit<ListParams<FilterableFields["listCephClusters"]>, "limit" | "cursor"> = {}): PageIterator<CephCluster> =>
            genPageIterator(async ({ cursor }) => cephClusters.list({ ...p, cursor })),

        get: async (id: string): Promise<CephCluster> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}", { params: { path: { cluster: id } } }), zCephCluster),

        // THE FABRIC BIRTH: mon+mgr per member node, one OSD per member
        // vdisk, health gate (`ceph.deploy`). Admission minimum >= 3 member
        // vdisks on >= 3 distinct nodes (the TVSTOR_CEPH_ALLOW_SINGLE_NODE
        // dev relaxation is a backend flag, never a client concern).
        create: async (body: Record<string, unknown>, opts: IdempotencyOptions = {}): Promise<{ cluster: CephCluster; task: string | null }> => {
            const result = await transport.POST("/api/v1/cephClusters", {
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            const cluster = unwrap(result, zCephCluster);
            return { cluster, task: result.response.headers.get("x-tvstor-task") };
        },

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<CephCluster> =>
            unwrap(await transport.PATCH("/api/v1/cephClusters/{cluster}", {
                params: { path: { cluster: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zCephCluster),

        // The ONE membership growth action: one new {node, vdisk} member =
        // one new OSD. Resolves with the node-affine ceph.osd.prepare task
        // ref (202); the cluster rebalances onto the new OSD by itself.
        addMember: async (id: string, body: { node: string; vdisk: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/actions/addMember", {
                params: { path: { cluster: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // The teardown — the cluster's ONLY exit, carrying the full danger
        // ladder: SUDO-PROTECTED (call auth.sudo first, the grant rides
        // automatically) AND confirm-typed (`confirm` must equal the
        // cluster's CURRENT name — the caller types it, the facade never
        // fills it in) AND the MANDATORY explicit `wipeDevices` decision
        // with NO default. Requires no volumes/shares/stores (409 in_use
        // naming them). Resolves with the ceph.teardown task ref (202).
        destroy: async (id: string, body: { confirm: string; wipeDevices: boolean }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/actions/destroy", {
                params: { path: { cluster: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // MSGR2-MIGRAZIONE (2026-08-25): migrate an EXISTING cluster (born
        // msgr1/crc, pre-Fase A) to msgr2 secure mode. SUDO (call auth.sudo
        // first) AND `confirm` equal to the cluster's CURRENT name — the
        // migration rolling-restarts every daemon of a serving cluster.
        // Idempotent: an already-secure cluster resolves with a task that
        // declares "no work"; a mid-failure resumes by calling this again.
        // Resolves with the ceph.migrate.secure parent task ref (202).
        migrateSecure: async (id: string, body: { confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/actions/migrateSecure", {
                params: { path: { cluster: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // ALWAYS 422 pointing at `destroy` (the danger ladder a body-less
        // DELETE cannot carry) — declared here so the refusal is reachable
        // through the facade, never papered over.
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/cephClusters/{cluster}", { params: { path: { cluster: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // ---- R4 ESPLORATORE (2026-08-26, remodel-disegno §2.5): read-only
        // sul canale query lease-free — il server accoda `ceph.query` sul
        // nodo di controllo e risponde 200 sincrono; 503 parlante (task
        // nominato) quando il worker non risponde nella scadenza. ----

        health: async (id: string): Promise<z.infer<typeof zCephHealthReport>> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}/health", { params: { path: { cluster: id } } }), zCephHealthReport),

        pools: async (id: string): Promise<z.infer<typeof zCephPoolExplorerList>> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}/pools", { params: { path: { cluster: id } } }), zCephPoolExplorerList),

        pool: async (id: string, pool: string): Promise<z.infer<typeof zCephPoolExplorerEntry>> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}/pools/{pool}", { params: { path: { cluster: id, pool } } }), zCephPoolExplorerEntry),

        poolObjects: async (id: string, pool: string, p: { prefix?: string; cursor?: string } = {}): Promise<z.infer<typeof zCephPoolObjectsPage>> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}/pools/{pool}/objects", {
                params: { path: { cluster: id, pool }, query: p as never },
            }), zCephPoolObjectsPage),

        crushMap: async (id: string): Promise<z.infer<typeof zCephCrushMap>> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}/crushMap", { params: { path: { cluster: id } } }), zCephCrushMap),

        osds: async (id: string): Promise<z.infer<typeof zCephOsdExplorerList>> =>
            unwrap(await transport.GET("/api/v1/cephClusters/{cluster}/osds", { params: { path: { cluster: id } } }), zCephOsdExplorerList),

        // ---- R4 GESTIONE: sudo + cephcluster.update (chiama auth.sudo
        // prima) — 202 col task mon-affine. `out` NON e' una rimozione dalla
        // membership (il claim vdisk resta 1:1). ----

        setOsdFlags: async (id: string, body: { flag: "noout" | "norebalance" | "nobackfill" | "norecover" | "pause"; value: boolean }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/actions/setOsdFlags", {
                params: { path: { cluster: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        osdOut: async (id: string, osdId: number, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/osds/{osdId}/actions/out", {
                params: { path: { cluster: id, osdId } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        osdIn: async (id: string, osdId: number, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/osds/{osdId}/actions/in", {
                params: { path: { cluster: id, osdId } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        deepScrub: async (id: string, pool: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/cephClusters/{cluster}/pools/{pool}/actions/deepScrub", {
                params: { path: { cluster: id, pool } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },
    };



    // MultiSCSI clusters (REMODEL R1 #214 — the multiscsi half of the old
    // datapool). CREATE births the fabric (`multiscsicluster.init`: per-node
    // nvmet export -> attach everywhere -> VG+thin pool per vdisk) — 201 with
    // the resource in phase `initializing` plus the task ref. DELETE is the
    // teardown (202 + `multiscsicluster.teardown`, refused while
    // volumes/shares/stores live). addMember re-runs the SAME init family
    // over the grown membership (#209 — external vdisks join exactly this
    // way). The SCRUB-MS policy rides PATCH; the on-demand pass is `scrub`.
    const multiscsiClusters = {
        list: async (p: ListParams<FilterableFields["listMultiscsiClusters"]> = {}): Promise<Page<MultiscsiCluster>> =>
            page(await transport.GET("/api/v1/multiscsiClusters", { params: { query: listQuery(p) as never } }), zMultiscsiCluster),

        listAll: (p: Omit<ListParams<FilterableFields["listMultiscsiClusters"]>, "limit" | "cursor"> = {}): PageIterator<MultiscsiCluster> =>
            genPageIterator(async ({ cursor }) => multiscsiClusters.list({ ...p, cursor })),

        get: async (id: string): Promise<MultiscsiCluster> =>
            unwrap(await transport.GET("/api/v1/multiscsiClusters/{cluster}", { params: { path: { cluster: id } } }), zMultiscsiCluster),

        create: async (body: Record<string, unknown>, opts: IdempotencyOptions = {}): Promise<{ cluster: MultiscsiCluster; task: string | null }> => {
            const result = await transport.POST("/api/v1/multiscsiClusters", {
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            const cluster = unwrap(result, zMultiscsiCluster);
            return { cluster, task: result.response.headers.get("x-tvstor-task") };
        },

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<MultiscsiCluster> =>
            unwrap(await transport.PATCH("/api/v1/multiscsiClusters/{cluster}", {
                params: { path: { cluster: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zMultiscsiCluster),

        // #209: appends the pair and re-enqueues the ensure-idempotent init
        // family over the GROWN membership — the re-run converges the new
        // member and no-ops the rest. Resolves with the parent task ref.
        addMember: async (id: string, body: { node: string; vdisk: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/multiscsiClusters/{cluster}/actions/addMember", {
                params: { path: { cluster: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // SCRUB-MS (#94, ratifica Luca 2026-08-09 §9.1/§9.2): the on-demand
        // consistency pass over the cluster's md arrays. Resolves with the
        // `multiscsicluster.scrub` parent task ref (202); one node-affine
        // child per member node.
        // `mode: "check"` counts and touches nothing. `mode: "repair"` lets
        // md rewrite one copy over another and is an OPERATOR ACT: it needs
        // SUDO (call auth.sudo first, the grant rides automatically) AND
        // `confirm` equal to the cluster's CURRENT name — the caller types
        // it, the facade never fills it in. On a mirror md cannot know which
        // copy is sane; read `scrubStatus.arrays[].repairSemantics` on the
        // render before deciding.
        // `409 conflict` while a pass on this cluster is already running.
        scrub: async (id: string, body: { mode: "check" | "repair"; confirm?: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/multiscsiClusters/{cluster}/actions/scrub", {
                params: { path: { cluster: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // Resolves with the multiscsicluster.teardown task ref (202); 409
        // in_use naming the occupants while volumes/shares/stores live.
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/multiscsiClusters/{cluster}", { params: { path: { cluster: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },
    };



    // Volumes (M2a, 2026-08-02; reshaped by M2d, dettatura 2026-08-03): always
    // thin, carved from a DATAPOOL bound to `multiscsi` — the create body takes
    // `{datapool, mode: "mirror", replicas: {copies, minInSync}, sizeGiB}` (the
    // API accepts ONLY the structured replicas object; the `X/Y` string is CLI
    // grammar). Create answers 201 + the authority-node-affine `volume.create`
    // task; delete answers 202 + the `volume.delete` task. M2b (PROPOSAL §6.4,
    // 2026-08-02) adds the snapshot subresource, the clone variant of create
    // (`source: { snapshot }` — sizeGiB/mode/replicas forbidden, datapool
    // inherited; the task becomes `volume.clone`) and the confirm-typed revert
    // action. Since M2d the readOnly consumer summary is `targets[]` (replaces
    // M2c's `mappedTo[]`), and delete/revert of an EXPORTED volume answer 409
    // in_use naming the targets — surfaced as TVStorApiError, no facade change.
    // M2e (ratifiche Luca 2026-08-03, docs/multiscsi-two-node-failure-notes.md):
    // the readOnly render grows the authority-lease block (`authority` — holder
    // node, leaseExpiresAt, epoch, phase held|stale|failingOver), the per-leg
    // fence marks (`legs[].fenced`/`fencedAt` — fence-before-degrade) and
    // `failoverHealth` — pure render PASSTHROUGH, riding get/list through
    // zVolume untouched — plus two actions: `failover` (the manual takeover,
    // sudo) and `forcePromote` (declared data-loss, sudo AND confirm-typed).
    const volumes = {
        list: async (p: ListParams<FilterableFields["listVolumes"]> = {}): Promise<Page<Volume>> =>
            page(await transport.GET("/api/v1/volumes", { params: { query: listQuery(p) as never } }), zVolume),

        listAll: (p: Omit<ListParams<FilterableFields["listVolumes"]>, "limit" | "cursor"> = {}): PageIterator<Volume> =>
            genPageIterator(async ({ cursor }) => volumes.list({ ...p, cursor })),

        get: async (id: string): Promise<Volume> =>
            unwrap(await transport.GET("/api/v1/volumes/{volume}", { params: { path: { volume: id } } }), zVolume),

        // REMODEL R2 (#214): the PLAIN create is a synchronous 201 with NO
        // task — the volume is born `declared` (a row and a promise; the
        // bytes rise at the first consumer) and `task` resolves null. The
        // CLONE variant (`source`) is born materializing and carries the
        // clone task ref as before.
        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<WriteResult<Volume>> =>
            writeResult(await transport.POST("/api/v1/volumes", { params: { query: dryRunQuery(opts) }, body: body as never }), zVolume),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Volume> =>
            unwrap(await transport.PATCH("/api/v1/volumes/{volume}", {
                params: { path: { volume: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zVolume),

        // Resolves with the volume.delete task ref (202 — materialized
        // volume), or null on the synchronous 204 of a `declared` volume
        // (REMODEL R2: a row and a promise die with no task).
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/volumes/{volume}", { params: { path: { volume: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // The confirm-typed revert action (M2b, PROPOSAL §6.4 2026-08-02):
        // `confirm` must equal the volume's CURRENT name — the caller types it, the
        // facade never fills it in. Resolves with the volume.revert task ref.
        revert: async (id: string, body: { snapshot: string; confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/revert", {
                params: { path: { volume: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // MANUAL failover (M2e, 2026-08-03) — the operator-triggered entry into
        // the same volume.failover machinery the automatic stale-lease detection
        // drives: fence-or-grant (witness-first by default) → epoch bump →
        // assemble on the new authority from NON-FENCED legs → ALUA flip →
        // export re-point. Sudo-protected: call auth.sudo first. `targetNode`
        // optional — omitted, placement picks a survivor holding a non-fenced
        // leg. Resolves with the volume.failover task ref (202). No proof
        // obtainable (witness unreachable AND no verified fence) or every
        // surviving leg fenced is 412 precondition_failed — surfaced as
        // TVStorApiError.
        failover: async (id: string, body: { targetNode?: string } = {}, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/failover", {
                params: { path: { volume: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // The operator's DECLARED DATA-LOSS decision (M2e, ratifica
        // fence-before-degrade 2026-08-03): promote a STALE-FENCED leg — writes
        // acked after its fence vanish forever, coherently. Sudo-protected AND
        // confirm-typed: `confirm` must equal the volume's CURRENT name — the
        // caller types it, the facade never fills it in. Still requires the
        // peer-stopped proof (force never waives the split-brain floor, only
        // the data-loss rule). Resolves with the forced volume.failover task
        // ref (202).
        forcePromote: async (id: string, body: { leg: number; confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/forcePromote", {
                params: { path: { volume: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // THE DR FAILOVER VERB, emergency half (#38 REP-PROMOZIONE,
        // 2026-08-22): promote a READ-ONLY DR replica to a servable primary
        // volume — the last COMMITTED chain point is promoted (never a
        // mid-apply head), the `replica` marker clears, the destination
        // remembers the pair (the returning source meets 409
        // replica_promoted). Sudo AND confirm-typed: `confirm` = the
        // volume's CURRENT name, `acknowledgeStamp` = the CURRENT
        // `replica.lastReceivedStamp` the caller has READ — the consumed RPO
        // is declared, never implicit (a moved stamp is 409: re-read,
        // re-acknowledge); the facade fills neither in. Optional `name` =
        // the promoted identity. Resolves with the replication.promote task
        // ref (202).
        promote: async (id: string, body: { confirm: string; acknowledgeStamp: string; name?: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/promote", {
                params: { path: { volume: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // THE SYNC DEGRADE OPT-IN (#39 REP-SYNC-MS, 2026-08-22): with the
        // remote site down a sync volume is BLOCKED by default (certainty
        // over availability, Volume.sync) — this verb trades the RPO=0
        // promise for availability OUT LOUD: effective minInSync drops to 1,
        // the phase reads `degraded` (never a silent `ready`), the
        // volume.syncDegraded metric fires the factory rule. ONE-SHOT: the
        // completed resync consumes the opt-in, the next outage blocks
        // again. Sudo AND confirm-typed (`confirm` = the volume's CURRENT
        // name — the caller types it, the facade never fills it in). A pure
        // config write: resolves with the updated Volume (200, no task).
        syncDegrade: async (id: string, body: { confirm: string }): Promise<Volume> =>
            unwrap(await transport.POST("/api/v1/volumes/{volume}/actions/syncDegrade", {
                params: { path: { volume: id } },
                body: body as never,
            }), zVolume),

        // THE SYNC RE-PROTECTION (#209 REP-S2S5, 2026-08-24 — actionSyncAttach,
        // PROPOSAL attuata): declare an EXISTING single-leg volume (the
        // adopted shape) synchronous by growing a remote leg on the
        // datapool's EXTERNAL member vdisk — replicas rise to {2, 2}
        // (blocked-default), the degrade opt-in is planted TRUE (declared
        // degraded service through the seed window, consumed at completed
        // resync), and the new leg's full rebuild IS the seed. Sudo AND
        // confirm-typed (`confirm` = the volume's CURRENT name — typed by
        // the caller, never filled in). Resolves with the volume.syncAttach
        // task ref (202).
        syncAttach: async (id: string, body: { confirm: string; remoteCluster: string; remoteVdisk: string; witnessEndpoints?: string[] }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/syncAttach", {
                params: { path: { volume: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // ONLINE EXPANSION (M5d, 2026-08-04): grow-only — the new size must
        // be strictly greater than the current one (422 otherwise, speaking
        // message). Resolves with the volume.expand / ceph.volume.expand
        // task ref (202). TVStor grows the LUN: iSCSI initiators must rescan
        // to see the new capacity, and the consumer grows its own
        // partition/filesystem (contract actionExpand — documented, never
        // hidden).
        // REMODEL R2: on a `declared` volume the expand is a synchronous
        // 200 — the promise grows, re-admitted by the anti-overcommit
        // formula — and `volume` carries the updated render with `task`
        // null; materialized volumes answer 202 + the task as before.
        // ENEA HP-SAN-1 (R19): export the volume to an I/O server pair (sudo,
        // 202 `ceph.pair.apply` re-renders the pair's caps) / take it back
        // (409 while a member holds the exclusive lock: unmount or fence first).
        attachToIoServerPair: async (id: string, body: { pair: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/attachToIoServerPair", { params: { path: { volume: id } }, body: body as never, headers: idempotencyHeaders(opts) });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        detachFromIoServerPair: async (id: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/detachFromIoServerPair", { params: { path: { volume: id } }, headers: idempotencyHeaders(opts) });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        expand: async (id: string, body: { sizeGiB: number }, opts: IdempotencyOptions = {}): Promise<{ volume: Volume | null; task: string | null }> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/expand", {
                params: { path: { volume: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            if (result.response.status === 200) {
                const volume = unwrap(result, zVolume);
                return { volume, task: null };
            }
            unwrap(result);
            return { volume: null, task: result.response.headers.get("x-tvstor-task") };
        },

        // NAS-MDS-1 via B (2026-10-29): the EXPLICIT move of a volume that
        // rides a dedicated CephFS onto the cluster filesystem — 202 + task;
        // the body names the shares put OFFLINE (system lockdown until the
        // flip) and the estimated bytes to copy. The refusals (422/409) come
        // as ApiProblem.
        migrateToSharedFilesystem: async (id: string, opts: IdempotencyOptions = {}): Promise<{ task: string; shares: string[]; estimatedBytes: number }> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/migrateToSharedFilesystem", {
                params: { path: { volume: id } },
                headers: idempotencyHeaders(opts),
            });
            const body = unwrap(result, zMigrateToSharedFilesystemAccepted);
            return { task: result.response.headers.get("x-tvstor-task") ?? body.task, shares: body.shares, estimatedBytes: body.estimatedBytes };
        },

        // #34 SHRINK (ratifica 2026-08-28): il verso mancante del resize —
        // SUDO (auth.sudo prima) + CONFIRM-TYPED (il nome del volume nel
        // body, mai riempito dalla facade). Sincrono 200 (declared, o
        // filesystem ceph sotto la guardia d'aggregato) = `volume` col
        // render nuovo e `task` null; 202 (ext4 offline / quota object) =
        // `task`. I 422 parlanti (lun = la ratifica; xfs = la via; pavimenti
        // d'usato coi numeri) arrivano come ApiProblem.
        shrink: async (id: string, body: { sizeGiB: number; confirm: string }, opts: IdempotencyOptions = {}): Promise<{ volume: Volume | null; task: string | null }> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/shrink", {
                params: { path: { volume: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            if (result.response.status === 200) {
                const volume = unwrap(result, zVolume);
                return { volume, task: null };
            }
            unwrap(result);
            return { volume: null, task: result.response.headers.get("x-tvstor-task") };
        },

        // PER-VOLUME QOS (M5d, 2026-08-04; BOTH ENGINES since QoS-2 #36,
        // 2026-08-26): pure config through PATCH — {iopsLimit, mbpsLimit},
        // null (or both axes null) = unlimited. The PATCH births the
        // engine's enforcement task (ceph.volume.qos.apply mon-affine /
        // volume.qos.apply authority-affine), surfaced here beside the
        // updated volume so a caller can `tasks.wait` it; null task on a
        // no-op change. Enforcement honesty lives on the contract's
        // Volume.qos (librbd token bucket for native ceph consumers —
        // krbd/iSCSI-bridge unshaped; qemu-nbd token bucket at the gateway
        // on multiscsi — value changes live, enable/disable briefly
        // re-establishes the LUN). Wire shape pinned by
        // test/volume-set-qos.test.ts (the facade-verb rule, 2026-08-19).
        setQos: async (
            id: string,
            qos: { iopsLimit?: number | null; mbpsLimit?: number | null } | null,
        ): Promise<{ volume: Volume; task: string | null }> => {
            const result = await transport.PATCH("/api/v1/volumes/{volume}", {
                params: { path: { volume: id } },
                body: { qos } as never,
                headers: { "content-type": "application/merge-patch+json" },
            });
            const volume = unwrap(result, zVolume);
            return { volume, task: result.response.headers.get("x-tvstor-task") };
        },

        // CHANGED BLOCK TRACKING — the SnapDiff equivalent (M4a, 2026-08-04,
        // docs/cbt-snapdiff-design-2026-08-04.md). ONE action, ONE answer, TWO
        // engines: `rbd diff` behind ceph, `thin_delta` on the authority's leg
        // behind multiscsi — and NOTHING about that reaches this facade, which
        // is the point. `from` is a snapshot id, `to` a snapshot id or the
        // literal `head` (the live volume — the "everything since the last
        // backup" case). Read-class permission `volume.diff`, no sudo.
        // LEASE-FREE QUERY CHANNEL: the action answers 202 and this method
        // resolves with the task ref — a diff never takes the per-datapool
        // metadata lease (it is a READ), so no 409 exists on it and a long diff
        // never freezes the datapool's control plane. Feed the ref to
        // `tasks.wait`/`tasks.follow`, or call `diffAndWait` below, which does
        // both and decodes the two channels for you.
        diff: async (id: string, body: VolumeDiffInput, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/volumes/{volume}/actions/diff", {
                params: { path: { volume: id } },
                body: { from: body.from, to: body.to } as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // The one-call form: POST, wait for the diff task, decode
        // `Task.result` into the normalized summary, and hand back an async
        // iterator over the FULL extent list read from the task log (NDJSON,
        // via `tasks.follow` — so it also works while the task is still
        // running once this resolves, and re-reads cheaply). Throws
        // TVStorTaskError if the diff task fails, exactly like every other
        // waited task.
        diffAndWait: async (
            id: string,
            body: VolumeDiffInput,
            opts: TaskWaitOptions = {},
        ): Promise<VolumeDiffAnswer> => {
            const ref = await volumes.diff(id, body);
            if (ref === null) {
                throw new Error("the diff action answered without an X-TVStor-Task reference");
            }
            const task = await tasks.wait(ref, opts);
            return {
                task: ref,
                summary: parseVolumeDiffResult(task["result"]),
                // The FULL list, checked against the authoritative count (fix
                // 2026-08-08): a log stream that ends short now throws instead
                // of handing a backup product a silently truncated delta.
                extents: (): AsyncGenerator<DiffExtent> =>
                    diffExtentsOfLog(() => tasks.follow(ref), parseVolumeDiffResult(task["result"]).extentCount),
            };
        },

        // The log channel alone, for a caller that already holds a diff task
        // ref (a resumed CLI session, a webui panel re-opening a finished
        // task): the FULL, authoritative extent list, streamed.
        diffExtents: async function* (taskRef: string): AsyncGenerator<DiffExtent> {
            // One read of the task row for the DECLARED total (fix
            // 2026-08-08): without it a log that ends early — a store that
            // died mid-stream, rows still behind the uploader's high-water
            // mark — reads as a complete, shorter delta. A result that does
            // not parse leaves the total unknown and the header line in the
            // log supplies it instead.
            const declared = await tasks.get(taskRef)
                .then((task) => parseVolumeDiffResult(task["result"]).extentCount)
                .catch((): null => null);
            yield* diffExtentsOfLog(() => tasks.follow(taskRef), declared);
        },

        // The snapshot subresource (M2b, PROPOSAL §6.4 2026-08-02): config-class
        // rows plus the observed census fields (`state`/`observedAt`) composed from
        // `volume_status`. No `?scope=` — visibility is the parent volume's.
        snapshots: {
            list: async (volumeId: string, p: Omit<ListParams<FilterableFields["listVolumeSnapshots"]>, "scope"> = {}): Promise<Page<VolumeSnapshot>> =>
                page(await transport.GET("/api/v1/volumes/{volume}/snapshots", {
                    params: { path: { volume: volumeId }, query: listQuery(p) as never },
                }), zVolumeSnapshot),

            listAll: (volumeId: string, p: Omit<ListParams<FilterableFields["listVolumeSnapshots"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<VolumeSnapshot> =>
                genPageIterator(async ({ cursor }) => volumes.snapshots.list(volumeId, { ...p, cursor })),

            get: async (volumeId: string, id: string): Promise<VolumeSnapshot> =>
                unwrap(await transport.GET("/api/v1/volumes/{volume}/snapshots/{snapshot}", {
                    params: { path: { volume: volumeId, snapshot: id } },
                }), zVolumeSnapshot),

            // 201 + the volume.snapshot.create task in X-TVStor-Task.
            // ENEA-A-R5 (2026-10-23): `retention` is FROZEN at birth (`none`
            // when omitted) — while it runs the delete and the volume's
            // destruction answer 409 retention_active.
            create: async (volumeId: string, body: { name: string; retention?: SnapshotRetention }, opts: DryRunOptions = {}): Promise<WriteResult<VolumeSnapshot>> =>
                writeResult(await transport.POST("/api/v1/volumes/{volume}/snapshots", {
                    params: { path: { volume: volumeId }, query: dryRunQuery(opts) },
                    // `id` is response-required per contract (#195) and the
                    // generated request type cannot say so — the create idiom.
                    body: body as never,
                }), zVolumeSnapshot),

            // ENEA-A-R5: the ONE writable field — retention, EXTEND only
            // (shortening or removing a running retention is 422
            // retention_frozen, sudo included).
            patch: async (volumeId: string, id: string, body: { retention: SnapshotRetention }, opts: DryRunOptions = {}): Promise<VolumeSnapshot> =>
                unwrap(await transport.PATCH("/api/v1/volumes/{volume}/snapshots/{snapshot}", {
                    params: { path: { volume: volumeId, snapshot: id }, query: dryRunQuery(opts) },
                    body: body as never,
                }), zVolumeSnapshot),

            // Resolves with the forward-only volume.snapshot.delete task ref (202).
            delete: async (volumeId: string, id: string): Promise<string | null> => {
                const result = await transport.DELETE("/api/v1/volumes/{volume}/snapshots/{snapshot}", {
                    params: { path: { volume: volumeId, snapshot: id } },
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },
        },
    };



    // Hosts (M2c, 2026-08-02 — SURVIVES M2d as-is): initiator groups — the
    // consumer identity of the SAN (architecture §5.1). SYNCHRONOUS config CRUD,
    // no task: an unreferenced host touches no target, and an `iqns` change
    // converges onto the target ACLs via the worker reconcile pass
    // (apply-on-drift) — watch the targets' `paths[]`. Delete answers 409 in_use
    // while referenced by a target (explicit target edit first, never a cascade).
    // The M2c "CHAP joins with the keystore round" fence is CLOSED by M2g
    // (2026-08-03): CHAP lives on the TARGET (`chap.credential`), not here —
    // hosts stay pure IQN groups.
    const hosts = {
        list: async (p: ListParams<FilterableFields["listHosts"]> = {}): Promise<Page<Host>> =>
            page(await transport.GET("/api/v1/hosts", { params: { query: listQuery(p) as never } }), zHost),

        listAll: (p: Omit<ListParams<FilterableFields["listHosts"]>, "limit" | "cursor"> = {}): PageIterator<Host> =>
            genPageIterator(async ({ cursor }) => hosts.list({ ...p, cursor })),

        get: async (id: string): Promise<Host> =>
            unwrap(await transport.GET("/api/v1/hosts/{host}", { params: { path: { host: id } } }), zHost),

        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Host> =>
            unwrap(await transport.POST("/api/v1/hosts", { params: { query: dryRunQuery(opts) }, body: body as never }), zHost),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Host> =>
            unwrap(await transport.PATCH("/api/v1/hosts/{host}", {
                params: { path: { host: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zHost),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/hosts/{host}", { params: { path: { host: id } } }));
        },
    };



    // ENEA HP-SAN-1 (R19, 2026-10-24): the I/O SERVER PAIR — a Lustre OSS/MDS
    // pair (2..4 members, names + fabric addresses) as a NATIVE rbd consumer:
    // ONE cephx identity per pair, confined to the pools of ITS volumes (never
    // `*`), minted by the `ceph.pair.apply` task on the cluster's control node
    // (create answers 202). `credential` is a SENSITIVE READ (sudo) that hands
    // keyring + monitors + minimal ceph.conf once; `rotateCredential` revokes
    // the old key on the cluster (the members must receive the new one).
    // `fence`/`unfence` = the SAN-side fence of 6.5.2-7 (blocklist of every
    // member address + release of its rbd locks): THE CALLER WAITS FOR THE
    // TASK'S OUTCOME, never the 202 — a `failed` fence did not happen.
    // JBOF-DC-1 (R25, 2026-10-25, docs/jbof-dc-1-disegno-2026-10-25.md §2, §5): the
    // enclosure — N heads, one owner per shared disk; failover (fence confirmed,
    // the dead head's disks move to a live head, 202 enclosure.import) and
    // failback (both alive, 202 parent release→import).
    const enclosures = {
        list: async (p: ListParams<FilterableFields["listEnclosures"]> = {}): Promise<Page<Enclosure>> =>
            page(await transport.GET("/api/v1/enclosures", { params: { query: listQuery(p) as never } }), zEnclosure),

        listAll: (p: Omit<ListParams<FilterableFields["listEnclosures"]>, "limit" | "cursor"> = {}): PageIterator<Enclosure> =>
            genPageIterator(async ({ cursor }) => enclosures.list({ ...p, cursor })),

        get: async (id: string): Promise<Enclosure> =>
            unwrap(await transport.GET("/api/v1/enclosures/{enclosure}", { params: { path: { enclosure: id } } }), zEnclosure),

        create: async (body: { name: string; heads: string[]; disks: string[]; owner?: string; reservePercent?: number; notes?: string; scope?: string }, opts: DryRunOptions = {}): Promise<Enclosure> =>
            unwrap(await transport.POST("/api/v1/enclosures", { params: { query: dryRunQuery(opts) }, body: body as never }), zEnclosure),

        delete: async (id: string, opts: DryRunOptions = {}): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/enclosures/{enclosure}", { params: { path: { enclosure: id }, query: dryRunQuery(opts) } }));
        },

        failover: async (id: string, body: { from: string; to: string; fence: { confirmed: boolean; method: "power-off" | "nvme-reservation" | "pcie-port" | "bmc" | "declared"; note?: string } }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/enclosures/{enclosure}/actions/failover", {
                params: { path: { enclosure: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        failback: async (id: string, body: { from: string; to: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/enclosures/{enclosure}/actions/failback", {
                params: { path: { enclosure: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },
    };

    const ioServerPairs = {
        list: async (p: ListParams<FilterableFields["listIoServerPairs"]> = {}): Promise<Page<IoServerPair>> =>
            page(await transport.GET("/api/v1/ioServerPairs", { params: { query: listQuery(p) as never } }), zIoServerPair),

        listAll: (p: Omit<ListParams<FilterableFields["listIoServerPairs"]>, "limit" | "cursor"> = {}): PageIterator<IoServerPair> =>
            genPageIterator(async ({ cursor }) => ioServerPairs.list({ ...p, cursor })),

        get: async (id: string): Promise<IoServerPair> =>
            unwrap(await transport.GET("/api/v1/ioServerPairs/{pair}", { params: { path: { pair: id } } }), zIoServerPair),

        // 202: the pair row (pending) plus the `ceph.pair.apply` task ref.
        create: async (body: Record<string, unknown>, opts: DryRunOptions & IdempotencyOptions = {}): Promise<{ pair: IoServerPair; task: string | null }> => {
            const result = await transport.POST("/api/v1/ioServerPairs", { params: { query: dryRunQuery(opts) }, body: body as never, headers: idempotencyHeaders(opts) });
            const pair = unwrap(result, zIoServerPair);
            return { pair, task: result.response.headers.get("x-tvstor-task") };
        },

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<IoServerPair> =>
            unwrap(await transport.PATCH("/api/v1/ioServerPairs/{pair}", {
                params: { path: { pair: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zIoServerPair),

        // 202: the entity is removed on the cluster by the task; 409 while volumes are attached.
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/ioServerPairs/{pair}", { params: { path: { pair: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // SENSITIVE READ (sudo: call auth.sudo first): keyring + monitors + ceph.conf.
        credential: async (id: string): Promise<IoServerPairCredential> =>
            unwrap(await transport.GET("/api/v1/ioServerPairs/{pair}/credential", { params: { path: { pair: id } } }), zIoServerPairCredential),

        rotateCredential: async (id: string, body: { confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/ioServerPairs/{pair}/actions/rotateCredential", { params: { path: { pair: id } }, body: body as never, headers: idempotencyHeaders(opts) });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        fence: async (id: string, body: { member: string; confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/ioServerPairs/{pair}/actions/fence", { params: { path: { pair: id } }, body: body as never, headers: idempotencyHeaders(opts) });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        unfence: async (id: string, body: { member: string; confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/ioServerPairs/{pair}/actions/unfence", { params: { path: { pair: id } }, body: body as never, headers: idempotencyHeaders(opts) });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },
    };



    // ENEA HP-SAN-3 (R21, 2026-10-25, catalogsources.yaml,
    // docs/enea-hp-r21-disegno-2026-10-25.md §1 and §3): the CATALOG SOURCE —
    // a filesystem ALREADY MOUNTED on one node (a Lustre client, an NFS export
    // mounted by hand, any POSIX fs) declared as a source of that node's
    // catalog: the worker of `node` walks it in FULL mode by mtime and loads
    // the entries into the SAME catalog database it uses for its shares
    // (`catalog_entries.source = 'mount'`). A pure record of intent (no phase,
    // nothing created on the node): create is SYNCHRONOUS (201), delete is
    // 204. Admission is the node's mount census (`cluster.nodeMounts`), never
    // the operator's word: `mountPath` must EQUAL a census target (422
    // catalog_source_not_mounted) and lie outside the product's trees (422
    // catalog_source_path_reserved); `node` and `mountPath` are IMMUTABLE
    // (patch them = 422 immutable_field — delete and recreate). The `catalog`
    // verbs are the R10 verbs of a share, same query, same answers, same
    // problem codes, forwarded to the owner node; the status adds `mount
    // {present, …}` from the node's CURRENT census and `nodeState`.
    const catalogSources = {
        // `node` filters by the owner node's NAME (the collection's own query
        // parameter, beside the common filter/sort/scope/limit/cursor).
        list: async (p: ListParams<FilterableFields["listCatalogSources"]> & { node?: string } = {}): Promise<Page<CatalogSource>> => {
            const { node, ...rest } = p;
            const query = { ...listQuery(rest), ...(node !== undefined ? { node } : {}) };
            return page(await transport.GET("/api/v1/catalogSources", { params: { query: query as never } }), zCatalogSource);
        },

        listAll: (p: Omit<ListParams<FilterableFields["listCatalogSources"]>, "limit" | "cursor"> & { node?: string } = {}): PageIterator<CatalogSource> =>
            genPageIterator(async ({ cursor }) => catalogSources.list({ ...p, cursor })),

        get: async (id: string): Promise<CatalogSource> =>
            unwrap(await transport.GET("/api/v1/catalogSources/{source}", { params: { path: { source: id } } }), zCatalogSource),

        // 201, synchronous: the admitted source (fstype/fsSource photographed
        // from the census). `?dryRun=true` runs the whole admission without
        // persisting — the create dialog's validate step.
        create: async (body: Record<string, unknown>, opts: DryRunOptions & IdempotencyOptions = {}): Promise<CatalogSource> =>
            unwrap(await transport.POST("/api/v1/catalogSources", { params: { query: dryRunQuery(opts) }, body: body as never, headers: idempotencyHeaders(opts) }), zCatalogSource),

        // Merge-patch of name/subPath/excludes/catalog/notes — re-admitted
        // against the node's current census.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<CatalogSource> =>
            unwrap(await transport.PATCH("/api/v1/catalogSources/{source}", {
                params: { path: { source: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zCatalogSource),

        // 204: the row is gone at once; a running scan receives
        // cancel_requested and the owner's next tick drops the catalogued rows.
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/catalogSources/{source}", { params: { path: { source: id } } }));
        },

        // ---- the catalog verbs of a source: the same forms as
        // fileShares.catalog.* (fileshares.yaml catalog*), answered by the
        // source's node; fileshare.catalog.* grains reused.
        catalog: {
            // The same CatalogStatus as a share plus `mount {present, fstype,
            // source, options, seenAt}` and `nodeState {inService, maintenance}`.
            status: async (sourceId: string): Promise<CatalogStatus> =>
                unwrap(await transport.GET("/api/v1/catalogSources/{source}/catalog/status", {
                    params: { path: { source: sourceId } },
                }), zCatalogStatus),

            list: async (sourceId: string, q: CatalogListQuery = {}): Promise<Page<CatalogEntry>> =>
                page(await transport.GET("/api/v1/catalogSources/{source}/catalog", {
                    params: { path: { source: sourceId }, query: catalogQueryOf(q) as never },
                }), zCatalogEntry),

            listAll: (sourceId: string, q: Omit<CatalogListQuery, "cursor"> = {}): PageIterator<CatalogEntry> =>
                genPageIterator(async ({ cursor }) => catalogSources.catalog.list(sourceId, { ...q, ...(cursor !== undefined && cursor !== null ? { cursor } : {}) })),

            summary: async (sourceId: string): Promise<CatalogSummary> =>
                unwrap(await transport.GET("/api/v1/catalogSources/{source}/catalog/summary", {
                    params: { path: { source: sourceId } },
                }), zCatalogSummary),

            // Always FULL on a source (`mode: incremental` is 422
            // catalog_mode_not_supported); 409 catalog_scan_running while one
            // is active.
            scan: async (sourceId: string, body: { mode?: "full" | "incremental" } = {}, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/catalogSources/{source}/catalog/actions/scan", {
                    params: { path: { source: sourceId } },
                    body: body as never,
                    headers: idempotencyHeaders(opts),
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // THE SAME credential as the node's shares (one role per
            // database): sudo.
            credentials: async (sourceId: string): Promise<CatalogCredentials> =>
                unwrap(await transport.GET("/api/v1/catalogSources/{source}/catalog/credentials", {
                    params: { path: { source: sourceId } },
                }), zCatalogCredentials),

            export: async (sourceId: string, format: "csv" | "jsonl", q: Omit<CatalogListQuery, "cursor" | "limit"> = {}): Promise<FsDownload> => {
                const query = new URLSearchParams({ format });
                for (const [key, value] of Object.entries(catalogQueryOf(q))) {
                    query.set(key, String(value));
                }
                const response = await fetchImpl(`${options.baseUrl}/api/v1/catalogSources/${sourceId}/catalog/export?${query.toString()}`, { headers: authHeaders() });
                if (!response.ok || response.body === null) {
                    throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
                }
                const length = response.headers.get("content-length");
                return {
                    stream: response.body,
                    contentType: response.headers.get("content-type"),
                    contentLength: length !== null ? Number(length) : null,
                    contentDisposition: response.headers.get("content-disposition"),
                };
            },

            exportUrl: (sourceId: string, format: "csv" | "jsonl", q: Omit<CatalogListQuery, "cursor" | "limit"> = {}): string => {
                const query = new URLSearchParams({ format });
                for (const [key, value] of Object.entries(catalogQueryOf(q))) {
                    query.set(key, String(value));
                }
                return `${options.baseUrl}/api/v1/catalogSources/${sourceId}/catalog/export?${query.toString()}`;
            },
        },
    };



    // Credentials — the TYPED KEYSTORE (M2g, 2026-08-03; TVirt Annex E adapted,
    // credentials.yaml): named secrets that Targets (`chap.credential`) and the
    // fencing cascade (`fencingDevices[].credential`) REFERENCE by id instead of
    // carrying inline. `kind` is immutable; the per-kind SECRET fields are
    // WRITE-ONLY end to end — the render carries public fields plus the `usedBy`
    // referrer census only, so `get`/`list` answer through zCredential (which has
    // no secret fields at all) and the inputs are the hand-typed
    // CredentialCreateInput/CredentialRotateInput above. DANGER LADDER: create
    // and rotate CARRY SECRET MATERIAL and are sudo-protected — call auth.sudo
    // first, the X-TVStor-Sudo grant rides automatically. PATCH REFUSES secret
    // fields (422 — TVStor deviation from TVirt's PATCH-rotation, dated
    // 2026-08-03): rotate is THE ONLY post-creation write path for secrets.
    // Delete answers 409 in_use while referenced — the problem detail names the
    // referrers (surfaced as TVStorApiError, no facade special-casing).
    const credentials = {
        list: async (p: ListParams<FilterableFields["listCredentials"]> = {}): Promise<Page<Credential>> =>
            page(await transport.GET("/api/v1/credentials", { params: { query: listQuery(p) as never } }), zCredential),

        listAll: (p: Omit<ListParams<FilterableFields["listCredentials"]>, "limit" | "cursor"> = {}): PageIterator<Credential> =>
            genPageIterator(async ({ cursor }) => credentials.list({ ...p, cursor })),

        get: async (id: string): Promise<Credential> =>
            unwrap(await transport.GET("/api/v1/credentials/{credential}", { params: { path: { credential: id } } }), zCredential),

        // Pure config write (201, no task): the secret is envelope-encrypted and
        // the row replicated synchronously. Sudo-protected (secret material).
        create: async (body: CredentialCreateInput, opts: DryRunOptions = {}): Promise<Credential> =>
            unwrap(await transport.POST("/api/v1/credentials", { params: { query: dryRunQuery(opts) }, body: body as never }), zCredential),

        // Public fields only (`name`, the per-kind username/entity, `notes`) —
        // a secret field here is 422, `kind` is 422 immutable_field. Needs no
        // sudo: no secret material rides a PATCH.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Credential> =>
            unwrap(await transport.PATCH("/api/v1/credentials/{credential}", {
                params: { path: { credential: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zCredential),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/credentials/{credential}", { params: { path: { credential: id } } }));
        },

        // Rotate = replace the secret material (sudo-protected). `.task` carries
        // the consumer re-apply parent (one target.apply per referring target —
        // rotate re-applies on the ACLs) when the credential is referenced by
        // targets; null otherwise (a bmcAccount rotation needs no task — the
        // fencing shim resolves at use time).
        rotate: async (id: string, body: CredentialRotateInput, opts: DryRunOptions & IdempotencyOptions = {}): Promise<WriteResult<Credential>> =>
            writeResult(await transport.POST("/api/v1/credentials/{credential}/actions/rotate", {
                params: { path: { credential: id }, query: dryRunQuery(opts) },
                body: body as never,
                headers: idempotencyHeaders(opts),
            }), zCredential),

        // THE CASSAFORTE PATH (M4c, 2026-08-04): every luksKey passphrase
        // visible to the caller, IN CLEAR, in this response and nowhere else
        // — the ONE sanctioned exception to the never-rendered discipline.
        // SUDO-PROTECTED (call auth.sudo first) AND confirm-typed (`confirm`
        // must equal the CLUSTER's name — the caller types it, the facade
        // never fills it in). Print it, put it in the physical safe, close
        // the window: the platform keeps no copy.
        // VOL-CRYPT-CLUSTER (2026-09-04): every key names the storage
        // cluster it protects (`storageCluster`); `opts.storageCluster`
        // narrows the printout to ONE cluster's keys (unknown id → 422).
        exportRecovery: async (confirm: string, opts: { storageCluster?: string } = {}): Promise<RecoveryKeysExport> =>
            unwrap(await transport.POST("/api/v1/credentials/actions/exportRecovery", {
                body: { confirm, ...(opts.storageCluster !== undefined ? { storageCluster: opts.storageCluster } : {}) },
            }), zRecoveryKeysExport),

        // SEC-REVOKE #79 ("revoca HARD", ratifica 2026-08-08) — throw a
        // fileUser out of the web explorer: revokes every live files-realm
        // session of the credential on any node AND destroys the editor
        // (tvse_) tokens those sessions minted — an open Collabora frame
        // stops saving within seconds, unsaved work is lost. Kicking out is
        // not disabling: the account keeps existing and can log in again
        // (remove it from the shares' users blocks first when the intent is
        // that it stays out). Kinds other than fileUser answer a speaking
        // 422, never a silent zero. Permission `user.sessions.revoke`; no
        // sudo, no body; the counters are the answer (the T8 lesson).
        revokeSessions: async (id: string, opts: IdempotencyOptions = {}): Promise<CredentialSessionsRevocation> => {
            const body = unwrap<{ revokedSessions?: number; revokedEditorTokens?: number }>(
                await transport.POST("/api/v1/credentials/{credential}/actions/revokeSessions", {
                    params: { path: { credential: id } },
                    headers: idempotencyHeaders(opts),
                }),
            );
            return {
                revokedSessions: body.revokedSessions ?? null,
                revokedEditorTokens: body.revokedEditorTokens ?? null,
            };
        },
    };



    // Directory realms (NAS-2 Fase 1a, 2026-08-15, directoryrealms.yaml —
    // facade branch added by #181 ondata 1a): the cluster-level declaration of
    // the DIRECTORY the file services take identities from — `ad` (Kerberos,
    // winbind, the only branch SMB can use) or `ldap` (bind over MANDATORY
    // TLS, SSSD, SFTP/FTP/web). N per cluster (ratifica B3); the realm's
    // `name` is the qualifying half of every `REALM\user` login AND the key
    // of its uid range in the cluster allocator — hence immutable, as are
    // `type`, `uidRange` and `domainSid` (the last has its own gesture,
    // `refound`). ALL SYNCHRONOUS — no task ever rides these calls (declaring
    // a realm changes nothing on any node; the join hangs off
    // `FileShare.identity`, #159). EVERY MUTATION IS SUDO-GATED (create,
    // patch, delete, refound): call auth.sudo first, the X-TVStor-Sudo grant
    // rides automatically — the same danger ladder as credentials. The
    // readOnly composition (`uidRange.declared`, `allocatedIdentities`) comes
    // from the allocator's own row, which SURVIVES a delete (allocations are
    // never released — the files those uids own are still on the disk).
    const directoryRealms = {
        list: async (p: ListParams<FilterableFields["listDirectoryRealms"]> = {}): Promise<Page<DirectoryRealm>> =>
            page(await transport.GET("/api/v1/directoryRealms", { params: { query: listQuery(p) as never } }), zDirectoryRealm),

        listAll: (p: Omit<ListParams<FilterableFields["listDirectoryRealms"]>, "limit" | "cursor"> = {}): PageIterator<DirectoryRealm> =>
            genPageIterator(async ({ cursor }) => directoryRealms.list({ ...p, cursor })),

        get: async (id: string): Promise<DirectoryRealm> =>
            unwrap(await transport.GET("/api/v1/directoryRealms/{directoryRealm}", { params: { path: { directoryRealm: id } } }), zDirectoryRealm),

        // 201 synchronous — the uid range is engraved in the same transaction
        // (omitted = first free grid slot; supplied = the declared road). The
        // speaking refusals are the server's: 409 conflict (name taken),
        // 409 id_range_conflict (names the occupying realm),
        // 409 domain_identity_conflict (D4 — names both SIDs), 422 validation
        // (block/type mismatch, reserved name, ldap without servers, wrong
        // credential kind). Sudo-gated.
        create: async (body: DirectoryRealmCreateInput, opts: DryRunOptions = {}): Promise<DirectoryRealm> =>
            unwrap(await transport.POST("/api/v1/directoryRealms", { params: { query: dryRunQuery(opts) }, body: body as never }), zDirectoryRealm),

        // Writable: `credential`, `servers`, the per-type block's fields,
        // `offlineWindowSeconds`, `identityKeyAttribute` (only while
        // `allocatedIdentities` is zero — 422 immutable_field after), `tags`,
        // `notes`. Sudo-gated. A PATCH never revokes the realm's live web
        // sessions — it forces an early membership refresh instead (#178).
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<DirectoryRealm> =>
            unwrap(await transport.PATCH("/api/v1/directoryRealms/{directoryRealm}", {
                params: { path: { directoryRealm: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zDirectoryRealm),

        // 204 synchronous, sudo-gated. The uid range and the allocations STAY
        // (tombstone — recreating the name RESUMES both); a realm a share
        // depends on refuses with 409 in_use NAMING the shares; the realm's
        // live web sessions are revoked in the same transaction (R9 source 2).
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/directoryRealms/{directoryRealm}", { params: { path: { directoryRealm: id } } }));
        },

        // READ-ONLY and not sudo-gated: resolves DNS from the answering node
        // and reads the cluster store — it changes nothing anywhere. 200
        // ALWAYS when the realm exists: a blocked outcome is a report, not an
        // HTTP error. `observedFrom` bounds the answer to the node that gave
        // it (domain adhesion is per node, reperto R8).
        preflight: async (id: string, opts: IdempotencyOptions = {}): Promise<DirectoryRealmPreflight> =>
            unwrap(await transport.POST("/api/v1/directoryRealms/{directoryRealm}/actions/preflight", {
                params: { path: { directoryRealm: id } },
                headers: idempotencyHeaders(opts),
            }), zDirectoryRealmPreflight),

        // THE WAY OUT OF THE D4 REFUSAL — deliberately not convenient: sudo
        // plus THREE typed values none of which can be guessed (the realm's
        // own name as `confirm`, the NEW domain SID, and the exact current
        // `allocatedIdentities`). The facade fills none of them in.
        refound: async (id: string, body: { confirm: string; domainSid: string; acknowledgeAllocations: number }): Promise<DirectoryRealmRefoundResult> =>
            unwrap(await transport.POST("/api/v1/directoryRealms/{directoryRealm}/actions/refound", {
                params: { path: { directoryRealm: id } },
                body,
            }), zDirectoryRealmRefoundResult),

        // R9 source 3 (#178) — throws every user of this realm out of the web
        // explorer AND destroys their document-editor tokens (HARD: unsaved
        // work in an open editor is lost). The accounts keep existing IN THE
        // DIRECTORY: kicking out is not disabling. Established smbd sessions
        // remain smbd's own (declared perimeter). Permission
        // `user.sessions.revoke`; no sudo, no body; the counters are the
        // answer and the caller renders them (the T8 lesson).
        revokeSessions: async (id: string, opts: IdempotencyOptions = {}): Promise<RealmSessionsRevocation> => {
            const body = unwrap<{ revokedSessions?: number; revokedEditorTokens?: number }>(
                await transport.POST("/api/v1/directoryRealms/{directoryRealm}/actions/revokeSessions", {
                    params: { path: { directoryRealm: id } },
                    headers: idempotencyHeaders(opts),
                }),
            );
            return {
                revokedSessions: body.revokedSessions ?? null,
                revokedEditorTokens: body.revokedEditorTokens ?? null,
            };
        },
    };



    // Targets (M2d, ratifica D-D 2026-08-03 — REPLACES the M2c mappings surface,
    // pre-release destructive rename): the NAMED consumer export — one target =
    // one volume at LUN 0 (the multi-volume model was NOT chosen); the masking
    // (host/ACL) hangs off the target: ACL = the UNION of the hosts' iqns, and
    // an EMPTY hosts list means NO ACL = NO ACCESS (a parked export, documented
    // rule). First-class SCOPED resource (PROPOSAL 2026-08-03), unlike the M2c
    // edges that rode the volume's scope. Create answers 201 + the `target.apply`
    // parent task (node-affine children, forward-only reconcile-style — reworked
    // from volume.export.apply); a hosts PATCH answers 200 + a target.apply
    // expedite task; delete answers 202 + the `target.remove` task — the row is
    // gone from the desired state at admission, the task expedites convergence.
    // The readOnly `paths[]` is the observed per-node export render
    // (portal/ALUA/observedAt from `export_status`, keyed by target since M2d —
    // staleness visible, never hidden). M3 (2026-08-03): the target speaks TWO
    // protocols (`protocol: iscsi | rbd`, immutable, default iscsi — the whole
    // pre-M3 surface is the iscsi case). `rbd` = NATIVE RBD export of a
    // ceph-datapool volume (422 on a multiscsi volume): no LIO, no LUN, no chap
    // (422 — RBD authenticates with cephx); `ceph.export.apply` mints one
    // pool/image-capped cephx keystore identity per host and the readOnly
    // `rbd` block renders the client mount info (monitors, pool, image,
    // per-host credential REFERENCE — never the secret) — pure render
    // PASSTHROUGH riding zTarget untouched, like `paths[]`. `iscsi` serves
    // ceph volumes too, via the rbd-map gateway bridge (backend detail: LIO
    // backstore /dev/rbdN on the gateway node — no facade difference).
    const targets = {
        list: async (p: ListParams<FilterableFields["listTargets"]> = {}): Promise<Page<Target>> =>
            page(await transport.GET("/api/v1/targets", { params: { query: listQuery(p) as never } }), zTarget),

        listAll: (p: Omit<ListParams<FilterableFields["listTargets"]>, "limit" | "cursor"> = {}): PageIterator<Target> =>
            genPageIterator(async ({ cursor }) => targets.list({ ...p, cursor })),

        get: async (id: string): Promise<Target> =>
            unwrap(await transport.GET("/api/v1/targets/{target}", { params: { path: { target: id } } }), zTarget),

        // `hosts` optional — omitted, the target is born with no ACL (no access;
        // on a `rbd` target: no minted identity — the same parked-export rule).
        // `protocol` optional (M3, 2026-08-03): omitted = `iscsi`, the whole
        // pre-M3 behavior; `rbd` requires the volume's datapool engine to be ceph
        // (422) and refuses `chap` (422). Creation-fixed — switching protocol
        // is delete + create. `chap` optional (M2g, 2026-08-03 — closes the M2c
        // CHAP fence): an iscsiChap keystore credential by ID — the secret
        // itself NEVER rides this call; the worker resolves it at use time onto
        // the ACLs (configfs attribute writes). `mutual: true` admits only
        // credentials carrying the mutual pair.
        // LUSTRE-2 (2026-10-31): `protocol: "lustre"` + lustreRole/lustreIndex/fsname —
        // the volume becomes a Lustre MGT/MDT/OST on a multiscsi datapool (no
        // hosts, no chap: clients mount over LNet).
        create: async (body: { name: string; volume: string; protocol?: "iscsi" | "rbd" | "lustre"; lustreRole?: "mgt" | "mdt" | "ost"; lustreIndex?: number; fsname?: string; hosts?: string[]; chap?: { credential: string; mutual?: boolean } | null; scope?: string; notes?: string }, opts: DryRunOptions = {}): Promise<WriteResult<Target>> =>
            writeResult(await transport.POST("/api/v1/targets", { params: { query: dryRunQuery(opts) }, body: body as never }), zTarget),

        // A `hosts` or `chap` change (M2g: add/remove/re-point the CHAP
        // credential) resolves with the target.apply expedite task in `.task`
        // (null on cosmetic edits). DATA-PATH WARNING: removing a host logs its
        // initiators out of the LUN, and enabling or re-pointing CHAP logs out
        // initiators that do not present the secret — deliberate operator acts.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<WriteResult<Target>> =>
            writeResult(await transport.PATCH("/api/v1/targets/{target}", {
                params: { path: { target: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zTarget),

        // Resolves with the target.remove task ref (202; null kept for symmetry
        // with the other async deletes).
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/targets/{target}", { params: { path: { target: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },
    };



    // File shares (NAS-1, 2026-08-04, fileshares.yaml —
    // docs/nas-file-services-feasibility-2026-08-04.md, the approved unified
    // SAN/NAS direction): the ENGINE-NEUTRAL NAS resource — a named filesystem
    // exported over NFS (AUTH_SYS + host allow-lists), SMB (LOCAL `fileUser`
    // keystore users) and/or SFTP (same users, chrooted sftp-only sshd). The
    // share lives on a DATAPOOL and never names an engine (architecture §5.2):
    // multiscsi = the single-mounter gateway — the share OWNS a platform-born
    // backing mirror volume (`volume` readOnly back-ref), mounted and served by
    // that volume's AUTHORITY node, with the FLOATING `serviceAddress` following
    // the authority on failover (required there, forbidden on ceph in NAS-1);
    // ceph = a CephFS subvolume of the cluster fs `tvstor-fs`, served from every
    // member node over a CephFS MOUNT (D7, 2026-08-15 — no `vfs_ceph`, which
    // does not exist on EL10), and the topology DIFFERS BY PROTOCOL: NFS
    // (Ganesha FSAL_CEPH), SFTP and FTP active/active, SMB ACTIVE/STANDBY with
    // one owning gateway (`smbOwner`, #152) — NFS byte-range locks reach the
    // CephFS MDS and cross the gateways, Samba's do not (#153, 2026-08-17).
    // The readOnly observation half (`mountedOn`, `serviceAddressOn`,
    // `daemons[]`, capacity, `clientCount`) rides get/list through zFileShare
    // untouched — pure render PASSTHROUGH, staleness visible, never hidden.
    // IDENTITY FENCE, LIFTED IN PART BY NAS-2: `identity` binds a share to a
    // DirectoryRealm (#159), `exports.smb.principals` admits domain subjects
    // on SMB (#163), `aclModel` brings rich NT ACLs (#169), and
    // `exports.nfs.sec` brings Kerberos on NFS (#176 — krb5/krb5i/krb5p on an
    // `ad`-realm share, served by the ceph gateway; `sys` stays the default).
    // The rendered halves ride zFileShare untouched; since #181 ondata 1a the
    // WRITE side carries them too (create body below) and the `fs/acl` pair
    // (fsGetAcl/fsPutAcl) reads and writes the rich ACL of a path.
    const fileShares = {
        list: async (p: ListParams<FilterableFields["listFileShares"]> = {}): Promise<Page<FileShare>> =>
            page(await transport.GET("/api/v1/fileShares", { params: { query: listQuery(p) as never } }), zFileShare),

        listAll: (p: Omit<ListParams<FilterableFields["listFileShares"]>, "limit" | "cursor"> = {}): PageIterator<FileShare> =>
            genPageIterator(async ({ cursor }) => fileShares.list({ ...p, cursor })),

        get: async (id: string): Promise<FileShare> =>
            unwrap(await transport.GET("/api/v1/fileShares/{fileShare}", { params: { path: { fileShare: id } } }), zFileShare),

        // 201 + the engine's create parent in `.task` (`fileshare.create` on
        // multiscsi — volume birth, first-use xfs format, mount, service
        // address claim, daemons; `ceph.fileshare.create` on ceph — fs/MDS
        // birth on the datapool's FIRST share, then subvolume + gateway daemons).
        // EXACTLY the per-protocol blocks matching `protocols` must be present;
        // `replicas`/`serviceAddress` are the multiscsi half (required there,
        // forbidden on ceph — 422 either way); every referenced credential must
        // be a `fileUser` (SMB users need the password half); a claimed
        // serviceAddress is 409 in_use naming the holder.
        // NAS-3 (2026-08-05, PROPOSAL): `ftp` and `tftp` joined the protocol
        // vocabulary — ftp carries the SAME fileUser users (password
        // REQUIRED) plus the DECLARED FTPS passive range (reserved
        // cluster-wide, 409 on overlap; `tls: "none"` is the discouraged
        // plain-FTP opt-out); tftp is multiscsi-only (422 on ceph) and
        // READ-ONLY unless opted out (unauthenticated by nature); `pxe` is a
        // CONFIGURATION BLOCK on a tftp-enabled share — the platform
        // generates pxelinux.cfg/default (BIOS) and grub.cfg (UEFI, ratifica
        // Q3 2026-08-05) from the same entries; the kernels/initrds are
        // artifacts the operator uploads onto the share.
        // NAS-2 halves on the way IN (#181 ondata 1a): `identity` binds the
        // share to a DirectoryRealm (immutable after — ratifica B8: the
        // local->directory migration does not exist); `aclModel: nt` makes
        // the NT descriptor the authority for SMB (immutable; SHARE-1PROTO
        // #213, 2026-08-24: the model COMMANDS the protocol list — `nt`
        // admits only `smb`, `posix` only nfs/sftp/ftp/tftp, any crossing is
        // 422 protocol_model_mismatch; the B4 admission consent is gone —
        // and since FSACL-CONSENSO, 2026-08-26, the per-write twin on
        // fsPutAcl is gone too: no consent field survives anywhere);
        // `smb.principals` are the domain subjects of an `ad`-realm share
        // (`users` is the local-share list — the two never mix, #163);
        // `nfs.sec` krb5* demands a directory `ad` realm (#176);
        // `previousVersions` is ceph-only, ON by default there (#177 — on
        // multiscsi the block is 422 previous_versions_not_supported).
        // #111 (2026-08-14, GUI-RESIDUI #189 on the way in): `quota` is the
        // per-folder quota block — `scope: "share"` limits the share root on
        // BOTH engines (REQUIRED limitBytes, min 1 MiB, never above sizeGiB);
        // `scope: "subtree"` lists per-directory limits and is MULTISCSI-ONLY
        // (on ceph it is 422 quota_scope_not_supported naming the MDS
        // propagation delay — the CephFS quota attribute does not descend).
        // REMODEL R3 (#214): the share CONSUMES a volume of the declared
        // model (`volume` replaces `datapool`; `replicas` is dead — the
        // redundancy is the volume's). `sizeGiB` is the share's QUOTA on the
        // volume's promise: on ceph N shares divide it (the aggregate must
        // fit), on multiscsi it must EQUAL it (one share per volume).
        create: async (body: {
            name: string;
            volume: string;
            sizeGiB: number;
            protocols: ("nfs" | "smb" | "sftp" | "ftp" | "tftp")[];
            identity?: { mode: "local" | "directory"; realmId?: string | null };
            aclModel?: "posix" | "nt";
            previousVersions?: { enabled: boolean; intervalMinutes?: number | null; keep?: number | null } | null;
            quota?: {
                scope: "share" | "subtree";
                limitBytes?: number | null;
                directories?: { path: string; limitBytes: number }[] | null;
            } | null;
            nfs?: { readOnly?: boolean; allow: string[]; squash?: "root" | "none"; sec?: "sys" | "krb5" | "krb5i" | "krb5p" };
            // ENEA-A-R1 N6 (2026-10-23): `encryption` = `server smb encrypt` della stanza (default desired).
            smb?: { readOnly?: boolean; users?: { credential: string; readOnly?: boolean }[]; principals?: string[]; encryption?: "required" | "desired" | "off" };
            sftp?: { readOnly?: boolean; users: { credential: string; readOnly?: boolean }[] };
            ftp?: {
                readOnly?: boolean;
                users: { credential: string; readOnly?: boolean }[];
                tls?: "explicit" | "none";
                passivePortRange: { from: number; to: number };
            };
            tftp?: { readOnly?: boolean; subpath?: string };
            pxe?: {
                defaultEntry?: string;
                timeoutSeconds?: number;
                entries: { name: string; label?: string; kernel: string; initrd?: string; append?: string }[];
            };
            serviceAddress?: string;
            scope?: string;
            notes?: string;
            // ENEA-B-R14: throughput ceilings on the share's service address (multiscsi; 422 qos_not_supported on ceph).
            qos?: ShareQosInput | null;
        }, opts: DryRunOptions = {}): Promise<WriteResult<FileShare>> =>
            writeResult(await transport.POST("/api/v1/fileShares", { params: { query: dryRunQuery(opts) }, body: body as never }), zFileShare),

        // Writable: `name` (ALSO the protocol-visible export name — clients
        // re-connect under it), `protocols` with their per-protocol blocks (the
        // exactly-matching-blocks rule holds on PATCH — the `pxe` block rides
        // the tftp half exactly like at creation), `serviceAddress`, `quota`
        // (#111 — same engine rule as at creation: `scope: subtree` on ceph is
        // 422 quota_scope_not_supported; `null` removes the quota, on ceph
        // restoring the subvolume ceiling to sizeGiB), `previousVersions`
        // (#177), tags and notes. The NAS-2 leaves inside the blocks are
        // writable too (#185, 2026-08-19): `smb.principals` and `nfs.sec`
        // change post-creation under the SAME admission rules as at create
        // (principals required on an `ad`-realm share and forbidden on a local
        // one; krb5* only on an `ad`-realm share), and the SHARE-1PROTO
        // partition holds on PATCH: the new list is judged against the
        // CURRENT (immutable) aclModel — crossing the border is 422
        // protocol_model_mismatch, no consent. Export-affecting changes are
        // CONFIG WRITES that converge
        // via the worker reconcile pass — no task rides the response (the
        // patchTarget hosts-change shape). `datapool`/`sizeGiB`/`replicas` are
        // 422 immutable_field (expand is a later round, dated seam 2026-08-04);
        // `identity` and `aclModel` are 409 conflict naming the way out (new
        // share + copy — #159/#169, reconfirmed by #185: changing the ACL
        // model on a live share is a migration, not a patch).
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<FileShare> =>
            unwrap(await transport.PATCH("/api/v1/fileShares/{fileShare}", {
                params: { path: { fileShare: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zFileShare),

        // Resolves with the delete task ref (202). `destroyData` is the
        // MANDATORY explicit decision — NO DEFAULT (never silently destroys
        // data; omitting it server-side is 422, and this signature makes it
        // unomittable). REMODEL R3: the consumed VOLUME always survives the
        // share — `true` removes the share's own bytes (ceph: the subvolume;
        // multiscsi: 422 speaking — the bytes are the volume's one
        // filesystem, delete the VOLUME); `false` keeps them.
        delete: async (id: string, destroyData: boolean): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/fileShares/{fileShare}", {
                params: { path: { fileShare: id }, query: { destroyData } },
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // The REPAIR VERB: re-render every config, remount the filesystem and
        // restart the share's daemons in place — the operator's stick when the
        // observed state has drifted beyond what the reconcile pass heals.
        // Resolves with the task ref (202; `fileshare.remount` on multiscsi —
        // authority-node-affine stop→unmount→mount→re-render→start;
        // `ceph.fileshare.remount` on ceph — gateway daemons on every member
        // node, no mount of ours to cycle). Forward-only — a re-run converges.
        remount: async (id: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/fileShares/{fileShare}/actions/remount", {
                params: { path: { fileShare: id } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // #34 SHRINK (ratifica 2026-08-28) + GROW-MS (2026-09-04): il resize
        // della share, entrambi i versi. Cephfs: SINCRONO 200 col render
        // nuovo (la taglia E' la quota del subvolume, il reconcile la
        // converge dalla riga) — `share` porta il render, `task` null.
        // Multiscsi: il GROW risponde 202 + il task `fileshare.grow`
        // (gambe + md --grow + resize2fs ONLINE sull'autorita') — `task`
        // porta il ref, `share` null (la forma di volumes.expand/shrink);
        // lo SHRINK ms resta il 422 che nomina il volume shrink. I pavimenti
        // (usato osservato + margine) e i tetti (aggregato nella promessa /
        // anti-overcommit del volume) parlano i numeri come ApiProblem.
        resize: async (id: string, body: { sizeGiB: number }, opts: IdempotencyOptions = {}): Promise<{ share: FileShare | null; task: string | null }> => {
            const result = await transport.POST("/api/v1/fileShares/{fileShare}/actions/resize", {
                params: { path: { fileShare: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            if (result.response.status === 200) {
                const share = unwrap(result, zFileShare);
                return { share, task: null };
            }
            unwrap(result);
            return { share: null, task: result.response.headers.get("x-tvstor-task") };
        },

        // THE KILL-SWITCH (SEC-REVOKE D5 #79, 2026-08-18) — sudo: the share
        // stops serving on every protocol (worker reconcile beat, ~30 s), the
        // files-realm sessions whose perimeter contains it are revoked and its
        // editor tokens hard-deleted NOW. Idempotent: a second press answers
        // the same lockedAt and re-sweeps (normally 0/0). Synchronous config
        // write — no task rides the response.
        lockdown: async (id: string, opts: IdempotencyOptions = {}): Promise<FileShareLockdownResult> =>
            unwrap(await transport.POST("/api/v1/fileShares/{fileShare}/actions/lockdown", {
                params: { path: { fileShare: id } },
                headers: idempotencyHeaders(opts),
            }), zFileShareLockdownResult),

        // The contrary verb — sudo: clears the lockdown, serving converges
        // back on the next reconcile beat. Sessions/tokens the lockdown
        // revoked STAY revoked (users log in again). Idempotent.
        unlock: async (id: string, opts: IdempotencyOptions = {}): Promise<FileShare> =>
            unwrap(await transport.POST("/api/v1/fileShares/{fileShare}/actions/unlock", {
                params: { path: { fileShare: id } },
                headers: idempotencyHeaders(opts),
            }), zFileShare),

        // ENEA-A-R9 (2026-10-23): the destination-side promotion of a cephfs
        // mirror REPLICA share — sudo + confirm-typed + the acknowledged
        // recovery point (`acknowledgeSnapshot` = the replica's CURRENT
        // lastSyncedSnapshot, null when none was observed). 202 + the
        // cephfs.mirror.promote task: the peer's cephx user is revoked (the
        // cut — EVERY share riding that peer stops mirroring), the quota
        // re-applied, the share becomes ordinary.
        promote: async (id: string, body: { confirm: string; acknowledgeSnapshot: string | null; name?: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/fileShares/{fileShare}/actions/promote", {
                params: { path: { fileShare: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // ---- ENEA-A-R10 «catalogo» (2026-10-23, fileshares.yaml catalog*): the
        // share's file-metadata catalog, answered by the OWNER node (the
        // backend forwards). Opt-in per share (FileShare.catalog, PATCH).
        catalog: {
            // The policy in force, owner, database, last/running scan, next
            // due, totals — 200 even when disabled (`enabled: false`, nulls).
            status: async (shareId: string): Promise<CatalogStatus> =>
                unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/catalog/status", {
                    params: { path: { fileShare: shareId } },
                }), zCatalogStatus),

            // One page of entries: filters, `sort` (`-` = descending), keyset
            // `cursor` from the previous page's nextCursor. 409
            // catalog_disabled, 503 catalog_unavailable, 400
            // catalog_cursor_invalid.
            list: async (shareId: string, q: CatalogListQuery = {}): Promise<Page<CatalogEntry>> =>
                page(await transport.GET("/api/v1/fileShares/{fileShare}/catalog", {
                    params: { path: { fileShare: shareId }, query: catalogQueryOf(q) as never },
                }), zCatalogEntry),

            listAll: (shareId: string, q: Omit<CatalogListQuery, "cursor"> = {}): PageIterator<CatalogEntry> =>
                genPageIterator(async ({ cursor }) => fileShares.catalog.list(shareId, { ...q, ...(cursor !== undefined && cursor !== null ? { cursor } : {}) })),

            summary: async (shareId: string): Promise<CatalogSummary> =>
                unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/catalog/summary", {
                    params: { path: { fileShare: shareId } },
                }), zCatalogSummary),

            // Births ONE catalog.scan task on the owner node (202 +
            // X-TVStor-Task); `mode` forces full/incremental. 409
            // catalog_scan_running while one is active.
            scan: async (shareId: string, body: { mode?: "full" | "incremental" } = {}, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/fileShares/{fileShare}/catalog/actions/scan", {
                    params: { path: { fileShare: shareId } },
                    body: body as never,
                    headers: idempotencyHeaders(opts),
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // The read-only SQL credential (sudo): host, port, database,
            // username, password, dsn, the TLS the server presents.
            credentials: async (shareId: string): Promise<CatalogCredentials> =>
                unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/catalog/credentials", {
                    params: { path: { fileShare: shareId } },
                }), zCatalogCredentials),

            // The CSV/JSONL export, STREAMED (same filters and sort as list):
            // the body stream and the headers to save by — never buffered.
            export: async (shareId: string, format: "csv" | "jsonl", q: Omit<CatalogListQuery, "cursor" | "limit"> = {}): Promise<FsDownload> => {
                const query = new URLSearchParams({ format });
                for (const [key, value] of Object.entries(catalogQueryOf(q))) {
                    query.set(key, String(value));
                }
                const response = await fetchImpl(`${options.baseUrl}/api/v1/fileShares/${shareId}/catalog/export?${query.toString()}`, { headers: authHeaders() });
                if (!response.ok || response.body === null) {
                    throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
                }
                const length = response.headers.get("content-length");
                return {
                    stream: response.body,
                    contentType: response.headers.get("content-type"),
                    contentLength: length !== null ? Number(length) : null,
                    contentDisposition: response.headers.get("content-disposition"),
                };
            },

            // The browser-native export URL (anchor href) — carries the session
            // token as `access_token` ONLY where the backend admits it; the
            // catalog export does NOT (the bearer is required), so this is the
            // bare URL for a fetch with the Authorization header.
            exportUrl: (shareId: string, format: "csv" | "jsonl", q: Omit<CatalogListQuery, "cursor" | "limit"> = {}): string => {
                const query = new URLSearchParams({ format });
                for (const [key, value] of Object.entries(catalogQueryOf(q))) {
                    query.set(key, String(value));
                }
                return `${options.baseUrl}/api/v1/fileShares/${shareId}/catalog/export?${query.toString()}`;
            },
        },

        // ---- ENEA-A-R12 «gestione 2» (2026-10-23, fileshares.yaml auditTail):
        // the share's CLIENT AUDIT — the switch is the `audit` leaf of the
        // share (create/PATCH: {enabled, operations?}); the last `tail`
        // events (1-1000, default 100) come from the OWNER node's audit file
        // (the backend forwards; 503 audit_tail_unavailable when the owner
        // does not answer). A sensitive read (ENEA-A-R11 opt-in per scope).
        audit: {
            tail: async (shareId: string, tail?: number): Promise<ClientAuditTail> =>
                unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/audit", {
                    params: { path: { fileShare: shareId }, query: tail !== undefined ? { tail } : {} },
                }), zClientAuditTail),
        },

        // ---- the web explorer's fs surface (NAS-WebExplorer #30, 2026-08-05,
        // fileshares.yaml): live-filesystem verbs on the share's serving node,
        // reached through the backend-to-backend proxy — the facade never
        // learns which node served. Paths are RELATIVE to the share root and
        // fenced server-side (`..`, absolute paths, backslashes, control
        // characters → 422 speaking the violated rule); permission grains are
        // `fileshare.browse` (list + the two stream GETs) and `fileshare.write`
        // (mkdir/rename/delete/upload). A share not serving is 422 naming its
        // phase; a dead serving node is 503 `node_unreachable` (retryable).

        // One page of ONE folder (non-recursive; omitted path = the root).
        // Own limit bounds (default 500, max 2000 — declared deviation from
        // the common Limit); no filter/sort/scope — a folder, not a resource
        // collection. A cursor from ANOTHER folder's listing is 400.
        fsList: async (id: string, p: { path?: string; limit?: number; cursor?: string | null } = {}): Promise<FsListing> => {
            const query: Record<string, unknown> = {};
            if (p.path !== undefined && p.path !== "") {
                query["path"] = p.path;
            }
            if (p.limit !== undefined) {
                query["limit"] = p.limit;
            }
            if (p.cursor !== undefined && p.cursor !== null) {
                query["cursor"] = p.cursor;
            }
            const body = unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/fs", {
                params: { path: { fileShare: id }, query: query as never },
            }), zFsListing);
            return { path: body.path, entries: body.entries, nextCursor: body.nextCursor ?? null };
        },

        // Per-entry auto-pagination of ONE folder — the genPageIterator idiom
        // with the listing's `entries` as the page items.
        fsListAll: (id: string, p: { path?: string; limit?: number } = {}): PageIterator<FsEntry> =>
            genPageIterator(async ({ cursor }) => {
                const listing = await fileShares.fsList(id, { ...p, cursor });
                return { items: listing.entries, nextCursor: listing.nextCursor };
            }),

        // Creates ONE folder — the parent must already exist (404 otherwise;
        // intermediate creation belongs to the upload verb alone); a taken
        // name (file or folder alike) is 409.
        fsMkdir: async (id: string, path: string): Promise<void> => {
            unwrap(await transport.POST("/api/v1/fileShares/{fileShare}/fs/mkdir", {
                params: { path: { fileShare: id } },
                body: { path },
            }));
        },

        // Renames INSIDE the entry's own folder: `newName` is ONE segment
        // (422 otherwise); a move between folders does not exist on this
        // surface (dated seam 2026-08-05). Taken sibling name → 409.
        fsRename: async (id: string, path: string, newName: string): Promise<void> => {
            unwrap(await transport.POST("/api/v1/fileShares/{fileShare}/fs/rename", {
                params: { path: { fileShare: id } },
                body: { path, newName },
            }));
        },

        // Deletes the listed entries, folders RECURSIVELY — no trash, no
        // undo. NOT transactional: in order, the first failure aborts the
        // remainder with its own problem; already-removed entries stay
        // removed. The share root (empty path) is refused (422).
        fsDelete: async (id: string, paths: string[]): Promise<void> => {
            unwrap(await transport.POST("/api/v1/fileShares/{fileShare}/fs/delete", {
                params: { path: { fileShare: id } },
                body: { paths },
            }));
        },

        // RAW-BODY upload — the bytes ARE the request body (no multipart
        // envelope), streamed end to end, so this bypasses the JSON transport
        // and rides the injected fetch directly (the system.openapi idiom).
        // Missing intermediate folders are created; an existing file at
        // `path` is OVERWRITTEN (last-write-wins — confirm client-side); a
        // kind collision (folder where the file must land, file where a
        // folder must rise) is 409. Accepts bytes (Uint8Array/Buffer,
        // ArrayBuffer) or a web ReadableStream — a stream never buffers
        // whole (`duplex: "half"`, the Node fetch requirement for streamed
        // request bodies; browsers ignore it).
        fsUpload: async (id: string, path: string, body: Uint8Array | ArrayBuffer | ReadableStream<Uint8Array>): Promise<void> => {
            const query = new URLSearchParams({ path });
            const response = await fetchImpl(`${options.baseUrl}/api/v1/fileShares/${id}/fs/content?${query.toString()}`, {
                method: "PUT",
                headers: authHeaders({ "content-type": "application/octet-stream" }),
                body,
                duplex: "half",
            });
            if (!response.ok) {
                throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
            }
        },

        // Streams ONE regular file's bytes (422 on a folder or special
        // file) — the body is handed over UNREAD with the headers a consumer
        // saves by; `inline` asks for the preview Content-Disposition.
        fsDownload: async (id: string, path: string, opts: { inline?: boolean } = {}): Promise<FsDownload> => {
            const query = new URLSearchParams({ path });
            if (opts.inline === true) {
                query.set("inline", "true");
            }
            const response = await fetchImpl(`${options.baseUrl}/api/v1/fileShares/${id}/fs/content?${query.toString()}`, { headers: authHeaders() });
            if (!response.ok || response.body === null) {
                throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
            }
            const length = response.headers.get("content-length");
            return {
                stream: response.body,
                contentType: response.headers.get("content-type"),
                contentLength: length !== null ? Number(length) : null,
                contentDisposition: response.headers.get("content-disposition"),
            };
        },

        // Builds the browser-native download URL (anchor href, window.open,
        // iframe src — contexts that cannot set headers): the DECLARED auth
        // exception puts the CURRENT session token on the query string as
        // `access_token` (stream GETs only; header wins when both ride).
        // The URL embeds the token — treat it as the credential it carries:
        // hand it to the browser, never log it, never persist it.
        fsDownloadUrl: (id: string, path: string, opts: { inline?: boolean } = {}): string => {
            const query = new URLSearchParams({ path });
            if (opts.inline === true) {
                query.set("inline", "true");
            }
            if (token !== null) {
                query.set("access_token", token);
            }
            return `${options.baseUrl}/api/v1/fileShares/${id}/fs/content?${query.toString()}`;
        },

        // The folder twin of fsDownloadUrl: the on-the-fly ZIP of `path`
        // (omitted = the whole share) — same access_token exception, same
        // credential caution. 422 when `path` names a file.
        fsArchiveUrl: (id: string, path?: string): string => {
            const query = new URLSearchParams();
            if (path !== undefined && path !== "") {
                query.set("path", path);
            }
            if (token !== null) {
                query.set("access_token", token);
            }
            const qs = query.toString();
            return `${options.baseUrl}/api/v1/fileShares/${id}/fs/archive${qs !== "" ? `?${qs}` : ""}`;
        },

        // Mints a DOCUMENT EDITOR session for the file at `path` (WEBEXP-EDIT
        // #63, 2026-08-05 — Collabora/CODE via WOPI): resolves with the editor
        // frame URL (RELATIVE to the API origin since WEBEXP-PROXY 2026-08-06
        // — it rides the backend's /api/v1/collabora reverse proxy) and the
        // DEDICATED short-lived `tvse_` editor token the
        // webui form-posts into the iframe (`access_token`/`access_token_ttl`,
        // the WOPI convention — `accessTokenTtl` is ms since epoch). The token
        // renders EXACTLY ONCE. `mode: view` demands `fileshare.browse`,
        // `mode: edit` additionally `fileshare.write` (403 otherwise); a
        // cluster with no `documentEditor` configured, or an extension the
        // server's discovery does not offer, is a SPEAKING 409 conflict —
        // render the problem, never a broken editor page.
        fsEditorSession: async (id: string, p: { path: string; mode: "view" | "edit" }): Promise<FsEditorSession> =>
            unwrap(await transport.POST("/api/v1/fileShares/{fileShare}/fs/editorSession", {
                params: { path: { fileShare: id } },
                body: { path: p.path, mode: p.mode },
            }), zFsEditorSession),

        // Reads the ACL of `path` (NAS-2 Fase 2, #169) in the share's NATIVE
        // model — never an invented translation. `path` rides the QUERY here
        // (absent/empty = the share root; contrast fsPutAcl, where it rides
        // the body — the wire tests pin both). 422 acl_not_readable when the
        // on-disk ACL carries something this product does not model — the
        // problem names what, and never pretends to have read. Permission
        // `fileshare.browse`.
        fsGetAcl: async (id: string, p: { path?: string } = {}): Promise<FsAcl> => {
            const query: Record<string, unknown> = {};
            if (p.path !== undefined && p.path !== "") {
                query["path"] = p.path;
            }
            return unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/fs/acl", {
                params: { path: { fileShare: id }, query: query as never },
            }), zFsAcl);
        },

        // Writes the ACL of `path` (#169) and resolves with the ACL as it now
        // stands, read back. `model` declares which model the SENT entries
        // are written in; the three outcomes are the server's: native write
        // (no translation), `nt` on a `posix` share (evaluated — 422
        // acl_not_representable ONLY when the loss would OPEN access, with
        // `losses` naming the guilty entries; a loss that restricts or
        // approximates PROCEEDS — what lands is the approximation, shown
        // as-is by the read-back (FSACL-CONSENSO, 2026-08-26: the per-write
        // consent is gone), `posix` on an `nt` share (422 —
        // the silent-degrade direction stays refused). The mask halves
        // are serialized to JSON numbers (bigint does not ride JSON).
        // Permission `fileshare.write`.
        fsPutAcl: async (id: string, p: { path?: string; model: "posix" | "nt"; owner?: string | null; group?: string | null; entries: FsAclEntryInput[] }): Promise<FsAcl> => {
            const entries = p.entries.map((entry) => ({
                kind: entry.kind,
                trustee: entry.trustee,
                rights: entry.rights,
                ...(entry.inherit !== undefined ? { inherit: entry.inherit } : {}),
                ...(entry.unnamedMask !== undefined ? { unnamedMask: Number(entry.unnamedMask) } : {}),
                ...(entry.unnamedFlags !== undefined ? { unnamedFlags: Number(entry.unnamedFlags) } : {}),
            }));
            const body: Record<string, unknown> = { model: p.model, entries };
            if (p.path !== undefined && p.path !== "") {
                body["path"] = p.path;
            }
            if (p.owner !== undefined) {
                body["owner"] = p.owner;
            }
            if (p.group !== undefined) {
                body["group"] = p.group;
            }
            return unwrap(await transport.PUT("/api/v1/fileShares/{fileShare}/fs/acl", {
                params: { path: { fileShare: id } },
                body: body as never,
            }), zFsAcl);
        },

        // ENEA-A-R5 (2026-10-23): the snapshot-RESOURCES of a ceph share —
        // CephFS subvolume snapshots named by the operator (`tvs-<name>` on
        // the cluster; the #177 `@GMT-` cadence is a different family and
        // never appears here). A multiscsi share has none: the create is 422
        // naming the VOLUME's snapshot route. `retention` is frozen at birth
        // (extend-only PATCH; delete 409 retention_active while it runs); the
        // share's own delete is 409 while a retained snapshot lives, and the
        // non-retained ones are drained by the delete task BEFORE the
        // subvolume (never a DB cascade). The clone is `fileShares.create`
        // with `source: { snapshot }` (the volumes' form).
        snapshots: {
            list: async (shareId: string): Promise<Page<FileShareSnapshot>> =>
                page(await transport.GET("/api/v1/fileShares/{fileShare}/snapshots", {
                    params: { path: { fileShare: shareId } },
                }), zFileShareSnapshot),

            listAll: (shareId: string): PageIterator<FileShareSnapshot> =>
                genPageIterator(async () => fileShares.snapshots.list(shareId)),

            get: async (shareId: string, id: string): Promise<FileShareSnapshot> =>
                unwrap(await transport.GET("/api/v1/fileShares/{fileShare}/snapshots/{snapshot}", {
                    params: { path: { fileShare: shareId, snapshot: id } },
                }), zFileShareSnapshot),

            // 201 + the ceph.fileshare.snapshot task in X-TVStor-Task.
            create: async (shareId: string, body: { name: string; retention?: SnapshotRetention }, opts: DryRunOptions = {}): Promise<WriteResult<FileShareSnapshot>> =>
                writeResult(await transport.POST("/api/v1/fileShares/{fileShare}/snapshots", {
                    params: { path: { fileShare: shareId }, query: dryRunQuery(opts) },
                    body: body as never,
                }), zFileShareSnapshot),

            patch: async (shareId: string, id: string, body: { retention: SnapshotRetention }, opts: DryRunOptions = {}): Promise<FileShareSnapshot> =>
                unwrap(await transport.PATCH("/api/v1/fileShares/{fileShare}/snapshots/{snapshot}", {
                    params: { path: { fileShare: shareId, snapshot: id }, query: dryRunQuery(opts) },
                    body: body as never,
                }), zFileShareSnapshot),

            // 202 + the ceph.fileshare.snapshot.delete task ref; 409
            // retention_active while retained, 409 in_use while a clone of it
            // is still being born.
            delete: async (shareId: string, id: string): Promise<string | null> => {
                const result = await transport.DELETE("/api/v1/fileShares/{fileShare}/snapshots/{snapshot}", {
                    params: { path: { fileShare: shareId, snapshot: id } },
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },
        },
    };



    // DHCP services (NAS-3, 2026-08-05, dhcpservices.yaml —
    // docs/nas3-contract-2026-08-05.md; THE ENTIRE RESOURCE IS PROPOSAL
    // pending ratification): the OPTIONAL platform-managed Kea DHCPv4 server.
    // One supervised kea-dhcp4 instance per service, ANCHORED to a
    // TFTP-enabled MULTISCSI file share (`fileShare` — required, immutable):
    // the daemon runs on that share's authority node bound to its floating
    // `serviceAddress`, follows every failover, and (when `bootOptions` is
    // present) hands PXE clients next-server/filename pointing at that same
    // address. OPTIONAL BY DOCTRINE: sites with an existing DHCP configure
    // options 66/67 on their own server instead and never create one of
    // these. SYNCHRONOUS CRUD (no tasks — config writes the worker reconcile
    // converges); EVERY mutation is SUDO-GATED (call auth.sudo first, the
    // grant rides automatically): a DHCP server answers every machine on the
    // segment, and two authoritative servers corrupt each other's leases —
    // the platform cannot see foreign DHCPs, so it warns loudly (create may
    // answer with an X-TVStor-Warning header — relayed-subnet territory)
    // and refuses the one overlap it can see: its own (subnet unique
    // cluster-wide, 409 in_use naming the holder). The observation half
    // (`phase`, `daemon`, `leaseCount`, `observedAt`) rides get/list through
    // zDhcpService untouched — staleness visible, never hidden.
    const dhcpServices = {
        list: async (p: ListParams<FilterableFields["listDhcpServices"]> = {}): Promise<Page<DhcpService>> =>
            page(await transport.GET("/api/v1/dhcpServices", { params: { query: listQuery(p) as never } }), zDhcpService),

        listAll: (p: Omit<ListParams<FilterableFields["listDhcpServices"]>, "limit" | "cursor"> = {}): PageIterator<DhcpService> =>
            genPageIterator(async ({ cursor }) => dhcpServices.list({ ...p, cursor })),

        get: async (id: string): Promise<DhcpService> =>
            unwrap(await transport.GET("/api/v1/dhcpServices/{dhcpService}", { params: { path: { dhcpService: id } } }), zDhcpService),

        // 201 synchronous (`.task` stays null — no task rides a DhcpService
        // write, WriteResult kept for facade symmetry); `.warning` carries
        // the X-TVStor-Warning header when the anchor share's serviceAddress
        // lies outside `subnet` (relayed-subnet territory — the site's DHCP
        // relay must point at the serviceAddress). ADMISSION: `fileShare`
        // must be a multiscsi share with `tftp` among its protocols (422);
        // pool and reservations inside `subnet` (422); a second service on
        // the same subnet is 409 in_use naming the holder.
        create: async (body: {
            name: string;
            fileShare: string;
            subnet: string;
            pool: { from: string; to: string };
            leaseSeconds?: number;
            routers?: string[];
            dnsServers?: string[];
            domainName?: string;
            reservations?: { mac: string; address: string; hostname?: string }[];
            bootOptions?: { nextServer?: string; filename?: string };
            enabled?: boolean;
            scope?: string;
            notes?: string;
        }, opts: DryRunOptions = {}): Promise<WriteResult<DhcpService> & { warning: string | null }> => {
            const result = await transport.POST("/api/v1/dhcpServices", { params: { query: dryRunQuery(opts) }, body: body as never });
            return {
                ...writeResult(result, zDhcpService),
                warning: result.response.headers.get("x-tvstor-warning"),
            };
        },

        // Writable: name, subnet, pool, leaseSeconds, routers, dnsServers,
        // domainName, reservations, bootOptions, enabled, tags, notes —
        // every serving-affecting change converges via the reconcile pass
        // (kea-dhcp4.conf re-render + reload; `enabled: false` parks the
        // service, daemon stopped, config kept — the honest alternative to
        // delete when the customer's own DHCP returns). `fileShare` is 422
        // immutable_field (re-anchoring is a new service).
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<DhcpService> =>
            unwrap(await transport.PATCH("/api/v1/dhcpServices/{dhcpService}", {
                params: { path: { dhcpService: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zDhcpService),

        // Synchronous 204: the row goes and the reconcile pass stops the
        // daemon — leased clients keep their leases until expiry (harmless,
        // stated honestly). Nothing references a DhcpService.
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/dhcpServices/{dhcpService}", { params: { path: { dhcpService: id } } }));
        },
    };



    // Networks (NET-1, 2026-08-05, networks.yaml — charter
    // docs/networking-modello-2026-08-04.md, contract shapes PROPOSAL where
    // marked): the DECLARED NETWORK model — cluster-level {name, category,
    // vlanId?, subnet, mtu?} in the five ratified categories (management,
    // replicaInternal, client, replicaExternal, san — #210) with PER-NODE bindings
    // (interface XOR platform-rendered bond, address inside the subnet).
    // Declaring a network is what migrates a surface off its dated address
    // seam: replicaInternal re-homes the internal NVMe/TCP transport and the
    // ENC-1 WireGuard endpoints, client re-homes the iSCSI portals / S3
    // endpoints and becomes the validation home of NAS serviceAddresses and
    // DHCP subnets, replicaExternal sources the DR links; with NO networks
    // declared everything stays on the seams — the honest fallback.
    // SYNCHRONOUS CRUD (no tasks — config writes the worker reconcile
    // converges); EVERY mutation is SUDO-GATED (call auth.sudo first): a
    // wrong replicaInternal binding cuts the data plane. The observed link
    // side (binding `observed`, composed `phase`) rides get/list through
    // zNetwork untouched — staleness visible, never hidden.
    const networks = {
        list: async (p: ListParams<FilterableFields["listNetworks"]> = {}): Promise<Page<Network>> =>
            page(await transport.GET("/api/v1/networks", { params: { query: listQuery(p) as never } }), zNetwork),

        listAll: (p: Omit<ListParams<FilterableFields["listNetworks"]>, "limit" | "cursor"> = {}): PageIterator<Network> =>
            genPageIterator(async ({ cursor }) => networks.list({ ...p, cursor })),

        get: async (id: string): Promise<Network> =>
            unwrap(await transport.GET("/api/v1/networks/{network}", { params: { path: { network: id } } }), zNetwork),

        // 201 synchronous (`.task` stays null — WriteResult kept for facade
        // symmetry): a network is born with NO bindings, nothing touches any
        // node yet. ADMISSION: subnet canonical CIDR, no overlap with another
        // network (409 naming the holder); a second replicaInternal or
        // replicaExternal network is 409 (cardinality — selection must be
        // deterministic); category is immutable after creation.
        create: async (body: {
            name: string;
            category: "management" | "replicaInternal" | "client" | "replicaExternal" | "san";
            subnet: string;
            vlanId?: number | null;
            mtu?: number | null;
            scope?: string;
            notes?: string;
        }, opts: DryRunOptions = {}): Promise<WriteResult<Network>> =>
            writeResult(await transport.POST("/api/v1/networks", { params: { query: dryRunQuery(opts) }, body: body as never }), zNetwork),

        // Writable: name (cosmetic — references are id-based), notes, vlanId
        // and mtu (both converge via reconcile on every bound node), subnet
        // ONLY while the network has no bindings (renumbering under live
        // addresses is delete-and-redeclare). `category` is 422
        // immutable_field.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Network> =>
            unwrap(await transport.PATCH("/api/v1/networks/{network}", {
                params: { path: { network: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zNetwork),

        // Synchronous 204 — only with NO bindings (409 in_use naming the
        // bound nodes otherwise: each binding delete is the explicit per-node
        // re-homing gesture); a client network additionally refuses while a
        // FileShare serviceAddress or DhcpService subnet lives inside it.
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/networks/{network}", { params: { path: { network: id } } }));
        },

        // The per-node binding sub-resource, keyed by ClusterNode id: ONE
        // binding per (network, node), UPSERT PUT — a binding is per-node
        // declarative config, never an append-only membership event.
        bindings: {
            // FACADE-TIPATA (#183): the response now parses through the
            // generated zNetworkNodeBindingList (it used to ride unvalidated
            // through an internal cast).
            list: async (networkId: string): Promise<NetworkNodeBinding[]> => {
                const result = await transport.GET("/api/v1/networks/{network}/bindings", {
                    params: { path: { network: networkId } },
                });
                const body = unwrap(result, zNetworkNodeBindingList);
                return body.items;
            },

            get: async (networkId: string, nodeId: string): Promise<NetworkNodeBinding> =>
                unwrap(await transport.GET("/api/v1/networks/{network}/bindings/{node}", {
                    params: { path: { network: networkId, node: nodeId } },
                }), zNetworkNodeBinding),

            // THE migration gesture (NET-1): the moment a replicaInternal
            // binding lands, the reconcile pass renders bond/VLAN/address and
            // re-homes the node's internal transport (and ENC-1 tunnel
            // endpoint); a client binding flips the node's iSCSI portal on
            // the next reconcile. `201` when new, `200` on full replacement.
            // ADMISSION: exactly one of interface/bond; address inside the
            // network's subnet, free fleet-wide (other bindings and FileShare
            // serviceAddresses — 409 in_use); interface/bond-member
            // single-owner per node; lacpRate only with mode 802.3ad.
            put: async (networkId: string, nodeId: string, body: {
                interface?: string;
                bond?: {
                    members: string[];
                    mode: "active-backup" | "802.3ad";
                    lacpRate?: "slow" | "fast" | null;
                };
                address: string;
            }, opts: DryRunOptions = {}): Promise<NetworkNodeBinding> =>
                unwrap(await transport.PUT("/api/v1/networks/{network}/bindings/{node}", {
                    params: { path: { network: networkId, node: nodeId }, query: dryRunQuery(opts) },
                    body: body as never,
                }), zNetworkNodeBinding),

            // Synchronous 204: the explicit per-node exit — the reconcile
            // pass tears down what the platform rendered and the node's
            // traffic for this category falls back to its seam (DISRUPTIVE
            // on replicaInternal, stated honestly).
            delete: async (networkId: string, nodeId: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v1/networks/{network}/bindings/{node}", {
                    params: { path: { network: networkId, node: nodeId } },
                }));
            },
        },
    };



    // Object stores (OBJ-1, 2026-08-04, objectstores.yaml —
    // docs/object-storage-s3-design-2026-08-04.md, ratifica §2): the S3
    // surface — ENGINE-NEUTRAL like volumes and file shares: the store lives
    // on a DATAPOOL and never names an engine. On a `ceph` datapool it is an
    // RGW zone served ACTIVE/ACTIVE by one supervised `radosgw` per member
    // node — no floating address (the NAS-1 ceph twin): clients connect to
    // the member nodes' own data addresses on the store's one `port`, listed
    // in the readOnly `endpoints[]` render. A `multiscsi` datapool is refused
    // in OBJ-1 (422 — versitygw is the OBJ-MS round, dated seam 2026-08-04).
    // TLS: `tlsCertificate` is an OPTIONAL keystore reference (kind
    // `tlsCertificate`); null = the store serves with the CLUSTER certificate
    // — the declared fallback, foreign to public CAs: S3 clients must pin or
    // trust the cluster CA (the operator doc carries the instruction). The
    // observation half (`daemons[]`, `endpoints[]`, usage totals,
    // `unmanagedBuckets[]` — buckets born over the S3 wire, censused and
    // rendered honestly, never owned) rides get/list through zObjectStore
    // untouched — pure render PASSTHROUGH, staleness visible, never hidden.
    // Buckets and S3 accounts are SUBRESOURCES of the store (the
    // snapshot/vdisk precedent — no scope, names unique per store).
    const objectStores = {
        list: async (p: ListParams<FilterableFields["listObjectStores"]> = {}): Promise<Page<ObjectStore>> =>
            page(await transport.GET("/api/v1/objectStores", { params: { query: listQuery(p) as never } }), zObjectStore),

        listAll: (p: Omit<ListParams<FilterableFields["listObjectStores"]>, "limit" | "cursor"> = {}): PageIterator<ObjectStore> =>
            genPageIterator(async ({ cursor }) => objectStores.list({ ...p, cursor })),

        get: async (id: string): Promise<ObjectStore> =>
            unwrap(await transport.GET("/api/v1/objectStores/{objectStore}", { params: { path: { objectStore: id } } }), zObjectStore),

        // 201 + the `objectstore.create` parent in `.task`. REMODEL R3
        // (#214): the store CONSUMES a volume of the declared model
        // (`volume` replaces `datapool`; `sizeGiB`/`replicas` are dead — the
        // capacity and the redundancy are the VOLUME's), exactly ONE store
        // per volume (409 naming the holder; its buckets are the N). CEPH:
        // the volume's own RGW pools rise first (ceph.objectstore.pools),
        // then one `rgw.node.apply` per member node — active/active.
        // MULTISCSI: one supervised versitygw on the volume's authority —
        // `serviceAddress` REQUIRED there, FORBIDDEN on ceph.
        // ENEA-A-R8: EXACTLY ONE of `volume` (rgw / versitygw form) and
        // `share` (the bidirectional form — versitygw over a CephFS share);
        // `fileOwner` belongs to the share form only.
        create: async (body: {
            name: string;
            volume?: string | null;
            share?: string | null;
            fileOwner?: { uid: number; gid: number } | null;
            port: number;
            tlsCertificate?: string | null;
            serviceAddress?: string | null;
            scope?: string;
            notes?: string;
            // ENEA-B-R14: per-account ceilings (RGW ratelimit; bps only on a multiscsi store; 422 on bidirectional).
            qos?: ObjectStoreQosInput | null;
        }, opts: DryRunOptions = {}): Promise<WriteResult<ObjectStore>> =>
            writeResult(await transport.POST("/api/v1/objectStores", { params: { query: dryRunQuery(opts) }, body: body as never }), zObjectStore),

        // Writable: `name` (cosmetic — the S3 wire never sees it), `port`,
        // `tlsCertificate` and, on a multiscsi store, `serviceAddress`
        // (CONFIG WRITES that converge onto the gateway(s) via the worker
        // reconcile pass — no task rides the response, the patchFileShare
        // precedent), `tags` and `notes`. `volume` is 422 immutable_field
        // (REMODEL R3 — a store never changes volume).
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<ObjectStore> =>
            unwrap(await transport.PATCH("/api/v1/objectStores/{objectStore}", {
                params: { path: { objectStore: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zObjectStore),

        // Resolves with the `objectstore.delete` task ref (202): one
        // `rgw.node.remove` per member node, then the zone and its RADOS
        // pools. REFUSED while the store holds ANY bucket, managed OR
        // observed-unmanaged (409 in_use naming them — the "never silently
        // destroys data" principle); the store's S3Accounts die WITH the
        // store, their minted keystore credentials reaped by the same task.
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/objectStores/{objectStore}", { params: { path: { objectStore: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // Buckets — the S3 containers (subresources; `name` is the
        // PROTOCOL-VISIBLE identifier: S3 naming rules, no dots, immutable —
        // S3 has no rename). MANAGED rows only: buckets born over the S3 wire
        // render on the PARENT store's `unmanagedBuckets[]`, never here.
        buckets: {
            list: async (storeId: string, p: Omit<ListParams<FilterableFields["listBuckets"]>, "scope"> = {}): Promise<Page<Bucket>> =>
                page(await transport.GET("/api/v1/objectStores/{objectStore}/buckets", {
                    params: { path: { objectStore: storeId }, query: listQuery(p) as never },
                }), zBucket),

            listAll: (storeId: string, p: Omit<ListParams<FilterableFields["listBuckets"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<Bucket> =>
                genPageIterator(async ({ cursor }) => objectStores.buckets.list(storeId, { ...p, cursor })),

            get: async (storeId: string, id: string): Promise<Bucket> =>
                unwrap(await transport.GET("/api/v1/objectStores/{objectStore}/buckets/{bucket}", {
                    params: { path: { objectStore: storeId, bucket: id } },
                }), zBucket),

            // 201 + the `bucket.create` task in `.task`. `owner` is an
            // S3Account OF THE SAME STORE (required — an ownerless bucket
            // cannot exist on the wire; immutable in OBJ-1). A name held by
            // an OBSERVED unmanaged bucket is 409 conflict naming it — the
            // zone namespace is one namespace, whoever populated it.
            // Profile v1 leaves (OBJ-MS, 2026-08-05, RATIFIED — both
            // engines): `versioning` ("enabled" at birth or omitted — a
            // bucket cannot be born suspended) and `objectLock` (presence =
            // lock-enabled AT CREATION ONLY, requires versioning enabled;
            // `defaultRetention` takes mode + exactly one of days/years).
            // `quotaBytes` is 422 on a multiscsi store (dated seam).
            create: async (storeId: string, body: {
                name: string;
                owner: string;
                quotaBytes?: number | null;
                versioning?: "enabled" | null;
                objectLock?: { defaultRetention?: { mode: "governance" | "compliance"; days?: number | null; years?: number | null } | null } | null;
                // ENEA-B-R14: the bucket's own ceilings (RGW ratelimit, bucket scope; 422 elsewhere).
                qos?: ObjectStoreQosInput | null;
            }, opts: DryRunOptions = {}): Promise<WriteResult<Bucket>> =>
                writeResult(await transport.POST("/api/v1/objectStores/{objectStore}/buckets", {
                    params: { path: { objectStore: storeId }, query: dryRunQuery(opts) },
                    body: body as never,
                }), zBucket),

            // Writable (OBJ-MS 2026-08-05): `quotaBytes` (ceph — 422 on
            // multiscsi, dated seam), `versioning` (S3 transitions only — a
            // locked bucket never suspends, back-to-null does not exist) and
            // `objectLock.defaultRetention` (the lock PRESENCE is a birth
            // property, 422 immutable_field) — config writes that converge
            // via the reconcile pass, no task.
            patch: async (storeId: string, id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<Bucket> =>
                unwrap(await transport.PATCH("/api/v1/objectStores/{objectStore}/buckets/{bucket}", {
                    params: { path: { objectStore: storeId, bucket: id }, query: dryRunQuery(opts) },
                    body: mergePatch as never,
                    headers: { "content-type": "application/merge-patch+json" },
                }), zBucket),

            // Resolves with the `bucket.delete` task ref (202).
            // `deleteObjects` is the MANDATORY explicit decision — NO DEFAULT
            // (the destroyVolume/wipeDevices doctrine; this signature makes
            // it unomittable): true purges the objects with the bucket;
            // false deletes only an EMPTY bucket (409 conflict when the
            // census shows objects, re-checked live by the executor).
            delete: async (storeId: string, id: string, deleteObjects: boolean): Promise<string | null> => {
                const result = await transport.DELETE("/api/v1/objectStores/{objectStore}/buckets/{bucket}", {
                    params: { path: { objectStore: storeId, bucket: id }, query: { deleteObjects } },
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },
        },

        // S3 accounts — the DATA-PLANE consumer identities (never an
        // operator, never a session — the fileUser twin). The PLATFORM MINTS
        // the key pair into the keystore (kind `s3Account`,
        // provenance-marked: direct DELETE of the credential is 409 in_use
        // while the account lives) and renders the secret EXACTLY ONCE in
        // the create/rotate response — never again. No PATCH exists in
        // OBJ-1: nothing writable lives on the row.
        s3Accounts: {
            list: async (storeId: string, p: Omit<ListParams<FilterableFields["listS3Accounts"]>, "scope"> = {}): Promise<Page<S3Account>> =>
                page(await transport.GET("/api/v1/objectStores/{objectStore}/s3Accounts", {
                    params: { path: { objectStore: storeId }, query: listQuery(p) as never },
                }), zS3Account),

            listAll: (storeId: string, p: Omit<ListParams<FilterableFields["listS3Accounts"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<S3Account> =>
                genPageIterator(async ({ cursor }) => objectStores.s3Accounts.list(storeId, { ...p, cursor })),

            get: async (storeId: string, id: string): Promise<S3Account> =>
                unwrap(await transport.GET("/api/v1/objectStores/{objectStore}/s3Accounts/{s3Account}", {
                    params: { path: { objectStore: storeId, s3Account: id } },
                }), zS3Account),

            // SUDO-PROTECTED (call auth.sudo first — the response renders
            // minted secret material, the danger-ladder rule). 201 with the
            // ONE-TIME S3AccountWithSecret render in `.resource` and the
            // `s3account.create` propagation task in `.task` — print the
            // secret, hand it to the consumer, close the window: no read
            // path ever re-renders it.
            create: async (storeId: string, body: { name: string }, opts: DryRunOptions = {}): Promise<WriteResult<S3AccountWithSecret>> =>
                writeResult(await transport.POST("/api/v1/objectStores/{objectStore}/s3Accounts", {
                    params: { path: { objectStore: storeId }, query: dryRunQuery(opts) },
                    body: body as never,
                }), zS3AccountWithSecret),

            // SUDO-PROTECTED — the create's twin: mints a NEW pair (access
            // key id AND secret: S3 rotates pairs), rendered once in
            // `.resource`; the `s3account.rotate` task adds the new key to
            // the RGW user first and removes the OLD one after
            // `graceMinutes` (0 = immediate swap — new-before-old, so a
            // client fleet re-points without a hard cut, PROPOSAL).
            rotate: async (storeId: string, id: string, body: { graceMinutes?: number } = {}, opts: DryRunOptions & IdempotencyOptions = {}): Promise<WriteResult<S3AccountWithSecret>> =>
                writeResult(await transport.POST("/api/v1/objectStores/{objectStore}/s3Accounts/{s3Account}/actions/rotate", {
                    params: { path: { objectStore: storeId, s3Account: id }, query: dryRunQuery(opts) },
                    body: body as never,
                    headers: idempotencyHeaders(opts),
                }), zS3AccountWithSecret),

            // Resolves with the `s3account.delete` task ref (202): the RGW
            // user's credentials stop authenticating immediately, the minted
            // keystore credential is reaped with the row (the provenance
            // cascade). 409 in_use while any MANAGED bucket names this
            // account as owner — delete the buckets first.
            delete: async (storeId: string, id: string): Promise<string | null> => {
                const result = await transport.DELETE("/api/v1/objectStores/{objectStore}/s3Accounts/{s3Account}", {
                    params: { path: { objectStore: storeId, s3Account: id } },
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },
        },

        // ---- ENEA-A-R12 «gestione 2» (2026-10-23, objectstores.yaml auditTail):
        // the store's CLIENT AUDIT (switch = the `audit` leaf, create/PATCH);
        // the last `tail` S3 events from the serving node's audit file.
        audit: {
            tail: async (storeId: string, tail?: number): Promise<ClientAuditTail> =>
                unwrap(await transport.GET("/api/v1/objectStores/{objectStore}/audit", {
                    params: { path: { objectStore: storeId }, query: tail !== undefined ? { tail } : {} },
                }), zClientAuditTail),
        },
    };



    // Backup targets (M4b, 2026-08-04, backups.yaml): the DESTINATION — where
    // backup objects live (`local` for rigs and small installs, `s3` for the
    // real world; the S3 secret is an `s3Account` keystore REFERENCE, never
    // inline) and under which IMMUTABILITY POLICY. Pure config CRUD, no task.
    // `kind` is immutable; the `immutability` block is freely writable at any
    // time BECAUSE of the binding brochure rule: a Backup freezes
    // `retainUntil`/`immutabilityMode` on its own row at creation, so no
    // policy change here ever weakens anything already taken — it speaks only
    // to future backups. Delete answers 409 in_use while any backup row
    // references the target (retained backups cannot be deleted, so a target
    // with governance/compliance backups inside their window cannot be
    // removed until the window passes — the promise working as designed).
    const backupTargets = {
        list: async (p: ListParams<FilterableFields["listBackupTargets"]> = {}): Promise<Page<BackupTarget>> =>
            page(await transport.GET("/api/v1/backupTargets", { params: { query: listQuery(p) as never } }), zBackupTarget),

        listAll: (p: Omit<ListParams<FilterableFields["listBackupTargets"]>, "limit" | "cursor"> = {}): PageIterator<BackupTarget> =>
            genPageIterator(async ({ cursor }) => backupTargets.list({ ...p, cursor })),

        get: async (id: string): Promise<BackupTarget> =>
            unwrap(await transport.GET("/api/v1/backupTargets/{backupTarget}", { params: { path: { backupTarget: id } } }), zBackupTarget),

        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<BackupTarget> =>
            unwrap(await transport.POST("/api/v1/backupTargets", { params: { query: dryRunQuery(opts) }, body: body as never }), zBackupTarget),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<BackupTarget> =>
            unwrap(await transport.PATCH("/api/v1/backupTargets/{backupTarget}", {
                params: { path: { backupTarget: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zBackupTarget),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/backupTargets/{backupTarget}", { params: { path: { backupTarget: id } } }));
        },
    };



    // Backups (M4b, 2026-08-04, backups.yaml): a point-in-time copy of ONE
    // volume on a BackupTarget, always taken FROM A SNAPSHOT — never the live
    // head. No `name` field (machine-born rows): address by id, find with
    // `?filter=` on volume/target/phase. `create` answers 202 — the pending
    // row rides `.resource`, the `backup.create` parent task `.task`
    // (`snapshot` omitted, the parent's first child takes one and keeps it as
    // the chain base); `kind: "incremental"` ships only the M4a diff extents
    // against the base backup's snapshot (omitted, the platform picks
    // incremental when a usable base exists, full otherwise). `retainUntil`
    // and `immutabilityMode` are computed and FROZEN at creation — the
    // brochure's binding rule lives on the row, not the target. `delete` is
    // THE ENDPOINT THE BROCHURE POINTS AT: 409 retention_locked while
    // `retainUntil` is future and the row's frozen mode is
    // governance/compliance — for EVERY principal, no force flag, no bypass
    // (surfaced as TVStorApiError, code `retention_locked`); 409 in_use while
    // another backup's `baseBackup` points here (delete leaf-first).
    const backups = {
        list: async (p: ListParams<FilterableFields["listBackups"]> = {}): Promise<Page<Backup>> =>
            page(await transport.GET("/api/v1/backups", { params: { query: listQuery(p) as never } }), zBackup),

        listAll: (p: Omit<ListParams<FilterableFields["listBackups"]>, "limit" | "cursor"> = {}): PageIterator<Backup> =>
            genPageIterator(async ({ cursor }) => backups.list({ ...p, cursor })),

        get: async (id: string): Promise<Backup> =>
            unwrap(await transport.GET("/api/v1/backups/{backup}", { params: { path: { backup: id } } }), zBackup),

        create: async (body: { volume: string; target: string; snapshot?: string; kind?: "full" | "incremental"; scope?: string; notes?: string }, opts: DryRunOptions & IdempotencyOptions = {}): Promise<WriteResult<Backup>> =>
            writeResult(await transport.POST("/api/v1/backups", { params: { query: dryRunQuery(opts) }, body: body as never, headers: idempotencyHeaders(opts) }), zBackup),

        // Resolves with the backup.delete task ref (202) — or throws
        // TVStorApiError `retention_locked` (409) while the row's frozen
        // window protects it: that refusal is the product promise, not a bug.
        delete: async (id: string): Promise<string | null> => {
            const result = await transport.DELETE("/api/v1/backups/{backup}", { params: { path: { backup: id } } });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // Materializes the CHAIN (the full, then every incremental up to and
        // including THIS backup) into a volume — works with the source volume
        // and its snapshots long gone. EXACTLY ONE of targetVolume /
        // newVolumeName: the overwrite variant is confirm-typed (`confirm`
        // must equal the target volume's CURRENT name — the caller types it,
        // the facade never fills it in); the new-volume variant takes
        // `datapool` (required when the source volume is gone) and the
        // VolumeCreateRequest engine-half gates (`mode`/`replicas` on a
        // both kinds — REMODEL R2). Resolves with the
        // backup.restore task ref (202).
        restore: async (id: string, body: {
            targetVolume?: string;
            confirm?: string;
            newVolumeName?: string;
            datapool?: string;
            // REMODEL R2: uniform on both kinds, REQUIRED with newVolumeName
            // (`mode` is dead — the restored volume is born species lun).
            // ENEA-A-R13 (2026-10-24): the TWO forms — replicated {copies,
            // minInSync} or erasure-coded {erasure: {k, m}} — ceph only for
            // EC; `deviceClass` composes with both (DISK-2).
            redundancy?: VolumeRedundancyInput;
        }, opts: DryRunOptions & IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/backups/{backup}/actions/restore", {
                params: { path: { backup: id }, query: dryRunQuery(opts) },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // SEC-TSA (#60/#180, backups.yaml actionVerifyTimestamp): RE-READS the
        // RFC 3161 stamp — fetches `meta.json` and `tst.tsr` from the
        // destination (a few KiB, NEVER the payload) and re-runs the public
        // `openssl ts -verify` recipe against the anchor FROZEN on this row.
        // Resolves with the backup.timestamp.verify task ref (202); THE TASK
        // FAILS WHEN THE VERIFICATION FAILS (its error names which of the
        // three problems it found: imprint mismatch / anchor rejection /
        // missing token), so tasks.wait throws TVStorTaskError carrying the
        // server's own verdict — no caller ever deduces a green. 422 when the
        // row carries no token at all (state none/unreachable/refused: there
        // is nothing to re-read); `unverifiable` IS re-verifiable on purpose.
        verifyTimestamp: async (id: string, opts: DryRunOptions & IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/backups/{backup}/actions/verifyTimestamp", {
                params: { path: { backup: id }, query: dryRunQuery(opts) },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // SEC-WORM-1 (2026-09-06): the WORM copy (backup.archive) — the
        // backup stays where it is, a copy goes on a tape target; returns
        // the copy render (phase pending) and the task ref.
        archive: async (id: string, body: { target: string; medium: { label: string }; notes?: string }, opts: IdempotencyOptions = {}): Promise<{ copy: BackupCopy; task: string | null }> => {
            const result = await transport.POST("/api/v1/backups/{backup}/actions/archive", {
                params: { path: { backup: id } },
                body,
                headers: idempotencyHeaders(opts),
            });
            const copy = unwrap(result, zBackupCopy);
            return { copy, task: result.response.headers.get("x-tvstor-task") };
        },

        // SEC-WORM-1: read the backup's bytes back now (backup.scrub).
        scrub: async (id: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/backups/{backup}/actions/scrub", {
                params: { path: { backup: id } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        copies: {
            list: async (backupId: string): Promise<BackupCopy[]> =>
                unwrap(await transport.GET("/api/v1/backups/{backup}/copies", { params: { path: { backup: backupId } } }), zBackupCopyList).items,

            get: async (backupId: string, copyId: string): Promise<BackupCopy> =>
                unwrap(await transport.GET("/api/v1/backups/{backup}/copies/{copy}", { params: { path: { backup: backupId, copy: copyId } } }), zBackupCopy),

            eject: async (backupId: string, copyId: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/backups/{backup}/copies/{copy}/actions/eject", {
                    params: { path: { backup: backupId, copy: copyId } },
                    headers: idempotencyHeaders(opts),
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            scrub: async (backupId: string, copyId: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
                const result = await transport.POST("/api/v1/backups/{backup}/copies/{copy}/actions/scrub", {
                    params: { path: { backup: backupId, copy: copyId } },
                    headers: idempotencyHeaders(opts),
                });
                unwrap(result);
                return result.response.headers.get("x-tvstor-task");
            },

            // Declare the medium lost or destroyed — sudo (call auth.sudo
            // first) + confirm-typed with the cartridge LABEL. The record
            // stays; the origin backup becomes deletable again.
            forget: async (backupId: string, copyId: string, confirm: string): Promise<BackupCopy> =>
                unwrap(await transport.POST("/api/v1/backups/{backup}/copies/{copy}/actions/forget", {
                    params: { path: { backup: backupId, copy: copyId } },
                    body: { confirm },
                }), zBackupCopy),
        },
    };



    // Replication links (M4b, 2026-08-04, replication.yaml): OUTBOUND
    // asynchronous DR replication to a second TVStor cluster — the link lives
    // on the SOURCE and ships snapshot deltas (the M4a CBT again: diff
    // against the last replicated snapshot, full seed when no base) to the
    // remote's internal receive endpoint, authenticated by the link's
    // `replicationToken` keystore entry — never a user session. Config CRUD
    // is synchronous; syncs are task-born (`replication.sync`), driven by the
    // `schedule.everyMinutes` reconcile tick or `syncNow`. The remote is NOT
    // contacted at admission (a DR site being down must never block
    // configuring its link) — the first sync is the connectivity test.
    // Delete never touches the destination: remote replica volumes remain,
    // readable, promotable by the future promote round.
    const replicationLinks = {
        list: async (p: ListParams<FilterableFields["listReplicationLinks"]> = {}): Promise<Page<ReplicationLink>> =>
            page(await transport.GET("/api/v1/replicationLinks", { params: { query: listQuery(p) as never } }), zReplicationLink),

        listAll: (p: Omit<ListParams<FilterableFields["listReplicationLinks"]>, "limit" | "cursor"> = {}): PageIterator<ReplicationLink> =>
            genPageIterator(async ({ cursor }) => replicationLinks.list({ ...p, cursor })),

        get: async (id: string): Promise<ReplicationLink> =>
            unwrap(await transport.GET("/api/v1/replicationLinks/{link}", { params: { path: { link: id } } }), zReplicationLink),

        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<ReplicationLink> =>
            unwrap(await transport.POST("/api/v1/replicationLinks", { params: { query: dryRunQuery(opts) }, body: body as never }), zReplicationLink),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<ReplicationLink> =>
            unwrap(await transport.PATCH("/api/v1/replicationLinks/{link}", {
                params: { path: { link: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zReplicationLink),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/replicationLinks/{link}", { params: { path: { link: id } } }));
        },

        // The SAME replication.sync the reconcile tick would start,
        // off-schedule (e.g. right before planned source-site maintenance):
        // one parent, one child per volume (snapshot → diff → ship → commit →
        // GC old base). 409 conflict while a sync for this link is already
        // running or the link is disabled (never two concurrent syncs of one
        // link). Resolves with the task ref (202).
        syncNow: async (id: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/replicationLinks/{link}/actions/syncNow", {
                params: { path: { link: id } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // THE ORDERLY DR HANDOVER (#38 REP-PROMOZIONE, 2026-08-22): the
        // source is alive — quiesce, final ship per volume, then the
        // destination promotes each replica over the same wire/token/anchor
        // as the ship; the LAST child freezes this link in phase 'promoted'
        // (sticky: ticks skip it, syncNow answers 409, enabled toggles are
        // 422 — the way back is `demote`, #208). Sudo AND confirm-typed
        // (`confirm` = the link's CURRENT name — the caller types it).
        // Resolves with the replication.handover task ref (202).
        promote: async (id: string, body: { confirm: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/replicationLinks/{link}/actions/promote", {
                params: { path: { link: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // THE REVERSAL'S FIRST MOVE (#208 RESIDUI-REP, 2026-08-23), on the
        // OLD SOURCE, for a link frozen 'promoted' only: demotes the listed
        // volumes into adoptable replicas of the promoted side (their
        // content will be OVERWRITTEN by the reverse link's seed — the
        // point), consumes the promotion memory they carried, and consumes
        // the link itself. Sudo AND confirm-typed; every pair is typed by
        // the caller (the acknowledgeStamp discipline — the facade never
        // fills confirm or sourceVolume). Synchronous 200 with the result.
        demote: async (
            id: string,
            body: { confirm: string; volumes: { volume: string; sourceVolume: string }[] },
            opts: IdempotencyOptions = {},
        ): Promise<ReplicationLinkDemoteResult> =>
            unwrap(await transport.POST("/api/v1/replicationLinks/{link}/actions/demote", {
                params: { path: { link: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            }), zReplicationLinkDemoteResult),
    };



    // Sync links (#39 REP-SYNC-MS, 2026-08-22, synclinks.yaml): the SITE-B
    // half of synchronous (RPO=0) replication, MULTISCSI ONLY. A SyncLink
    // registers WHO the leg inside a local backing volume is (name, size,
    // source cluster/volume — the SYNC twin of Volume.replica); exposing
    // that volume as the LUN the source consumes stays the ordinary targets
    // machinery. The A-side half lives on the VOLUME (Volume.sync at create,
    // volumes.syncDegrade). Promotion is witness-gated and STICKY.
    const syncLinks = {
        list: async (p: ListParams<FilterableFields["listSyncLinks"]> = {}): Promise<Page<SyncLink>> =>
            page(await transport.GET("/api/v1/syncLinks", { params: { query: listQuery(p) as never } }), zSyncLink),

        listAll: (p: Omit<ListParams<FilterableFields["listSyncLinks"]>, "limit" | "cursor"> = {}): PageIterator<SyncLink> =>
            genPageIterator(async ({ cursor }) => syncLinks.list({ ...p, cursor })),

        get: async (id: string): Promise<SyncLink> =>
            unwrap(await transport.GET("/api/v1/syncLinks/{link}", { params: { path: { link: id } } }), zSyncLink),

        create: async (body: Record<string, unknown>, opts: DryRunOptions = {}): Promise<SyncLink> =>
            unwrap(await transport.POST("/api/v1/syncLinks", { params: { query: dryRunQuery(opts) }, body: body as never }), zSyncLink),

        // Removes the REGISTRATION only (the backing volume and its targets
        // are untouched); a `promoted` link refuses deletion (409 — its row
        // IS the promotion memory and the fence against the returning old
        // primary).
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/syncLinks/{link}", { params: { path: { link: id } } }));
        },

        // THE CROSS-SITE FAILOVER VERB (#39): site A is dead — make its
        // volume servable HERE, from the leg its mirror synchronously kept
        // on this cluster's LUN. Sudo AND confirm-typed (`confirm` = the
        // link's CURRENT name); the fence (host revocation on the backing
        // volume's targets) lands AT ADMISSION, whatever the task does next.
        // The task then demands the third-site witness grant — a REFUSAL is
        // a decision (the peer is provably alive) and the task fails
        // speaking; `force: true` is the declared manual floor (recorded
        // `promotedMode: forced`, the operator owns the split-brain risk).
        // NO acknowledgeStamp, deliberately (divergence from the async
        // promote): the leg IS the bytes — RPO=0 has no consumed lag to
        // acknowledge. Resolves with the synclink.promote task ref (202).
        promote: async (id: string, body: { confirm: string; force?: boolean }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/syncLinks/{link}/actions/promote", {
                params: { path: { link: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // THE ADOPTION (#209 REP-S2S5, 2026-08-24 — actionAdopt): make the
        // promoted device a FIRST-CLASS volume of this cluster, without
        // copying a byte — the volumes row is born under the SOURCE
        // VOLUME'S OWN ID (the pair-adoption idiom) with the durable
        // Volume.adopted marker. Confirm-typed (`confirm` = the link's
        // CURRENT name — typed by the caller, never filled in); `name`
        // defaults to the link's registered volumeName. Resolves with the
        // synclink.adopt task ref (202).
        adopt: async (id: string, body: { confirm: string; name?: string }, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/syncLinks/{link}/actions/adopt", {
                params: { path: { link: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },

        // THE SYNC REVERSAL'S CONSUMING MOVE (#209 REP-S2S5, 2026-08-24 —
        // actionDemote; ratified: consumption is a DELETE, never a new
        // phase). Sudo AND confirm-typed, with the released pair RE-WRITTEN
        // by the operator: `sourceVolume` must equal the link's sourceVolume
        // — declaring means having read; the facade never fills either in.
        // Synchronous config write: resolves with SyncLinkDemoteResult
        // (200, no task) — the row is consumed, the fence memory ends here.
        demote: async (id: string, body: { confirm: string; sourceVolume: string }, opts: IdempotencyOptions = {}): Promise<SyncLinkDemoteResult> =>
            unwrap(await transport.POST("/api/v1/syncLinks/{link}/actions/demote", {
                params: { path: { link: id } },
                body: body as never,
                headers: idempotencyHeaders(opts),
            }), zSyncLinkDemoteResult),
    };



    // Monitoring reads (M5b, 2026-08-04, monitoring.yaml): the composite
    // cluster-health snapshot and the metric time series — both pure reads of
    // data the platform already replicates/observes and samples on the fixed
    // 5-minute cadence, nothing here triggers new probes.
    const monitoring = {
        // ONE call for the wall dashboard: node/store/datapool/volume/share
        // censuses plus the firing-alert counts. Store down, the render
        // composes from the node's local replica and says so via `observedAt`
        // (staleness visible, never hidden).
        overview: async (): Promise<MonitoringOverview> =>
            unwrap(await transport.GET("/api/v1/monitoring/overview"), zMonitoringOverview),

        // One downsampled series from `metric_samples`: per-step AVERAGE with
        // min/max alongside, empty steps OMITTED (a gap is a gap in
        // observation — never zero-filled). `targetId` is required for every
        // kind except `store` (the singleton — 400 both directions); `metric`
        // must belong to `targetKind` (400). An unknown target answers an
        // EMPTY series, not 404 — samples outlive their targets inside the
        // 90-day retention. `step` omitted, the server picks the smallest
        // multiple of 300 s yielding at most 300 points over `range`.
        trends: async (query: {
            targetKind: MetricTargetKind;
            targetId?: string;
            metric: MetricName;
            range?: TrendRange;
            step?: number;
        }): Promise<MonitoringTrendSeries> =>
            unwrap(await transport.GET("/api/v1/monitoring/trends", { params: { query: query as never } }), zMonitoringTrendSeries),

        // ENEA-A-R11 (2026-10-23, N5): the OpenMetrics scrape token for
        // `GET /metrics` — ONE token per cluster (sha256 in the settings).
        // `issue` MINTS (and ROTATES: the previous token stops working at
        // once) and returns the secret ONCE in the body; `revoke` removes it
        // (GET /metrics then answers 401 metrics_token_missing to any
        // bearer, a `monitoring.read` session still scrapes). Both are
        // sudo-gated mutations on `monitoring.manage`, audited with the
        // caller's identity. Prometheus: `authorization: {type: Bearer,
        // credentials: <token>}` (or `bearer_token_file`).
        metricsToken: {
            issue: async (): Promise<MetricsToken> =>
                unwrap(await transport.POST("/api/v1/monitoring/metricsToken"), zMetricsToken),
            revoke: async (): Promise<void> => {
                unwrap(await transport.DELETE("/api/v1/monitoring/metricsToken"));
            },
        },

        // ENEA-A-R11: the scrape itself, as TEXT (OpenMetrics 1.0, `# EOF`
        // terminated) — with the client's session (monitoring.read) or with
        // `bearer` (the tvsm_ token) in place of it. Not JSON: the facade
        // hands the body through verbatim for consoles and tests.
        metricsText: async (opts: { bearer?: string } = {}): Promise<string> => {
            const result = await transport.GET("/metrics", {
                parseAs: "text",
                ...(opts.bearer !== undefined ? { headers: { authorization: `Bearer ${opts.bearer}` } } : {}),
            });
            return unwrap<string>(result);
        },
    };



    // Alert rules (M5b, 2026-08-04, monitoring.yaml — the whole shape is this
    // round's PROPOSAL, pending ratification): threshold rules over the fixed
    // metric catalog, config-class and replicated, pure synchronous CRUD — no
    // task ever: the backend evaluation sweeper (protection-sweeper pattern)
    // picks changes up on its next pass. A breach sustained for `forMinutes`
    // FIRES one Alert per (rule, target) pair; the value crossing back past
    // `threshold ∓ clearMargin` RESOLVES it (hysteresis). `metric` and
    // `targetKind` are IMMUTABLE (422 — retyping a rule silently retargets
    // its live alerts; make a new rule); `enabled: false` resolves the rule's
    // firing alerts on the next sweep.
    const alertRules = {
        list: async (p: ListParams<FilterableFields["listAlertRules"]> = {}): Promise<Page<AlertRule>> =>
            page(await transport.GET("/api/v1/alertRules", { params: { query: listQuery(p) as never } }), zAlertRule),

        listAll: (p: Omit<ListParams<FilterableFields["listAlertRules"]>, "limit" | "cursor"> = {}): PageIterator<AlertRule> =>
            genPageIterator(async ({ cursor }) => alertRules.list({ ...p, cursor })),

        get: async (id: string): Promise<AlertRule> =>
            unwrap(await transport.GET("/api/v1/alertRules/{alertRule}", { params: { path: { alertRule: id } } }), zAlertRule),

        // `targetSelector` null/omitted = EVERY target of `targetKind`,
        // including ones born after the rule (the fleet-rule ergonomics);
        // must be null on `store` (the singleton). `forMinutes` is evaluated
        // on the 5-minute sampling grid (values rounded UP to the cadence).
        create: async (body: {
            name: string;
            metric: MetricName;
            targetKind: MetricTargetKind;
            targetSelector?: string | null;
            // ENEA-A-R12 (2026-10-23): `anomaly` = scostamento dalla baseline
            // (threshold e clearMargin in deviazioni).
            operator: "gt" | "lt" | "anomaly";
            threshold: number;
            forMinutes?: number;
            severity: "info" | "warning" | "critical";
            clearMargin?: number;
            enabled?: boolean;
            scope?: string;
            notes?: string;
        }, opts: DryRunOptions = {}): Promise<AlertRule> =>
            unwrap(await transport.POST("/api/v1/alertRules", { params: { query: dryRunQuery(opts) }, body: body as never }), zAlertRule),

        // Writable: name, notes, targetSelector, operator, threshold,
        // forMinutes, severity, clearMargin, enabled. Existing alerts keep
        // their frozen copies — the NEXT sweep evaluates with the new values.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<AlertRule> =>
            unwrap(await transport.PATCH("/api/v1/alertRules/{alertRule}", {
                params: { path: { alertRule: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zAlertRule),

        // Synchronous config delete. The rule's FIRING alerts are resolved by
        // the next sweep (resolve deliveries go out); resolved alert history
        // keeps the dangling rule id — honest history, reaped by retention.
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/alertRules/{alertRule}", { params: { path: { alertRule: id } } }));
        },
    };



    // Alert sinks (M5b, 2026-08-04, monitoring.yaml): notification
    // DESTINATIONS — kind `email` (SMTP; the account is a keystore
    // `smtpAccount` REFERENCE, the password never rides this resource) or
    // `webhook` (https POST of the JSON event; optional `webhookSecret`
    // keystore entry HMAC-signs the body). On every alert transition the
    // sweeper births one `alert.deliver` task per ENABLED sink — deliveries
    // ride the task engine, so retry/backoff and the delivery log come for
    // free. MUTATIONS ARE SUDO-GATED (PROPOSAL 2026-08-04: a sink names
    // destinations — re-pointing alert traffic is an exfiltration primitive,
    // the danger ladder without a secret on the row): call auth.sudo first,
    // the grant rides automatically. `kind` is immutable; exactly the
    // per-kind block matching `kind` must be present (422, create and PATCH
    // alike).
    const alertSinks = {
        list: async (p: ListParams<FilterableFields["listAlertSinks"]> = {}): Promise<Page<AlertSink>> =>
            page(await transport.GET("/api/v1/alertSinks", { params: { query: listQuery(p) as never } }), zAlertSink),

        listAll: (p: Omit<ListParams<FilterableFields["listAlertSinks"]>, "limit" | "cursor"> = {}): PageIterator<AlertSink> =>
            genPageIterator(async ({ cursor }) => alertSinks.list({ ...p, cursor })),

        get: async (id: string): Promise<AlertSink> =>
            unwrap(await transport.GET("/api/v1/alertSinks/{alertSink}", { params: { path: { alertSink: id } } }), zAlertSink),

        // Pure config write (201, no task — reachability problems surface on
        // the delivery tasks, and on `test` below, which exists for exactly
        // this). Sudo-protected. `email.credential` must reference a
        // `smtpAccount` keystore entry (null = unauthenticated relay);
        // `webhook.secret` a `webhookSecret` entry (null = unsigned);
        // `webhook.url` is https only.
        // ENEA-A-R11 (2026-10-23): kind `syslog` — RFC 5424 over RFC 5425
        // (TLS) to a SIEM: `tls.caPem` or `tls.fingerprintSha256` is
        // REQUIRED with transport tls (422 syslog_sink_unverifiable
        // otherwise), `tls.clientCertificate` a tlsCertificate credential
        // for mTLS, `forward` says what rides it (alerts by the worker;
        // journal/daemonLogs by the node's rsyslog forwarder). The webhook
        // gains `format: alert | callhome` (default alert = yesterday's
        // payload; callhome = the CallHomeEvent of the contract).
        create: async (body: {
            name: string;
            kind: "email" | "webhook" | "syslog";
            enabled?: boolean;
            email?: { host: string; port?: number; tls?: "starttls" | "implicit" | "none"; from: string; to: string[]; credential?: string | null };
            webhook?: { url: string; secret?: string | null; format?: "alert" | "callhome" };
            syslog?: {
                host: string;
                port?: number;
                transport?: "tls" | "tcp";
                tls?: { caPem?: string | null; fingerprintSha256?: string | null; clientCertificate?: string | null; serverName?: string | null };
                facility?: string;
                appName?: string;
                forward?: { alerts?: boolean; journal?: boolean; daemonLogs?: boolean };
            };
            scope?: string;
            notes?: string;
        }, opts: DryRunOptions = {}): Promise<AlertSink> =>
            unwrap(await transport.POST("/api/v1/alertSinks", { params: { query: dryRunQuery(opts) }, body: body as never }), zAlertSink),

        // Sudo-protected like create (same destination-repointing rationale).
        // Deliveries resolve the sink's config at task run time — a change
        // applies from the next transition.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: DryRunOptions = {}): Promise<AlertSink> =>
            unwrap(await transport.PATCH("/api/v1/alertSinks/{alertSink}", {
                params: { path: { alertSink: id }, query: dryRunQuery(opts) },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zAlertSink),

        // Synchronous config delete, sudo-protected. No referrer guard —
        // nothing references a sink; its pending deliveries fail terminally.
        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v1/alertSinks/{alertSink}", { params: { path: { alertSink: id } } }));
        },

        // Births ONE `alert.deliver` task with `event: test` and a synthetic
        // payload — the SAME executor, transport and credential resolution as
        // a real delivery, so a green test is evidence the real path works.
        // Legal on a DISABLED sink (configure → test → enable). No sudo.
        // Resolves with the task ref (202) — the task's outcome and log ARE
        // the diagnosis (SMTP dialogue, HTTP status; secrets never logged).
        test: async (id: string, opts: IdempotencyOptions = {}): Promise<string | null> => {
            const result = await transport.POST("/api/v1/alertSinks/{alertSink}/actions/test", {
                params: { path: { alertSink: id } },
                headers: idempotencyHeaders(opts),
            });
            unwrap(result);
            return result.response.headers.get("x-tvstor-task");
        },
    };



    // Alerts (M5b, 2026-08-04, monitoring.yaml): RUNTIME rows born by the
    // evaluation sweeper, never by a client — read + ack only, no create, no
    // delete (resolved episodes are reaped by the 90-day retention sweep).
    // NOT a scoped resource (the tasks precedent: machine-born rows,
    // addressed by id, found by `?filter=` — `phase:eq:"firing"` is the
    // dashboard's query). DEDUP (rule, target): at most one `firing` row per
    // pair; resolve closes it and the next breach births a NEW row, so
    // history accumulates one row per episode. The rule-derived fields are
    // FROZEN COPIES taken at fire time.
    const alerts = {
        list: async (p: Omit<ListParams<FilterableFields["listAlerts"]>, "scope"> = {}): Promise<Page<Alert>> =>
            page(await transport.GET("/api/v1/alerts", { params: { query: listQuery(p) as never } }), zAlert),

        listAll: (p: Omit<ListParams<FilterableFields["listAlerts"]>, "scope" | "limit" | "cursor"> = {}): PageIterator<Alert> =>
            genPageIterator(async ({ cursor }) => alerts.list({ ...p, cursor })),

        get: async (id: string): Promise<Alert> =>
            unwrap(await transport.GET("/api/v1/alerts/{alert}", { params: { path: { alert: id } } }), zAlert),

        // Records the caller as acknowledgedBy/acknowledgedAt — a synchronous
        // store write, 200 with the updated row. Ack is BOOKKEEPING: it
        // silences nothing and resolves nothing; it tells the team "seen,
        // being handled". An already-acknowledged alert answers 409 conflict
        // naming the holder — surfaced as TVStorApiError.
        ack: async (id: string): Promise<Alert> =>
            unwrap(await transport.POST("/api/v1/alerts/{alert}/actions/ack", { params: { path: { alert: id } } }), zAlert),

        // THE RESTRICTED OPERATOR CLOSE (#164, ratifica Luca 2026-08-16 §3):
        // legal ONLY on an episode the system declares unclosable — a row
        // carrying a non-null `unclosableSince`, meaning the evaluator found
        // its window empty of both samples and absence declarations. Anything
        // else answers 409 conflict (surfaced as TVStorApiError): a general
        // resolve would be the shortcut by which alerts get switched off
        // instead of fixed, and an episode that will not close while
        // `unclosableSince` is null is a defect of the evaluator.
        resolve: async (id: string): Promise<Alert> =>
            unwrap(await transport.POST("/api/v1/alerts/{alert}/actions/resolve", { params: { path: { alert: id } } }), zAlert),
    };



    const tasks = {
        list: async (p: ListParams<FilterableFields["listTasks"]> = {}): Promise<Page<Task>> =>
            page(await transport.GET("/api/v1/tasks", { params: { query: listQuery(p) as never } }), zTask),

        listAll: (p: Omit<ListParams<FilterableFields["listTasks"]>, "limit" | "cursor"> = {}): PageIterator<Task> =>
            genPageIterator(async ({ cursor }) => tasks.list({ ...p, cursor })),

        get: async (ref: string): Promise<Task> => {
            const { id } = parseTaskRef(ref);
            return unwrap(await transport.GET("/api/v1/tasks/{task}", { params: { path: { task: id } } }), zTask);
        },

        // E2E-2c rilievo 86 (2026-11-01): wait until the holder of the
        // datapool's metadata lease (a 409 metadata_lease_held, see
        // MetadataLeasePolicy) no longer holds it — the family when there is
        // one, the task otherwise — in ANY terminal phase: a family that
        // succeeded, failed, was cancelled or aborted holds no lease (the
        // backend's HOLDS_LEASE rule), and a row that is gone (404) holds none
        // either. The holder's own outcome is not the caller's business: this
        // never raises TVStorTaskError. The poll backs off like `wait`; the
        // budget expiring raises TVStorTaskTimeoutError on the holder.
        waitForLeaseRelease: async (holder: LeaseHolder, opts: LeaseReleaseWaitOptions = {}): Promise<void> => {
            const ref = holder.family ?? holder.task;
            const started = Date.now();
            const deadline = started + (opts.timeoutMs ?? LEASE_HOLDER_WAIT_MS);
            const ceiling = Math.max(opts.pollMs ?? WAIT_POLL_FIRST_MS, opts.pollMaxMs ?? WAIT_POLL_MAX_MS);
            let interval = opts.pollMs ?? WAIT_POLL_FIRST_MS;
            for (;;) {
                let task: Task;
                try {
                    task = await tasks.get(ref);
                }
                catch (err) {
                    if (err instanceof TVStorApiError && err.status === 404) {
                        return;
                    }
                    throw err;
                }
                opts.onPoll?.(task);
                const phase = String(task["phase"]);
                if (phase !== "queued" && phase !== "running") {
                    return;
                }
                if (Date.now() >= deadline) {
                    throw new TVStorTaskTimeoutError(ref, Date.now() - started, task);
                }
                const remaining = Math.max(0, deadline - Date.now());
                await new Promise((r) => setTimeout(r, Math.min(interval, remaining)));
                interval = Math.min(Math.round(interval * WAIT_POLL_GROWTH), ceiling);
            }
        },

        // The children of a parent task (TVirt #128 model) — resume history
        // (`superseded`) included; filter it out for the CURRENT attempts. Sorted
        // by id (uuidv7 ⇒ birth order: the original steps in sequence, resume
        // attempts after); `stepSeq` on each row is the authoritative order.
        children: async (ref: string, p: Omit<ListParams<FilterableFields["listTasks"]>, "filter"> = {}): Promise<Page<Task>> => {
            const { id } = parseTaskRef(ref);
            return tasks.list({ ...p, filter: [{ field: "parent", op: "eq", value: `t-${id.replace(/^t-/, "")}` }], sort: { field: "id", direction: "asc" } });
        },

        // Cooperative cancellation: leaves at safe points, parents at step
        // boundaries. The family then settles `cancelled` whatever the running
        // step does — stop clean or fail (TASK-CANCELSEM, ratifica 2026-08-12
        // §4): once you asked to stop, «you stopped it» is the answer, and a
        // step that broke on the way out is reported by `failedStep` rather than
        // by the family's phase.
        cancel: async (ref: string, opts: IdempotencyOptions = {}): Promise<void> => {
            const { id } = parseTaskRef(ref);
            unwrap(await transport.POST("/api/v1/tasks/{task}/actions/cancel", { params: { path: { task: id } }, headers: idempotencyHeaders(opts) }));
        },

        // Halted-parent management: resume retries from the failed step, abort runs
        // the declared undo actions of completed steps in reverse. Resume wants a
        // `failed` parent — a `cancelled` family answers 412 even when a step of
        // its failed while stopping (TASK-CANCELSEM): abort is its verb.
        resume: async (ref: string, opts: IdempotencyOptions = {}): Promise<Task> => {
            const { id } = parseTaskRef(ref);
            return unwrap(await transport.POST("/api/v1/tasks/{task}/actions/resume", { params: { path: { task: id } }, headers: idempotencyHeaders(opts) }), zTask);
        },

        abort: async (ref: string, opts: IdempotencyOptions = {}): Promise<AbortResult> => {
            const { id } = parseTaskRef(ref);
            return unwrap(await transport.POST("/api/v1/tasks/{task}/actions/abort", { params: { path: { task: id } }, headers: idempotencyHeaders(opts) }), zAbortResult);
        },

        // Polls until the task reaches a terminal phase; resolves with the final
        // render on success, throws TVStorTaskError for every other terminal
        // phase — `cancelled` included, so a caller who cancelled its own task
        // still gets an exception carrying the phase, and reads «cancelled» in
        // the message instead of the «failed» it used to read (TASK-CANCELSEM) —
        // and TVStorTaskTimeoutError when the WAIT runs out — two different errors
        // for two different facts (#102 CLI-WAIT, ratifica Luca 2026-08-12 §8;
        // the CLI spells the second one exit 124 and the first one exit 1).
        // The interval between polls backs off: see WAIT_POLL_FIRST_MS above.
        wait: async (ref: string, opts: TaskWaitOptions = {}): Promise<Task> => {
            const started = Date.now();
            const deadline = started + (opts.timeoutMs ?? 120_000);
            const ceiling = Math.max(opts.pollMs ?? WAIT_POLL_FIRST_MS, opts.pollMaxMs ?? WAIT_POLL_MAX_MS);
            let interval = opts.pollMs ?? WAIT_POLL_FIRST_MS;
            for (;;) {
                // Rilievo 210 (sh3g S12 passo 9, 2026-11-03): a 503 `store_unavailable` DURING the wait is
                // a store gap, not a verdict — the task may be the very one moving the store (the
                // planned switchover stops the primary for ~20 s): keep polling until the deadline.
                // Everything else (401, 404, 5xx of another kind) is fatal as before.
                let task: Task;
                try {
                    task = await tasks.get(ref);
                }
                catch (err) {
                    if (err instanceof TVStorApiError && err.status === 503 && err.code === "store_unavailable" && Date.now() < deadline) {
                        const left = Math.max(0, deadline - Date.now());
                        await new Promise((r) => setTimeout(r, Math.min(interval, left)));
                        interval = Math.min(Math.round(interval * WAIT_POLL_GROWTH), ceiling);
                        continue;
                    }
                    throw err;
                }
                opts.onProgress?.(task);
                const phase = String(task["phase"]);
                if (phase === "succeeded") {
                    return task;
                }
                if (phase === "failed" || phase === "cancelled" || phase === "aborted") {
                    throw new TVStorTaskError(task);
                }
                // `>=`, non `>`: l'ultimo sonno viene tagliato ESATTAMENTE sulla
                // scadenza, quindi il confronto stretto vedrebbe `deadline ===
                // now` e tornerebbe a dormire zero millisecondi in cerchio.
                if (Date.now() >= deadline) {
                    throw new TVStorTaskTimeoutError(ref, Date.now() - started, task);
                }
                // Never sleep PAST the deadline: with a backed-off interval a
                // `--wait-timeout 10s` would otherwise expire up to five seconds
                // late, and the number the operator wrote is the number the
                // wait must honour.
                const remaining = Math.max(0, deadline - Date.now());
                await new Promise((r) => setTimeout(r, Math.min(interval, remaining)));
                interval = Math.min(Math.round(interval * WAIT_POLL_GROWTH), ceiling);
            }
        },

        follow: async function* (ref: string): AsyncGenerator<Record<string, unknown>> {
            const { id } = parseTaskRef(ref);
            const response = await fetchImpl(`${options.baseUrl}/api/v1/tasks/${id}/log?follow=true`, { headers: authHeaders() });
            if (!response.ok || response.body === null) {
                throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
            }
            yield* ndjsonLines(response.body);
        },

        // The structured log as a plain array (frames so far; the whole log once the
        // task is terminal): the non-follow read of /tasks/{id}/log.
        log: async (ref: string): Promise<Record<string, unknown>[]> => {
            const frames: Record<string, unknown>[] = [];
            const { id } = parseTaskRef(ref);
            const response = await fetchImpl(`${options.baseUrl}/api/v1/tasks/${id}/log`, { headers: authHeaders() });
            if (!response.ok || response.body === null) {
                throw new TVStorApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>, response.headers);
            }
            for await (const frame of ndjsonLines(response.body)) {
                frames.push(frame);
            }
            return frames;
        },
    };



    return { auth, system, users, scopes, rbac, cluster, cephClusters, multiscsiClusters, volumes, hosts, ioServerPairs, enclosures, catalogSources, credentials, directoryRealms, targets, fileShares, dhcpServices, networks, objectStores, backupTargets, backups, replicationLinks, syncLinks, monitoring, alertRules, alertSinks, alerts, tasks };
}

